import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { desktopUi } from "../features/desktopUi";
import {
  setPartFlag, partImageProblem, uploadPartImage, loadPartDocuments as fetchPartDocuments,
  loadPart, loadPartRevisions, partProcessSteps, isArchivedRevision,
} from "../features/parts/partData";
import { currentActor } from "../utils/beacons";
import TaskTrendDashboard from "./TaskTrendDashboard";
import config from '../config.js';
import PartOutsourcingCard from "../components/PartOutsourcingCard.jsx";
import ProcessSequencer from '../components/ProcessSequencer';
import { CoatingColorField } from '../components/CoatingColor';
import BulkPartsEntryPage from "./BulkPartsEntryPage";
import { Tooltip } from 'bootstrap';
import ReactFlow, { Background, Controls, useNodesState, useEdgesState, MarkerType } from 'reactflow';
import 'reactflow/dist/style.css';
import pinPrompt from '../utils/pinPrompt';
import openPdf from '../utils/openPdf';
import { checkUploadSize, uploadFailureMessage } from '../utils/uploadLimits';
import { dxfAnalyzedMessage, dxfValuesToKeep, profileAreaProblem } from '../utils/dxfWarnings';
import { parseApiError as parseApiErrorShared, apiErrorMessage, detailToString } from '../utils/apiError';
import { setAiContext, clearAiContext } from '../utils/aiContext';
import { fmtMoney, fmtDelta, formulaString, asOf } from '../utils/priceMath';
import {
  computePartRollup, normalizeMarkupPercent, costFormulaTerms, priceFormulaTerms, freshness,
  isMaterialDebugMethod, materialMethodKind, manualMethodLabel, describeMaterialLive, describeAllocationRow,
  describeUtilisation, parseCalcLog, normalizeAssemblyRow, assemblyRoleSentence, opsOwnedStepsIndex,
  coatingOwners, opsOwnersForStep, asQuotedInsideAssembly, COST_FORMULA, PRICE_FORMULA,
  describeShuttle, describeCoatingMirror, coatingPayloadFromCard,
} from '../utils/partEntryCostMath';
import { SourceTag, storedLabel, PartEntryCostMathTable, PartEntryCalcLogTable, ShuttleLine } from '../components/partEntry/PartEntryCostProvenance';
import { emailSenderHeaders } from '../utils/emailSender';

/**
 * PartEntryPage - Enhanced with Revision File Copy Options & PPAP Visualization
 * Updated to include Laser Consumable Gas Costing & Calculation Logging in UI
 * NEW: Added Sort By filters to list view.
 */

// Part-specific wrapper around the shared parser (src/utils/apiError.js).
//
// The shared parser replaces ~80 lines that used to live here. The local copy
// could not render a 422: FastAPI returns `detail` as an ARRAY of field errors,
// so `detail.includes(...)` threw a TypeError, the catch swallowed it, and the
// raw JSON body was shown instead. The shared version handles all three detail
// shapes and is unit-tested against that case.
//
// Two things stay local because they are specific to this page:
//   1. An oversized upload is rejected by API Gateway with a bare 413 carrying
//      no CORS header, so fetch() rejects exactly as it does when the machine is
//      offline. Don't blame the network when a file was involved.
//   2. The parts table's own unique key is (product_id, revision_code); naming
//      both reads better than the generic "a record with ..." wording.
function parseApiError(errorMessage, context = '', uploadedFile = null) {
  const raw = typeof errorMessage === 'string' ? errorMessage : '';

  if (uploadedFile && raw.includes('Failed to fetch')) {
    return uploadFailureMessage(uploadedFile);
  }

  if (raw.includes('uq_product_revision')) {
    const m = raw.match(/\(product_id, revision_code\)=\(([^,]+), ([^)]+)\)/);
    if (m) {
      return `A part with Product ID "${m[1]}" (Revision ${m[2]}) already exists. Please use a unique Product ID.`;
    }
    return 'A part with this Product ID already exists. Please use a unique Product ID.';
  }

  return parseApiErrorShared(errorMessage, context);
}

// "stored 09-01 12:54: $0.91 (+$0.02 stale)" printed under a live figure so the
// operator can see what the Assemblies card / SO snapshot will read (F06).
function StoredLine({ asOfLabel, stored, live, precise, hasStored }) {
  if (!hasStored) return <div className="text-muted">not saved yet</div>;
  const f = freshness(stored, live);
  return (
    <div>
      {asOfLabel}: {fmtMoney(stored, { dash: true, precise })}
      {f.comparable && f.stale ? <> ({fmtDelta(f.delta)} <SourceTag kind="stale">stale</SourceTag>)</> : null}
    </div>
  );
}

export default function PartsEntryPage() {
  // --- Core State ---
  const { partId: routePartId } = useParams();   // deep-link target, e.g. /parts/1137 ("Open Part" links)
  const deepLinkOpenedRef = useRef(false);
  const [parts, setParts] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [partData, setPartData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [materialPrices, setMaterialPrices] = useState({
      price_by_weight: 0,
      price_by_bounding_area: 0,
      price_by_solid_profile: 0,
      price_by_cut_length: 0,
      price_by_stock_length: 0,
  });
  const [cwtOptions, setCwtOptions] = useState({ material_types: [], surface_finishes: [] });
  const [thicknessOptions, setThicknessOptions] = useState([]);
  const [materialDensities, setMaterialDensities] = useState({});
  const [pendingDxfFile, setPendingDxfFile] = useState(null);
  const [isCalculatingLaserTime, setIsCalculatingLaserTime] = useState(false);

  // --- Versioning State ---
  const [revisionHistory, setRevisionHistory] = useState([]);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [showRevModal, setShowRevModal] = useState(false);
  const [revChangeDescription, setRevChangeDescription] = useState("");
  const [revRequestedBy, setRevRequestedBy] = useState("customer");
  // PRICING IMPACT for the approval form. New Version modal defaults to
  // 'unchanged' (in-place repoints keep pricing untouched by design).
  const [revPricingImpact, setRevPricingImpact] = useState("unchanged");
  const [revPricingQuoteRef, setRevPricingQuoteRef] = useState("");
  // Per-revision-row PRICING IMPACT selection, keyed by new_part_id;
  // seeded from the stored value whenever revisionApprovals loads (effect
  // lives below the revisionApprovals declaration).
  const [pricingSelByPart, setPricingSelByPart] = useState({});
  const [revGenerateForm, setRevGenerateForm] = useState(true);
  const [revisionApprovals, setRevisionApprovals] = useState([]);
  // Seed the per-row PRICING IMPACT selections from the stored values
  // whenever the approvals list (re)loads.
  useEffect(() => {
    const next = {};
    (revisionApprovals || []).forEach(ev => {
      next[ev.new_part_id] = { impact: ev.pricing_impact || "", ref: ev.pricing_quote_ref || "" };
    });
    setPricingSelByPart(next);
  }, [revisionApprovals]);
  const [repointPartId, setRepointPartId] = useState(null); // current-rev part_id → open orders update modal
  // Proactive warning: older revisions of this part family are still
  // referenced by assemblies / open orders. {assemblies, soLines, quoteLines,
  // currentPartId} or null.
  const [openRefsWarning, setOpenRefsWarning] = useState(null);

  // --- Documents State ---
  const [partDocuments, setPartDocuments] = useState([]);
  const [docUploadFile, setDocUploadFile] = useState(null);
  const [docDisplayName, setDocDisplayName] = useState("");
  const [docDescription, setDocDescription] = useState("");
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);

  // --- UI & Filter State ---
  const [view, setView] = useState('single'); 
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCustomerId, setFilterCustomerId] = useState("");
  const [filterStockType, setFilterStockType] = useState("");
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [showTemporary, setShowTemporary] = useState(false);

  // Material Filters
  const [filterMaterialType, setFilterMaterialType] = useState("");
  const [filterSurfaceFinish, setFilterSurfaceFinish] = useState("");
  const [filterThickness, setFilterThickness] = useState("");
  
  // NEW: Sort State
  const [sortBy, setSortBy] = useState("name_asc");

  const [allCutSheets, setAllCutSheets] = useState([]);
  const [cutSheetsForPart, setCutSheetsForPart] = useState([]);
  const [allCutLists, setAllCutLists] = useState([]);
  const [cutListsForPart, setCutListsForPart] = useState([]);
  const [allAssemblies, setAllAssemblies] = useState([]);
  const [assembliesForPart, setAssembliesForPart] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [ppapUploadFile, setPpapUploadFile] = useState(null); 
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  // --- Warning state for Missing Laser Tech Specs ---
  const [missingSpecWarning, setMissingSpecWarning] = useState(null);
  
  // --- PPAP STATE ---
  const [editingCharacteristicId, setEditingCharacteristicId] = useState(null);
  const [showFlowModal, setShowFlowModal] = useState(false); // NEW: Process Flow Modal

  // --- Loading & Error State ---
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCutSheetsForPart, setLoadingCutSheetsForPart] = useState(false);
  const [loadingCutListsForPart, setLoadingCutListsForPart] = useState(false);
  const [loadingAssembliesForPart, setLoadingAssembliesForPart] = useState(false);
  const [serviceVendors, setServiceVendors] = useState([]);

  // --- Cost provenance (F06 / F08 / F16) ---
  // The STORED snapshot from GET /parts/{id} (what the Assemblies card and the
  // SO conversion read) is kept apart from the LIVE figures the boxes show, so
  // the page can print "stored $1.72 vs live $2.33" instead of silently
  // replacing one with the other. costEngine records which engine produced the
  // live labor/gas/consumables (history-aware on load, estimate after an edit).
  const [storedSnap, setStoredSnap] = useState(null);
  const [costEngine, setCostEngine] = useState({ engine: null, at: null });
  const [costNotice, setCostNotice] = useState(null);
  const [calcLogOpen, setCalcLogOpen] = useState(false);
  // Wave 2 — decision 4: shuttle provenance from the last engine call (the share is
  // INSIDE cost_labor; this only makes it visible). Decision 3: the Outsourcing card's
  // rows (for the read-only coating mirror) and the coating $/unit the CARD wrote this
  // session — the only way that key reaches the Save payload (coatingPayloadFromCard).
  const [costShuttle, setCostShuttle] = useState(() => describeShuttle(null));
  const [outsourcingRows, setOutsourcingRows] = useState({ steps: [], prices: [], loaded: false });
  const [coatingFromCard, setCoatingFromCard] = useState(null);
  const handleOutsourcingData = useCallback((d) => {
    setOutsourcingRows({
      steps: Array.isArray(d?.steps) ? d.steps : [],
      prices: Array.isArray(d?.prices) ? d.prices : [],
      loaded: true,
    });
  }, []);

  // --- Constants ---
  const API_BASE = config.API_BASE_URL;
  const fabSubProcesses = ["Cutting", "Drilling", "Milling", "Lathe", "Welding", "Assembly", "Tapping"];
  const allProcessOptions = ["Laser", "Tap", "Brake", "Blast", "Coating", "Finish", ...fabSubProcesses];
  const PROFILE_SHAPES = ["Flat Bar", "Round Bar", "Square Bar", "Hex Bar",
    "Round Tube", "Square Tube", "Rectangular Tube",
    "Pipe", "Angle", "Channel", "I-Beam", "Tee"
  ];
  const PPAP_FREQUENCIES = [
    "100%",
    "Random",
    "1 pc / Shift",
    "Start of Shift",
    "1 pc / Hour",
    "3 pcs / Hour",
    "5 pcs / Hour",
    "1 pc / Job",
    "3 pcs / Job",
    "5 pcs / Job",
    "First/Last Off",
    "First/Middle/Last",
    "Once per Setup",
    "At Tool Change",
    "After Adjustment",
    "Every X Parts",
    "Per Container"
];

  // F33: written from the formulas the code actually runs (see
  // src/utils/partEntryCostMath.js, which mirrors jobq-api update_all_part_costs).
  const pricingHelpText = `
    What each box is (all per unit, all TRUE COST unless marked price):
    - Cost - Labor: hours per step × the shop's TRUE-COST labour rate — the 90-day punch-weighted operator WAGE for that station (falls back to the default wage table when a station has no punches). This is NOT the quote "shop rate" (the selling $/hr on the Quote page); wages are roughly 1/10 of it. Hours come from job history when the part has been run ("historical"), else from the estimate fields above ("estimate").
    - Laser shuttle share: on a FIRST-RUN Laser part the estimate adds a proportional table-swap (shuttle) share at the true rate. It is already inside Cost - Labor and is shown on its own line beneath it and in the Calculation log. Historical Laser hours include the swaps, so none is added ("first-run only — history includes it").
    - Cost - Gas: assist-gas $/hr for the material/thickness × beam-on hours (historical average when jobs exist).
    - Cost - Consumables: nozzle + lens $/hr × beam-on hours, at cost.
    - Cost - Material: the raw stock allocated to this part by the selected method (CWT weight, cut-sheet share, cut-list stick share, manual). Cut-sheet and cut-list allocations charge the WHOLE sheet/stick to the nested parts — see the utilisation note beside the radio.
    - Coating $/unit: the outside coater's price per part (a cost; marked up like material on the price side). When an assembly's operations line prices coating, quotes exclude it from this part. It is edited on the Outsourcing card (Coating row) — the box here is a read-only mirror, and Apply on the card writes the preferred vendor's book price through to the row and this column.

    Total Cost = Labor + Gas + Consumables + Material + Coating.

    Cost + material markup = Labor + Gas + Consumables + (Material + Coating) × (1 + Markup %). This is an INTERNAL estimate at wage rates and the material markup — it is not a quote price and quotes never read it. Quotes price labour at the quote's selling rates and material at the quote's markup.
    - Final Sale Price: the book price you set; quote approval also writes it.

    Stored vs live: the Assemblies card and Sales Order snapshots read the STORED total (the "stored" figures here). The live figures are recomputed when this page loads (labor/gas/consumables from the history-aware engine) and again when you edit routing or minutes (estimate engine). Save writes the live figures.

    Automatic refresh: JobQ_Date_Auto_Update → Update All Part Costs runs daily ~06:00 CT and rewrites labor, gas, consumables, material (cut-sheet methods only — cut-LIST parts are NOT refreshed and need a Part-page save after a stick reprice), total and cost + markup for every part. 'Recalculate from History' runs the same engine for this part now.
  `;

  const [shapeStockOptions, setShapeStockOptions] = useState([]);
  const [selectedSteps, setSelectedSteps] = useState([]);

  // --- Consumable Component Yield State ---
  const [componentConsumables, setComponentConsumables] = useState([]);
  const [consumableYieldId, setConsumableYieldId] = useState("");
  const [savedConsumableYieldId, setSavedConsumableYieldId] = useState("");
  
  // --- Utility Functions ---

  const calculateLimits = (nominal, tolerance) => {
    const value = parseFloat(nominal);
    if (nominal == null || isNaN(value) || !tolerance) {
        return { lsl: null, usl: null };
    }
    const cleanedTolerance = tolerance.replace(/ /g, '');
    let plusTol = 0;
    let minusTol = 0;
    if (cleanedTolerance.includes('±')) {
        const tol = cleanedTolerance.replace(/[^0-9.]/g, '');
        plusTol = minusTol = parseFloat(tol);
    } 
    else if (cleanedTolerance.includes('/')) {
        const parts = cleanedTolerance.split('/');
        const plusStr = parts[0].replace(/[^0-9.+-]/g, '');
        const minusStr = parts[1].replace(/[^0-9.+-]/g, '');
        plusTol = Math.abs(parseFloat(plusStr));
        minusTol = Math.abs(parseFloat(minusStr));
    } 
    else if (!isNaN(parseFloat(cleanedTolerance)) && cleanedTolerance.length > 0) {
         const tol = parseFloat(cleanedTolerance);
         plusTol = minusTol = Math.abs(tol);
    }
    if (isNaN(plusTol) || plusTol < 0) plusTol = 0;
    if (isNaN(minusTol) || minusTol < 0) minusTol = 0;
    return {
        usl: parseFloat((value + plusTol).toFixed(4)),
        lsl: parseFloat((value - minusTol).toFixed(4))
    };
  };

  const formatLimit = (value, nominal) => {
    if (value == null) return '';
    const nominalStr = String(nominal ?? '').trim();
    const dotIdx = nominalStr.indexOf('.');
    const nomDecimals = dotIdx === -1 ? 0 : nominalStr.length - dotIdx - 1;
    const trimmed = value.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
    const tDot = trimmed.indexOf('.');
    const trimmedDecimals = tDot === -1 ? 0 : trimmed.length - tDot - 1;
    return value.toFixed(Math.max(nomDecimals, trimmedDecimals));
  };

  const formatDateSafe = (dateString) => {
      if (!dateString) return "Unknown Date";
      try {
          return new Date(dateString).toLocaleDateString();
      } catch (e) {
          return "Invalid Date";
      }
  };

  // Clear AI context on unmount
  useEffect(() => () => clearAiContext(), []);

  // --- Effects ---
  useEffect(() => {
    loadParts();
    loadAllCutSheets();
    loadAllCutLists();
    loadAllAssemblies();
    loadAllCustomers();
    
    const fetchCwtOptions = async () => {
        try {
            const res = await fetch(`${API_BASE}/cwt_pricing_options`);
            const data = await res.json();
            setCwtOptions(data);
        } catch (error) {
            console.error("Failed to fetch CWT pricing options:", error);
        }
    };

    const fetchThicknessOptions = async () => {
      try {
        const res = await fetch(`${API_BASE}/material_thicknesses`);
        if (!res.ok) throw new Error('Failed to fetch thickness options');
        const data = await res.json();
        setThicknessOptions(data);
      } catch (err) {
        console.error("Failed to fetch thickness options:", err.message);
      }
    };

    const fetchMaterialDensities = async () => {
      try {
        const res = await fetch(`${API_BASE}/material_densities`);
        if (!res.ok) throw new Error('Failed to fetch material densities');
        const data = await res.json();
        const map = {};
        for (const row of data) {
          map[row.material_type] = parseFloat(row.density_lbs_per_cubic_inch);
        }
        setMaterialDensities(map);
      } catch (err) {
        console.error("Failed to fetch material densities:", err.message);
      }
    };

    fetchCwtOptions();
    fetchThicknessOptions();
    fetchMaterialDensities();

    // Load component consumables for "Produces Component" dropdown
    const fetchComponentConsumables = async () => {
      try {
        const res = await fetch(`${API_BASE}/consumables`);
        if (!res.ok) throw new Error('Failed to fetch consumables');
        const data = await res.json();
        setComponentConsumables(data.filter(c => c.consumable_type === 'component'));
      } catch (err) {
        console.error("Failed to fetch component consumables:", err);
      }
    };
    fetchComponentConsumables();
  }, []);

  useEffect(() => {
    var tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    var tooltipList = tooltipTriggerList.map(function (tooltipTriggerEl) {
      return new Tooltip(tooltipTriggerEl);
    });
    return () => {
      tooltipList.forEach(tooltip => tooltip.dispose());
    };
  }, [view, partData]); 

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Live rollup (F06 / F14 / F15). ONE formula — the server's
  // (update_all_part_costs; PATCH /parts after Workstream B) — computed from
  // the figures on screen. Nothing is written back into partData, so
  // partData.cost_labor_material / price_labor_material stay what GET
  // /parts/{id} returned (the STORED values) and storedSnap mirrors them.
  // The previous effect priced coating at cost on the price side and rewrote
  // the stored totals in state without marking the page dirty.
  const liveRollup = useMemo(() => computePartRollup(partData), [partData]);

  // Load service vendors (powder coaters, platers) for the Coating-vendor picker.
  useEffect(() => {
      fetch(`${API_BASE}/service_vendors`)
          .then((r) => (r.ok ? r.json() : []))
          .then((data) => setServiceVendors(Array.isArray(data) ? data : []))
          .catch((err) => console.error("Failed to load service vendors", err));
  }, []);

  useEffect(() => {
      if (partData?.stock_type === 'Shape Stock' && partData?.profile_shape) {
          const fetchShapeStock = async () => {
              try {
                  const res = await fetch(`${API_BASE}/materials?material_category=Shape Stock&profile_shape=${encodeURIComponent(partData.profile_shape)}`);
                  if (!res.ok) throw new Error('Failed to fetch shape stock materials');
                  const data = await res.json();
                  setShapeStockOptions(data);
              } catch (err) {
                  console.error("Failed to fetch shape stock:", err);
                  setShapeStockOptions([]);
              }
          };
          fetchShapeStock();
      } else {
          setShapeStockOptions([]);
      }
  }, [partData?.profile_shape, partData?.stock_type]);

  useEffect(() => {
    if (!partData || !isDirty) return;
    const handler = setTimeout(() => {
      recalculateLaborCost(partData);
    }, 500);
    return () => clearTimeout(handler);
  }, [
    partData?.process_steps, partData?.initial_est_laser_minutes, partData?.initial_est_tap_minutes,
    partData?.initial_est_brake_minutes, partData?.initial_est_blast_minutes, partData?.initial_est_finish_minutes,
    partData?.initial_est_cutting_minutes, partData?.initial_est_drilling_minutes, partData?.initial_est_milling_minutes,
    partData?.initial_est_lathe_minutes, partData?.initial_est_weld_minutes, partData?.initial_est_assembly_minutes,
    partData?.initial_est_fab_tap_minutes, 
    // NEW: Recalculate when material props change for gas cost updates
    partData?.material_type, partData?.thickness_id
  ]);

  useEffect(() => {
    // 'manual' and 'manual_price' both mean the typed manual_material_price ($0 / blank =
    // customer supplied). The server keeps the material the client sends and recomputes
    // the rollup, so the box follows the typed price for BOTH (it used to be forced to 0
    // for 'manual', zeroing dev 1232/1233 $16.95 and 1236/1237 $4.84 on every pick).
    if (partData?.material_price_method === 'manual_price' || partData?.material_price_method === 'manual') {
      const manualPrice = safeParseFloat(partData.manual_material_price) || 0;
      if (partData.material_cost_component !== manualPrice) {
        setPartData(prev => ({ ...prev, material_cost_component: manualPrice }));
      }
    }
  }, [partData?.manual_material_price, partData?.material_price_method]);

  useEffect(() => {
    if (partData?.process_steps) {
      const steps = partData.process_steps
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .filter(name => name !== 'Fabrication');
      const stepObjects = steps.map((name, index) => ({ id: `step-${index}-${name}`, name }));
      setSelectedSteps(stepObjects);
    } else {
      setSelectedSteps([]);
    }
  }, [partData?.part_id]);

  useEffect(() => {
    const newProcessStepsString = selectedSteps.map(step => step.name).join(',');
    if (partData && partData.process_steps !== newProcessStepsString) {
      handlePartDataChange('process_steps', newProcessStepsString);
    }
  }, [selectedSteps]);

  // --- NEW: Check for Laser Tech Spec existence when material changes ---
  useEffect(() => {
    // Only validate if we have the necessary data and it is Sheet Stock
    if (partData?.stock_type !== 'Sheet Stock' || !partData?.material_type || !partData?.thickness_id) {
        setMissingSpecWarning(null);
        return;
    }

    // Find display name for thickness for a nicer error message
    const thicknessObj = thicknessOptions.find(t => t.thickness_id === parseInt(partData.thickness_id));
    const thicknessName = thicknessObj ? thicknessObj.display_thickness : partData.thickness_id;

    fetch(`${API_BASE}/laser_specs/validate?material_type=${encodeURIComponent(partData.material_type)}&thickness_id=${partData.thickness_id}`)
        .then(res => res.json())
        .then(data => {
            if (!data.exists) {
                setMissingSpecWarning(`WARNING: Material '${partData.material_type} - ${thicknessName}' has no Laser Tech Spec. Gas tracking will fail.`);
            } else {
                setMissingSpecWarning(null);
            }
        })
        .catch(e => console.error(e));
  }, [partData?.stock_type, partData?.material_type, partData?.thickness_id, thicknessOptions]);

  // --- Data Loading Functions ---

  async function loadParts() {
    setLoading(true);
    setError(null);
    try {
      // Fetch all parts including inactive (active_only=false) so UI can filter them
      const res = await fetch(`${API_BASE}/parts?active_only=false`);
      if (!res.ok) throw new Error(`Failed to load parts: ${res.status}`);
      const data = await res.json();
      const partsWithFlagBoolean = data.map((p) => ({ ...p, flagged: !!p.flagged }));
      setParts(partsWithFlagBoolean);
    } catch (err) {
      setError(parseApiError(err.message, 'Failed to load parts'));
    } finally {
      setLoading(false);
    }
  }

  async function loadAllCutSheets() {
    try {
      const res = await fetch(`${API_BASE}/cut_sheets`);
      if (!res.ok) throw new Error(`Failed to load cut sheets: ${res.status}`);
      setAllCutSheets(await res.json());
    } catch (err) {
      setError(parseApiError(err.message, 'Failed to load cut sheets'));
    }
  }

  async function loadAllCutLists() {
      try {
        const res = await fetch(`${API_BASE}/cut_lists`);
        if (!res.ok) throw new Error(`Failed to load cut lists: ${res.status}`);
        setAllCutLists(await res.json());
      } catch (err) {
        setError(parseApiError(err.message, 'Failed to load cut lists'));
      }
  }

  async function loadAllAssemblies() {
    try {
      const res = await fetch(`${API_BASE}/assemblies`);
      if (!res.ok) throw new Error(`Failed to load assemblies: ${res.status}`);
      setAllAssemblies(await res.json());
    } catch (err) {
      setError(parseApiError(err.message, 'Failed to load assemblies'));
    }
  }

  async function loadAllCustomers() {
    try {
      const res = await fetch(`${API_BASE}/customers`);
      if (!res.ok) throw new Error(`Failed to load customers: ${res.status}`);
      setCustomers((await res.json()).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      setError(parseApiError(err.message, 'Failed to load customers'));
    }
  }

  async function loadCutSheetsForPart(partId) {
    setLoadingCutSheetsForPart(true);
    try {
      const res = await fetch(`${API_BASE}/parts/${partId}/cut_sheets`);
      if (!res.ok) {
        setCutSheetsForPart([]);
        return;
      }
      setCutSheetsForPart(await res.json());
    } catch (err) {
      console.error("Error loading cut sheets:", err);
      setCutSheetsForPart([]);
    } finally {
      setLoadingCutSheetsForPart(false);
    }
  }

  async function loadCutListsForPart(partId) {
      setLoadingCutListsForPart(true);
      try {
          const res = await fetch(`${API_BASE}/parts/${partId}/cut_lists`);
          if (!res.ok) {
              setCutListsForPart([]);
              return;
          }
          setCutListsForPart(await res.json());
      } catch (err) {
          console.error("Error loading cut lists:", err);
          setCutListsForPart([]);
      } finally {
          setLoadingCutListsForPart(false);
      }
  }

  async function loadPartDocuments(pid) {
    const docs = await fetchPartDocuments(pid); // partData: null when unreadable (the list stays)
    if (docs) setPartDocuments(docs);
  }

  async function recalculateLaborCost(currentPartData) {
    if (!currentPartData) return; // Safety check

    const currentProcesses = (currentPartData.process_steps || "").split(",").map(s => s.trim());
    let totalFabMinutes = 0;
    
    // Sum up fab minutes...
    if (currentProcesses.includes("Cutting")) totalFabMinutes += parseTimeToMinutes(currentPartData.initial_est_cutting_minutes);
    if (currentProcesses.includes("Drilling")) totalFabMinutes += parseTimeToMinutes(currentPartData.initial_est_drilling_minutes);
    if (currentProcesses.includes("Milling")) totalFabMinutes += parseTimeToMinutes(currentPartData.initial_est_milling_minutes);
    if (currentProcesses.includes("Lathe")) totalFabMinutes += parseTimeToMinutes(currentPartData.initial_est_lathe_minutes);
    if (currentProcesses.includes("Welding")) totalFabMinutes += parseTimeToMinutes(currentPartData.initial_est_weld_minutes);
    if (currentProcesses.includes("Assembly")) totalFabMinutes += parseTimeToMinutes(currentPartData.initial_est_assembly_minutes);
    if (currentProcesses.includes("Tapping")) totalFabMinutes += parseTimeToMinutes(currentPartData.initial_est_fab_tap_minutes);

    const payload = {
      // ⚠ part_id is what lets the estimate honour this part's per-step outsourcing.
      // Without it the API cannot tell an outsourced Brake step from an in-house one and
      // charges internal labour for work that goes to a vendor. Null on a part that has not
      // been saved yet, which is correct: there is no per-part answer to give.
      part_id: currentPartData.part_id || null,
      process_steps: currentPartData.process_steps,
      initial_est_laser_minutes: parseTimeToMinutes(currentPartData.initial_est_laser_minutes),
      beam_on_minutes: safeParseFloat(currentPartData.beam_on_minutes),
      initial_est_tap_minutes: parseTimeToMinutes(currentPartData.initial_est_tap_minutes),
      initial_est_brake_minutes: parseTimeToMinutes(currentPartData.initial_est_brake_minutes),
      initial_est_fabrication_minutes: totalFabMinutes, 
      initial_est_blast_minutes: parseTimeToMinutes(currentPartData.initial_est_blast_minutes),
      initial_est_finish_minutes: parseTimeToMinutes(currentPartData.initial_est_finish_minutes),
      material_type: currentPartData.material_type,
      thickness_id: currentPartData.thickness_id ? parseInt(currentPartData.thickness_id) : null,
    };

    try {
      const res = await fetch(`${API_BASE}/parts/calculate-dynamic-cost`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) return; 
      
      const data = await res.json();
      setPartData(prev => ({
          ...prev,
          cost_labor: data.calculated_labor_cost,
          // Gas arrives at 4 dp from the post-B1 API (2 dp before); shown as returned.
          estimated_gas_cost: data.calculated_gas_cost,
          // F14: an older API omits calculated_consumable_cost; keep the previous
          // value rather than zeroing the term (which then got saved into the rollup).
          estimated_consumable_cost: data.calculated_consumable_cost ?? prev?.estimated_consumable_cost ?? 0,
          last_calculation_log: data.log
      }));
      setCostEngine({ engine: 'dynamic', at: new Date().toISOString() });
      setCostShuttle(describeShuttle(data));
    } catch (err) {
      console.error("Error during cost recalculation:", err);
    }
  }

  // Build the STORED snapshot from a GET /parts/{id} body. The provenance keys
  // (rollup / price_calc / material_live / cost_updated_at) exist only on the
  // post-Workstream-B API; everything renders "n/a" without them.
  function snapshotFromPart(p) {
    if (!p) return null;
    return {
      part_id: p.part_id,
      cost_labor: p.cost_labor,
      estimated_gas_cost: p.estimated_gas_cost,
      estimated_consumable_cost: p.estimated_consumable_cost,
      material_cost_component: p.material_cost_component,
      material_price_method: p.material_price_method,
      manual_material_price: p.manual_material_price,
      coating_cost_per_unit: p.coating_cost_per_unit,
      markup_percent: p.markup_percent,
      cost_labor_material: p.cost_labor_material,
      price_labor_material: p.price_labor_material,
      cost_updated_at: p.cost_updated_at ?? null,
      rollup: p.rollup ?? null,
      price_calc: p.price_calc ?? null,
      material_live: p.material_live ?? null,
      fetched_at: new Date().toISOString(),
    };
  }

  // Re-read only the stored snapshot + assembly membership after a save (does
  // not touch partData, so nothing the operator typed is discarded).
  async function refreshStoredSnapshot(pid) {
    if (!pid) return;
    try {
      const r = await fetch(`${API_BASE}/parts/${pid}`);
      if (!r.ok) return;
      const fresh = await r.json();
      setStoredSnap(snapshotFromPart(fresh));
      loadAssembliesForPart(pid, fresh.assemblies);
    } catch (e) {
      console.error("Failed to refresh stored cost snapshot", e);
    }
  }

  // --- Value Derivation from State ---
  const dbPartId = partData?.part_id;

  // Assembly membership is merged with GET /assemblies for role / join / ppq. On a
  // deep link the part can load BEFORE that list arrives, so re-run the merge once
  // it does. Idempotent: roles the API or the /components fallback set are kept.
  useEffect(() => {
    if (!allAssemblies.length) return;
    setAssembliesForPart(prev => (prev.length ? prev.map(r => normalizeAssemblyRow(r, allAssemblies, dbPartId)) : prev));
  }, [allAssemblies, dbPartId]);
  const productId = partData?.product_id || "";
  const description = partData?.description || "";
  const stockType = partData?.stock_type || "Sheet Stock";
  const sizeLength = partData?.size_length?.toString() || "";
  const sizeWidth = partData?.size_width?.toString() || "";
  const notes = partData?.notes || "";
  const customerId = partData?.customer_id?.toString() || "";
  const imageUrl = partData?.image_path || (partData?.image_base64 ? `data:image/png;base64,${partData.image_base64}` : "");
  const weightInGrams = partData?.weight_grams ?? "";
  const weightPoundsDisplay = partData?.weight_lbs > 0 ? parseFloat(partData.weight_lbs).toFixed(3) : "";
  const selectedProcesses = (partData?.process_steps || "").split(",").map(s => s.trim()).filter(Boolean);
  // Brake after Laser on the route: the only case the "Keep sheets apart for the Brake" box applies to (jobq-api
  // packaging_guide.brake_sheet_dividers needs the box AND this).
  const brakeAfterLaser = (() => {
    const steps = selectedProcesses.map((st) => st.toLowerCase());
    const at = steps.indexOf("laser");
    return at >= 0 && steps.slice(at + 1).includes("brake");
  })();
  
  // Time estimates
  const laserMinutes = partData?.initial_est_laser_minutes?.toString() || "";
  const tapMinutes = partData?.initial_est_tap_minutes?.toString() || "";
  const brakeMinutes = partData?.initial_est_brake_minutes?.toString() || "";
  const blastMinutes = partData?.initial_est_blast_minutes?.toString() || "";
  const finishMinutes = partData?.initial_est_finish_minutes?.toString() || "";
  const cuttingMinutes = partData?.initial_est_cutting_minutes?.toString() || "";
  const drillingMinutes = partData?.initial_est_drilling_minutes?.toString() || "";
  const millingMinutes = partData?.initial_est_milling_minutes?.toString() || "";
  const latheMinutes = partData?.initial_est_lathe_minutes?.toString() || "";
  const weldingMinutes = partData?.initial_est_weld_minutes?.toString() || "";
  const assemblyMinutes = partData?.initial_est_assembly_minutes?.toString() || "";
  const fabTapMinutes = partData?.initial_est_fab_tap_minutes?.toString() || "";

  const avgLaserHours = partData?.avg_time_per_station?.laser != null ? (partData.avg_time_per_station.laser * 60).toFixed(2) : "";
  const avgTapHours = partData?.avg_time_per_station?.tap != null ? (partData.avg_time_per_station.tap * 60).toFixed(2) : "";
  const avgBrakeHours = partData?.avg_time_per_station?.brake != null ? (partData.avg_time_per_station.brake * 60).toFixed(2) : "";
  const avgBlastHours = partData?.avg_time_per_station?.blast != null ? (partData.avg_time_per_station.blast * 60).toFixed(2) : "";
  const avgFinishHours = partData?.avg_time_per_station?.finish != null ? (partData.avg_time_per_station.finish * 60).toFixed(2) : "";
  const avgCuttingMinutes = partData?.avg_time_per_station?.cutting != null ? (partData.avg_time_per_station.cutting * 60).toFixed(2) : "";
  const avgDrillingMinutes = partData?.avg_time_per_station?.drilling != null ? (partData.avg_time_per_station.drilling * 60).toFixed(2) : "";
  const avgMillingMinutes = partData?.avg_time_per_station?.milling != null ? (partData.avg_time_per_station.milling * 60).toFixed(2) : "";
  const avgLatheMinutes = partData?.avg_time_per_station?.lathe != null ? (partData.avg_time_per_station.lathe * 60).toFixed(2) : "";
  const avgWeldingMinutes = partData?.avg_time_per_station?.welding != null ? (partData.avg_time_per_station.welding * 60).toFixed(2) : "";
  const avgAssemblyMinutes = partData?.avg_time_per_station?.assembly != null ? (partData.avg_time_per_station.assembly * 60).toFixed(2) : "";
  const avgFabTapMinutes = partData?.avg_time_per_station?.tapping != null ? (partData.avg_time_per_station.tapping * 60).toFixed(2) : "";
  const pierceCount = partData?.pierce_count || 0;

  // Helper to check if a time estimate field has a valid value (for conditional required styling)
  const hasTimeValue = (val) => val && val !== '' && val !== '0' && val !== '0:00' && val !== '00:00';

  const flagged = !!partData?.flagged;
  const flagReason = partData?.flag_reason || "";
  const flaggedByName = partData?.flagged_by_name || "";
  const flaggedAt = partData?.flagged_at || "";

  const solidProfileArea = partData?.solid_profile_area?.toString() || "";
  const materialType = partData?.material_type || "";
  const surfaceFinish = partData?.surface_finish || "";
  const thicknessId = partData?.thickness_id?.toString() || "";
  
  const cost_labor = partData?.cost_labor || 0;
  const price = partData?.price?.toString() || "";
  // F15: `??`, not `||` — a deliberate 0% must display (and re-save) as 0, not 10.
  const markup_percent = (partData?.markup_percent === null || partData?.markup_percent === undefined)
    ? "10" : String(partData.markup_percent);
  
  const ppapRequired = partData?.ppap_required ?? false;
  const ppapLevel = partData?.ppap_level?.toString() ?? "3";
  const ppapStatus = partData?.ppap_status ?? "Not Started";
  const ppapSampleSizeInitial = partData?.ppap_sample_size_initial?.toString() ?? "30";
  const ppapSampleSizeOngoing = partData?.ppap_sample_size_ongoing?.toString() ?? "3";
  const ppapLastApproved = partData?.ppap_last_approved_at;
  const ppapImageUrl = partData?.ppap_image_path || "";

  const ppapCriticalCharacteristics = Array.isArray(partData?.ppap_critical_characteristics)
      ? partData.ppap_critical_characteristics
      : [];

  // --- New Part Creation Checklist ---
  const isNewPart = !dbPartId;
  const isSheetStock = stockType === 'Sheet Stock';

  const newPartChecklist = useMemo(() => {
    if (!isNewPart || !partData) return null;

    const items = [
      {
        id: 'product-id',
        label: 'Product ID (SKU)',
        section: 'part-details-card',
        complete: !!partData.product_id?.trim(),
        required: true,
      },
      {
        id: 'description',
        label: 'Description',
        section: 'part-details-card',
        complete: !!partData.description?.trim(),
        required: isSheetStock,
      },
      {
        id: 'customer',
        label: 'Customer',
        section: 'part-details-card',
        complete: !!partData.customer_id,
        required: isSheetStock,
      },
      {
        id: 'dxf-upload',
        label: 'Upload DXF (for dimensions)',
        section: 'material-card',
        complete: !!(partData.size_length && parseFloat(partData.size_length) > 0 && partData.size_width && parseFloat(partData.size_width) > 0),
        required: isSheetStock,
        hint: 'Uploads Length & Width automatically',
      },
      {
        id: 'weight',
        label: 'Weight',
        section: 'material-card',
        complete: !!(partData.weight_grams && parseFloat(partData.weight_grams) > 0),
        required: isSheetStock,
      },
      {
        id: 'material-type',
        label: 'Material Type',
        section: 'material-card',
        complete: !!partData.material_type,
        required: isSheetStock,
      },
      {
        id: 'surface-finish',
        label: 'Surface Finish',
        section: 'material-card',
        complete: !!partData.surface_finish,
        required: isSheetStock,
      },
      {
        id: 'thickness',
        label: 'Thickness',
        section: 'material-card',
        complete: !!partData.thickness_id,
        required: isSheetStock,
      },
      {
        id: 'process-steps',
        label: 'Process Steps',
        section: 'labor-card',
        complete: !!partData.process_steps?.trim(),
        required: isSheetStock,
        hint: 'Add at least one process',
      },
      {
        id: 'profile-shape',
        label: 'Profile Shape',
        section: 'material-card',
        complete: !!partData.profile_shape,
        required: !isSheetStock, // Required for Shape Stock
      },
    ];

    // Add dynamic process time estimate items based on selected processes
    const processes = (partData.process_steps || "").split(",").map(s => s.trim()).filter(Boolean);
    const processTimeConfig = {
      'Laser': { field: 'initial_est_laser_minutes', label: 'Laser Task Time' },
      'Tap': { field: 'initial_est_tap_minutes', label: 'Tap Est. Time' },
      'Brake': { field: 'initial_est_brake_minutes', label: 'Brake Est. Time' },
      'Blast': { field: 'initial_est_blast_minutes', label: 'Blast Est. Time' },
      'Finish': { field: 'initial_est_finish_minutes', label: 'Finish Est. Time' },
      'Cutting': { field: 'initial_est_cutting_minutes', label: 'Cutting Est. Time' },
      'Drilling': { field: 'initial_est_drilling_minutes', label: 'Drilling Est. Time' },
      'Milling': { field: 'initial_est_milling_minutes', label: 'Milling Est. Time' },
      'Lathe': { field: 'initial_est_lathe_minutes', label: 'Lathe Est. Time' },
      'Welding': { field: 'initial_est_weld_minutes', label: 'Welding Est. Time' },
      'Assembly': { field: 'initial_est_assembly_minutes', label: 'Assembly Est. Time' },
      'Tapping': { field: 'initial_est_fab_tap_minutes', label: 'Fab Tapping Est. Time' },
    };

    processes.forEach(proc => {
      const config = processTimeConfig[proc];
      if (config) {
        const value = partData[config.field];
        const hasValue = value && value !== '' && value !== '0' && value !== '0:00';
        items.push({
          id: `process-time-${proc.toLowerCase()}`,
          label: config.label,
          section: 'labor-card',
          complete: hasValue,
          required: true,
          hint: 'Enter time estimate (MM:SS or minutes)',
        });
      }
    });

    // Filter to only show relevant items based on stock type
    const relevantItems = items.filter(item => {
      if (isSheetStock) {
        return item.id !== 'profile-shape';
      } else {
        // Shape Stock - show product-id, profile-shape, and any process time estimates
        return item.id === 'product-id' ||
               item.id === 'profile-shape' ||
               item.id.startsWith('process-time-');
      }
    });

    const requiredItems = relevantItems.filter(i => i.required);
    const completedRequired = requiredItems.filter(i => i.complete).length;
    const totalRequired = requiredItems.length;
    const allRequiredComplete = completedRequired === totalRequired;
    const progressPercent = totalRequired > 0 ? Math.round((completedRequired / totalRequired) * 100) : 0;

    return {
      items: relevantItems,
      completedRequired,
      totalRequired,
      allRequiredComplete,
      progressPercent,
    };
  }, [isNewPart, partData, isSheetStock, dbPartId]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // NEW: Filter thickness options based on selected material type
  const availableThicknessFilters = useMemo(() => {
    if (!filterMaterialType) return thicknessOptions;
    return thicknessOptions.filter(t => t.material_type === filterMaterialType);
  }, [thicknessOptions, filterMaterialType]);

  // NEW: Reset thickness filter when material type changes to prevent invalid combinations
  useEffect(() => {
    setFilterThickness("");
  }, [filterMaterialType]);

  const filteredAndSortedParts = useMemo(() => {
    let result = parts.filter((p) => {
      // Filter out inactive parts unless showInactive is checked
      if (!showInactive && p.active_status === false) return false;
      if (showFlaggedOnly && !p.flagged) return false;
      if (!showTemporary && p.is_temporary) return false;
      const term = searchTerm.toLowerCase();

      // Existing checks
      const matchesTerm = (p.product_id || "").toLowerCase().includes(term) || (p.description || "").toLowerCase().includes(term);
      const matchesCustomer = !filterCustomerId || p.customer_id === parseInt(filterCustomerId, 10);
      const matchesStockType = !filterStockType || p.stock_type === filterStockType;

      // NEW: Material Checks
      const matchesMaterial = !filterMaterialType || p.material_type === filterMaterialType;
      const matchesFinish = !filterSurfaceFinish || p.surface_finish === filterSurfaceFinish;
      // Compare thickness_id as string to handle select value format
      const matchesThickness = !filterThickness || (p.thickness_id && p.thickness_id.toString() === filterThickness);

      return matchesTerm && matchesCustomer && matchesStockType && matchesMaterial && matchesFinish && matchesThickness;
    });

    result.sort((a, b) => {
        // ... existing sort logic ...
        switch(sortBy) {
            case "name_asc":
                return (a.product_id || "").localeCompare(b.product_id || "");
            case "name_desc":
                return (b.product_id || "").localeCompare(a.product_id || "");
            case "date_newest":
                return (b.part_id || 0) - (a.part_id || 0);
            case "date_oldest":
                return (a.part_id || 0) - (b.part_id || 0);
            default:
                return 0;
        }
    });

    return result;
  }, [
      parts, searchTerm, filterCustomerId, filterStockType, showFlaggedOnly, showInactive, showTemporary, sortBy,
      // Add new dependencies
      filterMaterialType, filterSurfaceFinish, filterThickness
  ]);

  const filteredThicknessOptions = thicknessOptions.filter(
    (opt) => opt.material_type === materialType
  );

  const selectedShapeMaterial = shapeStockOptions.find(
    mat => mat.material_id === parseInt(partData?.material_id, 10)
  );

  // Auto-calculate weight_lbs for Shape Stock parts when material or cut length changes
  useEffect(() => {
    if (stockType === 'Shape Stock' && selectedShapeMaterial?.weight && selectedShapeMaterial?.stock_length && sizeLength && parseFloat(sizeLength) > 0) {
      const calcWeight = (selectedShapeMaterial.weight / selectedShapeMaterial.stock_length) * parseFloat(sizeLength);
      setPartData(prev => {
        if (prev?.weight_lbs !== calcWeight) {
          return { ...prev, weight_lbs: calcWeight };
        }
        return prev;
      });
    }
  }, [stockType, selectedShapeMaterial, sizeLength]);

  // Sheet Stock: derive weight from solid_profile_area × thickness × density when
  // weight hasn't been manually set. A DXF upload clears weight to trigger this.
  useEffect(() => {
    if (stockType !== 'Sheet Stock') return;
    const area = parseFloat(partData?.solid_profile_area);
    if (!area || area <= 0) return;
    if (!partData?.thickness_id || !partData?.material_type) return;
    const currentWeight = parseFloat(partData?.weight_lbs);
    if (currentWeight && currentWeight > 0) return;
    const thicknessRow = thicknessOptions.find(t => t.thickness_id === parseInt(partData.thickness_id, 10));
    const thicknessIn = thicknessRow ? parseFloat(thicknessRow.thickness_in) : null;
    const density = materialDensities[partData.material_type];
    if (!thicknessIn || !density) return;
    const calcLbs = area * thicknessIn * density;
    if (!isFinite(calcLbs) || calcLbs <= 0) return;
    setPartData(prev => ({
      ...prev,
      weight_lbs: calcLbs,
      weight_grams: (calcLbs * 453.592).toFixed(2),
    }));
  }, [
    stockType,
    partData?.solid_profile_area,
    partData?.thickness_id,
    partData?.material_type,
    partData?.weight_lbs,
    thicknessOptions,
    materialDensities,
  ]);

  // --- Event Handlers & Functions ---

  const handleSelectPart = async (p) => {
    if (isDirty && !window.confirm("You have unsaved changes. Are you sure you want to discard them?")) {
      return;
    }
    setView('single');
    setSelectedPart(p);
    setAiContext('part', { partId: p.part_id, partDescription: p.description, productId: p.product_id });
    setError(null);
    setPartData(null);
    
    // Clear file state
    setPartDocuments([]);
    setDocUploadFile(null);
    setDocDisplayName("");
    setDocDescription("");
    setPendingDxfFile(null);
    
    setRevisionHistory([]);
    setIsReadOnly(false);

    try {
      // 1. Fetch Part Details (features/parts/partData, the phone's read too)
      const fullPart = await loadPart(p.part_id);

      // 2. Fetch Calculated Costs (includes distinct Gas Cost)
      const costRes = await fetch(`${API_BASE}/parts/${p.part_id}/calculate_cost`);
      const costData = costRes.ok ? await costRes.json() : { calculated_labor_cost: 0, calculated_gas_cost: 0, log: "" };
      
      const initialGrams = (fullPart.weight_lbs != null ? parseFloat(fullPart.weight_lbs) : 0) * 453.592;
      const cleanedProcessSteps = partProcessSteps(fullPart).join(',');
      
      const readOnly = isArchivedRevision(fullPart);
      setIsReadOnly(readOnly);

      const hist = await loadPartRevisions(fullPart.product_id);
      if (hist) {
        setRevisionHistory(hist);
        refreshOpenRefsWarning(hist);
      }

      try {
          const apprRes = await fetch(`${API_BASE}/parts/${encodeURIComponent(fullPart.product_id)}/revision_approvals`);
          setRevisionApprovals(apprRes.ok ? await apprRes.json() : []);
      } catch (e) { console.error("Failed to fetch revision approvals", e); setRevisionApprovals([]); }
      
      // Stored snapshot (what the Assemblies card / SO conversion read) is kept
      // apart from the live engine figures below. If the engine call failed,
      // show the STORED labor/gas/consumables rather than zeros.
      setStoredSnap(snapshotFromPart(fullPart));
      setCostEngine({ engine: costRes.ok ? 'calculate_cost' : 'stored_fallback', at: new Date().toISOString() });
      setCostShuttle(describeShuttle(costRes.ok ? costData : null));
      // The card fetches on mount; setPartData(null) at the top of this handler unmounted
      // it, and the setPartData below remounts it, so these rows are re-fed on EVERY
      // select — including re-selecting the same part (pinned by the smoke test).
      setOutsourcingRows({ steps: [], prices: [], loaded: false });
      setCoatingFromCard(null);
      setCostNotice(null);
      setCalcLogOpen(false);

      setPartData({
        ...fullPart,
        process_steps: cleanedProcessSteps,
        stock_type: fullPart.stock_type || "Sheet Stock",
        markup_percent: fullPart.markup_percent ?? 10,
        cost_labor: costRes.ok ? costData.calculated_labor_cost : fullPart.cost_labor,
        estimated_gas_cost: costRes.ok ? (costData.calculated_gas_cost || 0) : (fullPart.estimated_gas_cost || 0),
        estimated_consumable_cost: costRes.ok ? (costData.calculated_consumable_cost || 0) : (fullPart.estimated_consumable_cost || 0),
        last_calculation_log: costData.log || "", // Init with server log
        pierce_count: fullPart.pierce_count || 0,
        flagged: !!fullPart.flagged,
        weight_grams: initialGrams > 0 ? initialGrams.toFixed(2) : "",
        ppap_required: fullPart.ppap_required ?? false,
        brake_sheet_dividers: !!fullPart.brake_sheet_dividers,
        ppap_level: fullPart.ppap_level ?? 3,
        ppap_status: fullPart.ppap_status ?? "Not Started",
        ppap_sample_size_initial: fullPart.ppap_sample_size_initial ?? 30,
        ppap_sample_size_ongoing: fullPart.ppap_sample_size_ongoing ?? 3,
        ppap_critical_characteristics: Array.isArray(fullPart.ppap_critical_characteristics) ? fullPart.ppap_critical_characteristics : [],
        beam_on_minutes: fullPart.beam_on_minutes || 0,
      });
      setIsDirty(false);

      loadCutSheetsForPart(p.part_id);
      loadCutListsForPart(p.part_id);
      loadAssembliesForPart(p.part_id, fullPart.assemblies);
      loadPartDocuments(p.part_id);
      setMaterialPrices({ price_by_weight: 0, price_by_bounding_area: 0, price_by_solid_profile: 0, price_by_cut_length: 0, price_by_stock_length: 0 });

      // Load consumable yield linkage
      try {
        const yieldRes = await fetch(`${API_BASE}/parts/${p.part_id}/consumable-yield`);
        if (yieldRes.ok) {
          const yieldData = await yieldRes.json();
          const yieldVal = yieldData?.consumable_id ? String(yieldData.consumable_id) : "";
          setConsumableYieldId(yieldVal);
          setSavedConsumableYieldId(yieldVal);
        } else {
          setConsumableYieldId("");
          setSavedConsumableYieldId("");
        }
      } catch (e) {
        console.error("Failed to fetch consumable yield:", e);
        setConsumableYieldId("");
        setSavedConsumableYieldId("");
      }

    } catch (err) {
      setError(parseApiError(err.message, 'Failed to load part details'));
      setPartData(null);
    }
  };

  // Deep-link: /parts/:partId (e.g. the "Open Part" link on a quote line or the SO
  // part-detail card) auto-opens that part in the single-part view on mount.
  // handleSelectPart fetches full details by id, so { part_id } alone is enough.
  // Guard so it fires once per mount.
  useEffect(() => {
    if (!routePartId || deepLinkOpenedRef.current) return;
    const idNum = parseInt(routePartId, 10);
    if (!idNum) return;
    deepLinkOpenedRef.current = true;
    handleSelectPart({ part_id: idNum });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routePartId]);

  const handlePartDataChange = (field, value) => {
    setPartData(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  // Multi-field variant for widgets that own a group of related fields (the
  // coating color code/name/hex triple). Going through setPartData directly
  // would skip setIsDirty, so a color-only edit would not count as unsaved.
  const handlePartDataPatch = (patch) => {
    setPartData(prev => ({ ...prev, ...patch }));
    setIsDirty(true);
  };

  const handleMaterialPriceMethodChange = (e) => {
      const newMethod = e.target.value;
      setPartData(prev => {
          let materialComponent = 0;
          // 'manual' = typed manual_material_price; $0 / blank = customer supplied. Never
          // force 0 when a typed price exists (server contract 2026-09-02).
          if (newMethod === 'manual' || newMethod === 'manual_price') materialComponent = safeParseFloat(prev.manual_material_price) || 0;
          else if (newMethod.startsWith("nested_cut_list_")) {
              const cutListPartId = parseInt(newMethod.split("_")[3], 10);
              const selectedCutList = cutListsForPart.find(cl => cl.cut_list_part_id === cutListPartId);
              if (selectedCutList) materialComponent = selectedCutList.material_cost || 0;
          } else if (newMethod.startsWith("nested_")) {
              const cutSheetPartId = parseInt(newMethod.split("_")[1], 10);
              const selectedCutSheet = cutSheetsForPart.find(cs => cs.cut_sheet_part_id === cutSheetPartId);
              if (selectedCutSheet) materialComponent = selectedCutSheet.material_cost || 0;
          } else if (materialPrices) {
              if (newMethod === 'weight') materialComponent = materialPrices.price_by_weight;
              else if (newMethod === 'bounding_area') materialComponent = materialPrices.price_by_bounding_area;
              else if (newMethod === 'solid_profile') materialComponent = materialPrices.price_by_solid_profile;
              else if (newMethod === 'cut_length') materialComponent = materialPrices.price_by_cut_length;
          }
          return { ...prev, material_price_method: newMethod, material_cost_component: materialComponent };
      });
      setIsDirty(true);
  };
  
  const handleWeightGramsChange = (gramsStr) => {
      const grams = safeParseFloat(gramsStr);
      const pounds = grams / 453.592;
      setPartData(prev => ({ ...prev, weight_grams: gramsStr, weight_lbs: pounds }));
      setIsDirty(true);
  };

  // Assembly membership (F07). GET /parts/{id}.assemblies lists BOTH primary and
  // component memberships; the old scan of every assembly's /components could
  // not see a primary at all (so a primary part read "No assemblies found") and
  // cost one request per assembly in the shop. Rows are merged with GET
  // /assemblies for role / join / ppq when the part API predates Workstream B2
  // (which adds role, join_workstation, ops_owned_steps, assembly_prices_coating),
  // and component quantities are fetched only for the assemblies listed.
  async function loadAssembliesForPart(pid, apiRows) {
    setLoadingAssembliesForPart(true);
    setAssembliesForPart([]);
    try {
      let rows;
      if (Array.isArray(apiRows)) {
        rows = apiRows.map(r => normalizeAssemblyRow(r, allAssemblies, pid));
      } else {
        // Legacy fallback (part API without `assemblies`): component scan.
        rows = [];
        for (const asm of allAssemblies) {
          try {
            const compRes = await fetch(`${API_BASE}/assemblies/${asm.assembly_id}/components`);
            if (!compRes.ok) continue;
            const components = await compRes.json();
            const match = (components || []).find((c) => c.part_id === pid);
            if (match) rows.push(normalizeAssemblyRow({ ...asm, ...match, role: 'component' }, allAssemblies, pid));
          } catch (err) { console.warn("assembly detail error:", err); }
        }
      }
      const needQty = rows.filter(r => r.role !== 'primary' && (r.quantity_per_assembly === null || r.quantity_per_assembly === undefined));
      await Promise.all(needQty.map(async (r) => {
        try {
          const compRes = await fetch(`${API_BASE}/assemblies/${r.assembly_id}/components`);
          if (!compRes.ok) return;
          const components = await compRes.json();
          const match = (components || []).find((c) => c.part_id === pid);
          if (match) {
            r.quantity_per_assembly = match.quantity_per_assembly ?? null;
            if (!r.role) { r.role = 'component'; r.roleSource = 'inferred'; }
          } else if (!r.role) {
            // Listed by the part API but not a component row → it is the primary.
            r.role = 'primary'; r.roleSource = 'inferred';
            // ppq may be unknown until GET /assemblies has loaded; the merge effect fills it.
            r.quantity_per_assembly = r.primary_part_quantity ?? null;
          }
        } catch (err) { console.warn("assembly detail error:", err); }
      }));
      setAssembliesForPart(rows);
    } finally {
      setLoadingAssembliesForPart(false);
    }
  }

  function handleShowSingleNew() {
    if (isDirty && !window.confirm("You have unsaved changes. Are you sure you want to discard them?")) {
      return;
    }
    setView('single');
    setSelectedPart(null);
    setRevisionHistory([]);
    setIsReadOnly(false);
    setPartData({
      part_id: null, product_id: "", customer_drawing_id: "", description: "", stock_type: "Sheet Stock", profile_shape: "",
      size_length: "", size_width: "", weight_lbs: 0, weight_grams: "", price: "", notes: "",
      process_steps: "", image_path: "", customer_id: "",
      initial_est_laser_minutes: "", initial_est_tap_minutes: "", initial_est_brake_minutes: "",
      initial_est_blast_minutes: "", initial_est_finish_minutes: "", initial_est_cutting_minutes: "",
      initial_est_drilling_minutes: "", initial_est_milling_minutes: "", initial_est_lathe_minutes: "",
      lathe_op_count: "", milling_op_count: "",
      lathe_op_labels: null, milling_op_labels: null,
      initial_est_weld_minutes: "", initial_est_assembly_minutes: "", initial_est_fab_tap_minutes: "",
      pierce_count: 0, solid_profile_area: "", material_type: "", surface_finish: "", thickness_id: "",
      material_price_method: "manual", flagged: false, flag_reason: "", flagged_by_name: "", flagged_at: "",
      cost_labor: 0, cost_labor_material: 0, markup_percent: "10", price_labor_material: 0, manual_material_price: "",
      estimated_gas_cost: 0, // Initialize Gas Cost
      last_calculation_log: "", // Initialize Log
      ppap_required: false, ppap_level: 3, ppap_status: "Not Started", ppap_sample_size_initial: 30,
      brake_sheet_dividers: false,
      ppap_sample_size_ongoing: 3, ppap_frequency: "100%", ppap_last_approved_at: null, ppap_critical_characteristics: [], ppap_image_path: "",
      beam_on_minutes: 0,
    });
    setIsDirty(false);
    setStoredSnap(null);
    setCostEngine({ engine: null, at: null });
    setCostShuttle(describeShuttle(null));
    setOutsourcingRows({ steps: [], prices: [], loaded: false });
    setCoatingFromCard(null);
    setCostNotice(null);
    setCalcLogOpen(false);
    setCutSheetsForPart([]); setCutListsForPart([]); setAssembliesForPart([]); setPartDocuments([]);
    setMaterialPrices({ price_by_weight: 0, price_by_bounding_area: 0, price_by_solid_profile: 0, price_by_cut_length: 0, price_by_stock_length: 0 });
    setUploadFile(null);
    setPpapUploadFile(null);
    setPendingDxfFile(null);
    setConsumableYieldId(""); setSavedConsumableYieldId("");
  }

  function handleShowBulkCreate() {
    if (isDirty && !window.confirm("You have unsaved changes. Are you sure you want to discard them?")) {
      return;
    }
    setView('bulk');
    setSelectedPart(null); 
    setPartData(null);    
    setIsDirty(false);
  }

  // --- REVISION HANDLING WITH MODAL ---
  function handleRevisionClick() {
    if (!dbPartId) return;
    setShowRevModal(true);
  }

  async function handleConfirmRevision(copyFiles) {
    setShowRevModal(false);
    const pin = await pinPrompt("Enter PIN to create the revision:");
    if (!pin) return;
    try {
        // copy_files stays a query param (documents); the body feeds the
        // revision paper trail (change description + approval form).
        const res = await fetch(`${API_BASE}/parts/${dbPartId}/create_revision?copy_files=${copyFiles}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                change_description: revChangeDescription.trim() || null,
                change_requested_by: revRequestedBy,
                pin,
                generate_approval_form: revGenerateForm,
                pricing_impact: revPricingImpact || null,
                pricing_quote_ref: revPricingImpact === 'revised' ? (revPricingQuoteRef.trim() || null) : null,
            }),
        });

        if (!res.ok) throw new Error(await apiErrorMessage(res));

        const result = await res.json();
        let msg = `Success! Created Revision ${result.new_revision_code}`;
        if (copyFiles) msg += " (Files Copied)";
        if (result.approval_form) {
            msg += "\n\n📄 Customer approval form generated (see Part Files / Revision History). " +
                   "Print or email it for signature, then upload the signed copy on the Revision History card.";
        }
        if (result.approval_form_error) msg += `\n\n⚠ Approval form: ${result.approval_form_error}`;
        if (result.warning) msg += `\n\nWARNING:\n${result.warning}`;
        alert(msg);
        setRevChangeDescription("");

        setPartData(null);
        await loadParts();

        const newPartRes = await fetch(`${API_BASE}/parts/${result.new_part_id}`);
        if(newPartRes.ok) {
            const newPartData = await newPartRes.json();
            handleSelectPart({
                part_id: newPartData.part_id,
                product_id: newPartData.product_id,
                description: newPartData.description
            });
        }

        if (result.approval_form && result.approval_form.file_url) {
            window.open(result.approval_form.file_url, "_blank");
        }

        const refs = result.open_order_refs;
        if (refs && (refs.eligible_so_lines > 0 || refs.eligible_quote_lines > 0)) {
            const wantsUpdate = window.confirm(
                `${refs.so_lines} open sales-order line(s) and ${refs.quote_lines} quote line(s) still reference the OLD revision.\n\n` +
                "Since pricing is unchanged, they can be updated in place (same SO / quote numbers — no re-issue).\n\n" +
                "Review and update them now?"
            );
            if (wantsUpdate) setRepointPartId(result.new_part_id);
        }
    } catch (err) {
        setError(parseApiError(err.message, 'Failed to create revision'));
    }
  }

  async function loadRevisionApprovals(productId) {
    if (!productId) return;
    try {
        const res = await fetch(`${API_BASE}/parts/${encodeURIComponent(productId)}/revision_approvals`);
        setRevisionApprovals(res.ok ? await res.json() : []);
    } catch (e) { console.error("Failed to fetch revision approvals", e); }
  }

  // Does anything (assembly BOM, open SO/quote line) still reference an OLDER
  // revision of this part family? Drives the warning strip in the Revision
  // History card. Non-fatal.
  async function refreshOpenRefsWarning(hist) {
    setOpenRefsWarning(null);
    const list = hist || revisionHistory || [];
    const curRev = list.find(r => r.is_current_revision);
    if (list.length <= 1 || !curRev) return;
    try {
      const res = await fetch(`${API_BASE}/parts/${curRev.part_id}/open_order_references`);
      if (!res.ok) return;
      const refs = await res.json();
      const counts = {
        assemblies: (refs.assembly_refs || []).length,
        soLines: (refs.so_lines || []).length,
        quoteLines: (refs.quote_lines || []).length,
        currentPartId: curRev.part_id,
      };
      if (counts.assemblies + counts.soLines + counts.quoteLines > 0) setOpenRefsWarning(counts);
    } catch (e) { /* non-fatal */ }
  }

  async function handleGenerateApprovalForm(newPartId, existingDescription) {
    let desc = (existingDescription || "").trim();
    if (!desc) {
        desc = (window.prompt("Describe the change (printed on the customer approval form):", "") || "").trim();
    }
    const pin = await pinPrompt("Enter PIN to generate the approval form:");
    if (!pin) return;
    try {
        const pricingSel = pricingSelByPart[newPartId] || {};
        const res = await fetch(`${API_BASE}/parts/${newPartId}/revision_approval_form`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                change_description: desc || null,
                pin,
                pricing_impact: pricingSel.impact || null,
                pricing_quote_ref: pricingSel.impact === 'revised' ? (pricingSel.ref || null) : null,
            }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) { alert(`Failed to generate form: ${detailToString(data.detail) || res.status}`); return; }
        if (data.file_url) window.open(data.file_url, "_blank");
        alert("Approval form generated — print or email it to the customer, then upload the signed copy here.");
        await loadRevisionApprovals(partData?.product_id);
        if (dbPartId) loadPartDocuments(dbPartId);
    } catch (e) {
        alert(`Failed to generate form: ${e.message || e}`);
    }
  }

  // Create (or replace) the online-approval portal link for a revision and
  // optionally email it to the customer. The link is the customer's whole
  // credential — approving on the page e-signs and stores the record.
  async function handleCreatePortalLink(newPartId) {
    const pin = await pinPrompt("Enter PIN to create the online approval link:");
    if (!pin) return;
    const emails = (window.prompt(
      "Email the approval link to (comma-separated addresses)?\nLeave blank to just copy the link.", ""
    ) || "").split(",").map(s => s.trim()).filter(Boolean);
    try {
      const res = await fetch(`${API_BASE}/parts/${newPartId}/revision_approval_link`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...emailSenderHeaders() },
        body: JSON.stringify({ pin, send_email: emails.length > 0, recipient_emails: emails }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(detailToString(data.detail) || `HTTP ${res.status}`);
      try { await navigator.clipboard.writeText(data.portal_url); } catch (e) { /* prompt below covers it */ }
      window.prompt(
        `${data.message}. Link copied to clipboard (expires ${data.expires_at ? new Date(data.expires_at).toLocaleDateString() : "in 30 days"}):`,
        data.portal_url
      );
      if (partData?.product_id) loadRevisionApprovals(partData.product_id);
    } catch (e) {
      alert(`Failed to create link: ${e.message || e}`);
    }
  }

  // Merge the part-family revision list with recorded approval events into
  // display rows — one row per revision TRANSITION (oldest revision has none).
  // revisionHistory is sorted newest-first by the API.
  function buildRevisionTrailRows() {
    const byNewPart = {};
    (revisionApprovals || []).forEach(ev => { byNewPart[ev.new_part_id] = ev; });
    const hist = revisionHistory || [];
    const rows = [];
    for (let i = 0; i < hist.length - 1; i++) {
      const rev = hist[i];
      const prev = hist[i + 1];
      const ev = byNewPart[rev.part_id] || null;
      rows.push({
        key: rev.part_id,
        new_part_id: rev.part_id,
        old_code: ev ? ev.old_revision_code : prev.revision_code,
        new_code: ev ? ev.new_revision_code : rev.revision_code,
        created_at: ev ? ev.created_at : rev.created_at,
        is_current: !!rev.is_current_revision,
        event: ev,
      });
    }
    return rows;
  }

  async function handleUploadSignedApproval(eventId, file) {
    if (!file) return;
    const tooBig = checkUploadSize(file, "signed approval");
    if (tooBig) { alert(tooBig); return; }
    const pin = await pinPrompt("Enter PIN to record the signed approval:");
    if (!pin) return;
    try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("pin", pin);
        const res = await fetch(`${API_BASE}/revision_events/${eventId}/signed_document`, { method: "POST", body: fd });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) { alert(`Failed to record signed approval: ${detailToString(data.detail) || res.status}`); return; }
        alert("✓ Signed approval recorded on the part.");
        await loadRevisionApprovals(partData?.product_id);
        if (dbPartId) loadPartDocuments(dbPartId);
    } catch (e) {
        alert(`Failed to record signed approval: ${e.message || e}`);
    }
  }

  async function handleSavePart() {
    setError(null);
    if (!partData || !partData.product_id?.trim()) { alert("Product ID (SKU) is required."); return; }
    if (partData.stock_type === 'Shape Stock' && !partData.profile_shape) { alert('Please select a Profile Shape for Shape Stock parts.'); return; }

    // Pre-submission duplicate SKU check — avoids a round trip and shows a specific
    // message instead of the API's sanitized 500.
    const normalizedSku = partData.product_id.trim().toLowerCase();
    const duplicate = parts.find(p =>
      (p.product_id || "").trim().toLowerCase() === normalizedSku && p.part_id !== dbPartId
    );
    if (duplicate) {
      setError(`A part with Product ID "${partData.product_id.trim()}" already exists (part #${duplicate.part_id}). Please use a unique Product ID.`);
      return;
    }

    // Sheet Stock required field validation
    if (partData.stock_type === 'Sheet Stock') {
      if (!partData.description?.trim()) { alert("Description is required for Sheet Stock parts."); return; }
      if (!partData.customer_id) { alert("Customer is required for Sheet Stock parts."); return; }
      if (!partData.size_length || parseFloat(partData.size_length) <= 0) { alert("Length is required for Sheet Stock parts."); return; }
      if (!partData.size_width || parseFloat(partData.size_width) <= 0) { alert("Width is required for Sheet Stock parts."); return; }
      if (!partData.weight_grams || parseFloat(partData.weight_grams) <= 0) { alert("Weight is required for Sheet Stock parts."); return; }
      if (!partData.material_type) { alert("Material Type is required for Sheet Stock parts."); return; }
      if (!partData.surface_finish) { alert("Surface Finish is required for Sheet Stock parts."); return; }
      if (!partData.thickness_id) { alert("Thickness is required for Sheet Stock parts."); return; }
      if (!partData.process_steps?.trim()) { alert("At least one process must be added for Sheet Stock parts."); return; }
    }

    // Process time estimate validation - require time for each selected process
    const processes = (partData.process_steps || "").split(",").map(s => s.trim()).filter(Boolean);
    const processTimeFields = {
      'Laser': { field: 'initial_est_laser_minutes', label: 'Laser Task Time' },
      'Tap': { field: 'initial_est_tap_minutes', label: 'Tap Est. Time' },
      'Brake': { field: 'initial_est_brake_minutes', label: 'Brake Est. Time' },
      'Blast': { field: 'initial_est_blast_minutes', label: 'Blast Est. Time' },
      'Finish': { field: 'initial_est_finish_minutes', label: 'Finish Est. Time' },
      'Cutting': { field: 'initial_est_cutting_minutes', label: 'Cutting Est. Time' },
      'Drilling': { field: 'initial_est_drilling_minutes', label: 'Drilling Est. Time' },
      'Milling': { field: 'initial_est_milling_minutes', label: 'Milling Est. Time' },
      'Lathe': { field: 'initial_est_lathe_minutes', label: 'Lathe Est. Time' },
      'Welding': { field: 'initial_est_weld_minutes', label: 'Welding Est. Time' },
      'Assembly': { field: 'initial_est_assembly_minutes', label: 'Assembly Est. Time' },
      'Tapping': { field: 'initial_est_fab_tap_minutes', label: 'Fab Tapping Est. Time' },
    };
    for (const proc of processes) {
      const config = processTimeFields[proc];
      if (config) {
        const value = partData[config.field];
        if (!value || value === '' || value === '0' || value === '0:00') {
          alert(`${config.label} is required when "${proc}" process is selected.`);
          return;
        }
      }
    }

    // LASER SPEC VALIDATION ---
    // Check if this part uses a specific material definition locally
    if (partData.stock_type === 'Sheet Stock' && partData.material_type && partData.thickness_id) {
         try {
            const checkRes = await fetch(`${API_BASE}/laser_specs/validate?material_type=${encodeURIComponent(partData.material_type)}&thickness_id=${partData.thickness_id}`);
            const checkData = await checkRes.json();

            if (!checkData.exists) {
                 const proceed = window.confirm(
                    `⚠️ MISSING LASER TECH SPEC ⚠️\n\n` +
                    `The defined Material Type and Thickness do not match any Laser Tech Spec.\n` +
                    `Gas cost estimation will be $0.00.\n\n` +
                    `Save anyway?`
                );
                if (!proceed) return;
            }
         } catch (err) {
             console.error("Spec validation failed", err);
         }
    }
    if (partData.ppap_required) {
        if (!partData.ppap_level || parseInt(partData.ppap_level) < 1 || parseInt(partData.ppap_level) > 5) {
            alert("PPAP Level must be between 1 and 5."); return;
        }
    }

    const normalizedProfileShape = partData.stock_type === 'Shape Stock' ? (partData.profile_shape ?? null) : null;
    const payload = {
      product_id: partData.product_id, customer_drawing_id: partData.customer_drawing_id, description: partData.description, stock_type: partData.stock_type, profile_shape: normalizedProfileShape,
      material_id: partData.material_id ? parseInt(partData.material_id, 10) : null,
      size_length: safeParseFloat(partData.size_length), size_width: safeParseFloat(partData.size_width),
      weight_lbs: partData.weight_lbs, price: safeParseFloat(partData.price),
      manual_material_price: safeParseFloat(partData.manual_material_price), cost_labor: partData.cost_labor,
      estimated_gas_cost: partData.estimated_gas_cost, // ADDED: Persist Gas Cost
      // F14: never drop the consumable term (an undefined key is omitted from JSON
      // and the stored rollup then disagreed with its own components).
      estimated_consumable_cost: partData.estimated_consumable_cost ?? 0,
      // F06 / F15: the totals sent are the SERVER formula over the figures on
      // screen (liveRollup). The post-Workstream-B PATCH recomputes them from the
      // components anyway and echoes the result back.
      cost_labor_material: liveRollup.cost_labor_material, notes: partData.notes,
      process_steps: partData.process_steps, image_path: partData.image_path,
      customer_id: partData.customer_id ? parseInt(partData.customer_id, 10) : null,
      initial_est_laser_minutes: parseTimeToMinutes(partData.initial_est_laser_minutes),
      initial_est_tap_minutes: parseTimeToMinutes(partData.initial_est_tap_minutes),
      initial_est_brake_minutes: parseTimeToMinutes(partData.initial_est_brake_minutes),
      initial_est_blast_minutes: parseTimeToMinutes(partData.initial_est_blast_minutes),
      initial_est_finish_minutes: parseTimeToMinutes(partData.initial_est_finish_minutes),
      initial_est_cutting_minutes: parseTimeToMinutes(partData.initial_est_cutting_minutes),
      initial_est_drilling_minutes: parseTimeToMinutes(partData.initial_est_drilling_minutes),
      initial_est_milling_minutes: parseTimeToMinutes(partData.initial_est_milling_minutes),
      initial_est_lathe_minutes: parseTimeToMinutes(partData.initial_est_lathe_minutes),
      // Multi-op machining. Blank / 1 => null, i.e. an ordinary single-setup part.
      lathe_op_count: partData.lathe_op_count ? parseInt(partData.lathe_op_count, 10) : null,
      milling_op_count: partData.milling_op_count ? parseInt(partData.milling_op_count, 10) : null,
      lathe_op_labels: partData.lathe_op_labels?.length ? partData.lathe_op_labels : null,
      milling_op_labels: partData.milling_op_labels?.length ? partData.milling_op_labels : null,
      initial_est_weld_minutes: parseTimeToMinutes(partData.initial_est_weld_minutes),
      initial_est_assembly_minutes: parseTimeToMinutes(partData.initial_est_assembly_minutes),
      initial_est_fab_tap_minutes: parseTimeToMinutes(partData.initial_est_fab_tap_minutes),
      pierce_count: parseInt(partData.pierce_count || 0, 10),
      solid_profile_area: partData.solid_profile_area ? parseFloat(partData.solid_profile_area) : null,
      material_type: partData.material_type, surface_finish: partData.surface_finish,
      thickness_id: partData.thickness_id ? parseInt(partData.thickness_id, 10) : null,
      material_price_method: partData.material_price_method, material_cost_component: partData.material_cost_component || 0,
      // F15: blank → 10, a typed 0 stays 0 (safeParseFloat mapped '' to 0 and ignored the default).
      markup_percent: normalizeMarkupPercent(partData.markup_percent), price_labor_material: liveRollup.price_labor_material,
      ppap_required: partData.ppap_required,
      // Brake sheet dividers (2026-11-04): keep this part's laser sheets apart for the Brake (cardboard between sheets).
      brake_sheet_dividers: !!partData.brake_sheet_dividers,
      ppap_level: parseInt(partData.ppap_level || 3, 10),
      ppap_status: partData.ppap_status || "Not Started",
      ppap_sample_size_initial: parseInt(partData.ppap_sample_size_initial || 30, 10),
      ppap_sample_size_ongoing: parseInt(partData.ppap_sample_size_ongoing || 3, 10),
      ppap_frequency: partData.ppap_frequency,
      ppap_last_approved_at: partData.ppap_last_approved_at,
      ppap_critical_characteristics: partData.ppap_critical_characteristics,
      ppap_image_path: partData.ppap_image_path,
      active_status: partData.active_status !== false,
      // Decision 3 (wave 2): coating_cost_per_unit is DELIBERATELY absent from this
      // whitelist. Its editor is the Outsourcing card (PATCH /parts/{id}/processes/{ppid}
      // writes the row and parts.coating_* together), plus the price-book write-through
      // (set-preferred / RFQ award) and the nightly; the page's copy went back on every
      // Save and reverted them with a 200. The key is spread in ONLY when the card
      // changed the value this session — and then it is the value the server already
      // holds. PATCH /parts uses exclude_unset, so an absent key leaves the column alone.
      ...coatingPayloadFromCard(coatingFromCard),
      coating_vendor_id: partData.coating_vendor_id ? parseInt(partData.coating_vendor_id, 10) : null,
      // Sent as typed — the API canonicalizes nothing about the code itself and
      // re-derives name/hex from its standards catalog, so this is the whole triple.
      coating_color_code: (partData.coating_color_code || '').trim() || null,
      coating_color_name: (partData.coating_color_name || '').trim() || null,
      coating_color_hex: (partData.coating_color_hex || '').trim() || null,
    };
    const url = dbPartId ? `${API_BASE}/parts/${dbPartId}` : `${API_BASE}/parts`;
    const method = dbPartId ? "PATCH" : "POST";
    try {
      const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      setIsDirty(false);
      setCoatingFromCard(null);

      if (dbPartId) {
        // The stored snapshot is now what we just sent. A post-Workstream-B PATCH
        // echoes the SERVER-recomputed rollup; prefer that when present and say
        // so if it disagrees with the page (its stored components differed).
        const echo = await res.json().catch(() => null);
        const sentSnap = snapshotFromPart({ ...partData, ...payload, part_id: dbPartId });
        const srvCost = echo && echo.cost_labor_material !== undefined && echo.cost_labor_material !== null ? Number(echo.cost_labor_material) : null;
        const srvPrice = echo && echo.price_labor_material !== undefined && echo.price_labor_material !== null ? Number(echo.price_labor_material) : null;
        setStoredSnap(prev => ({
          ...(prev || {}),
          ...sentSnap,
          cost_labor_material: srvCost ?? sentSnap.cost_labor_material,
          price_labor_material: srvPrice ?? sentSnap.price_labor_material,
          cost_updated_at: (echo && echo.cost_updated_at) || new Date().toISOString(),
        }));
        if (srvCost !== null && Math.abs(srvCost - liveRollup.cost_labor_material) > 0.005) {
          setCostNotice(`Saved. The server recomputed the stored total as ${fmtMoney(srvCost)} where this page computed ${fmtMoney(liveRollup.cost_labor_material)} — reload the part to see the components it used.`);
        } else {
          setCostNotice(null);
        }
        alert(`Part #${dbPartId} updated!`);
        refreshStoredSnapshot(dbPartId);
      } else {
        const created = await res.json();

        // If a DXF was uploaded during the new-part flow, persist it now that
        // we have a part_id. analyze_dxf saves the DXF + rendered PNG + outline
        // SVG to S3, writes a part_documents row, and returns the canonical
        // image_path so the right-side preview swaps from base64 to S3.
        if (pendingDxfFile) {
          try {
            const dxfForm = new FormData();
            dxfForm.append("file", pendingDxfFile);
            const dxfRes = await fetch(`${API_BASE}/parts/${created.part_id}/analyze_dxf`, { method: "POST", body: dxfForm });
            if (dxfRes.ok) {
              const dxfData = await dxfRes.json();
              if (dxfData.image_path) created.image_path = dxfData.image_path;
              // analyze_dxf just wrote the DXF's own area / size / pierce count over
              // what was saved; put back any the user corrected after the analysis.
              const keep = dxfValuesToKeep(payload, dxfData);
              if (keep) {
                const keepRes = await fetch(`${API_BASE}/parts/${created.part_id}`, {
                  method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(keep),
                });
                if (!keepRes.ok) {
                  alert("Part was created, but the area / size you corrected was replaced by the DXF's own numbers. Re-enter them and Save.");
                }
              }
            } else {
              console.error("DXF persist failed:", await dxfRes.text());
              alert("Part was created, but the DXF file failed to attach. Please re-upload it via the Upload DXF button.");
            }
          } catch (dxfErr) {
            console.error("Failed to persist DXF after part create:", dxfErr);
            alert("Part was created, but the DXF file failed to attach. Please re-upload it via the Upload DXF button.");
          }
          setPendingDxfFile(null);
        } else if (partData.image_base64) {
          // Image-only path (e.g. user pasted a PNG without a DXF)
          try {
            const byteCharacters = atob(partData.image_base64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'image/png' });
            const file = new File([blob], 'dxf_preview.png', { type: 'image/png' });

            const formData = new FormData();
            formData.append("file", file);
            const uploadRes = await fetch(`${API_BASE}/parts/${created.part_id}/upload-image`, { method: "POST", body: formData });
            if (uploadRes.ok) {
              const uploadData = await uploadRes.json();
              created.image_path = uploadData.image_url;
            }
          } catch (uploadErr) {
            console.error("Failed to upload preview image:", uploadErr);
          }
        }

        alert(`New part created! ID = ${created.part_id}`);
        // Recompute weight_grams from weight_lbs (API doesn't return weight_grams — it's frontend-only)
        const createdGrams = (created.weight_lbs != null ? parseFloat(created.weight_lbs) : 0) * 453.592;
        setPartData({
          ...created,
          weight_grams: createdGrams > 0 ? createdGrams.toFixed(2) : "",
          stock_type: created.stock_type || "Sheet Stock",
          markup_percent: created.markup_percent ?? 10,
          flagged: !!created.flagged,
          ppap_required: created.ppap_required ?? false,
          brake_sheet_dividers: !!created.brake_sheet_dividers,
          ppap_level: created.ppap_level ?? 3,
          ppap_status: created.ppap_status ?? "Not Started",
          ppap_sample_size_initial: created.ppap_sample_size_initial ?? 30,
          ppap_sample_size_ongoing: created.ppap_sample_size_ongoing ?? 3,
          ppap_critical_characteristics: Array.isArray(created.ppap_critical_characteristics) ? created.ppap_critical_characteristics : [],
        });
        setStoredSnap(snapshotFromPart({ ...payload, ...created, cost_updated_at: created.cost_updated_at || new Date().toISOString() }));
        setCostEngine({ engine: 'stored_fallback', at: new Date().toISOString() });
        loadPartDocuments(created.part_id);
        refreshStoredSnapshot(created.part_id);
      }
      // Save consumable yield linkage if changed
      const partIdForYield = dbPartId || partData?.part_id;
      if (partIdForYield && consumableYieldId !== savedConsumableYieldId) {
        try {
          if (savedConsumableYieldId) {
            await fetch(`${API_BASE}/parts/${partIdForYield}/consumable-yield/${savedConsumableYieldId}`, { method: "DELETE" });
          }
          if (consumableYieldId) {
            await fetch(`${API_BASE}/parts/${partIdForYield}/consumable-yield`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ consumable_id: parseInt(consumableYieldId, 10) })
            });
          }
          setSavedConsumableYieldId(consumableYieldId);
        } catch (yieldErr) {
          console.error("Failed to save consumable yield linkage:", yieldErr);
        }
      }

      loadParts();
    } catch (err) {
      setError(parseApiError(err.message, 'Failed to save part'));
    }
  }

  const handleRecalculateFromHistory = async () => {
    if (!dbPartId) {
      alert("Please save or select a part before recalculating from history.");
      return;
    }
    if (isDirty && !window.confirm("You have unsaved changes that will be overwritten. Continue?")) {
      return;
    }
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/parts/${dbPartId}/calculate_cost`);
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res, "Failed to recalculate"));
      }
      const data = await res.json();
      setPartData(prev => ({
        ...prev,
        cost_labor: data.calculated_labor_cost,
        estimated_gas_cost: data.calculated_gas_cost,
        estimated_consumable_cost: data.calculated_consumable_cost ?? prev?.estimated_consumable_cost ?? 0,
        last_calculation_log: data.log
      }));
      setIsDirty(true);
      setCostEngine({ engine: 'calculate_cost', at: new Date().toISOString() });
      const shuttle = describeShuttle(data);
      setCostShuttle(shuttle);
      // F08: the log renders as a table in the card, not in window.alert, and
      // the notice says where the hours came from instead of assuming "historical".
      const parsedLog = parseCalcLog(data.log);
      const srcWord = parsedLog.hoursSource === 'historical'
        ? 'job history'
        : (parsedLog.hoursSource === 'estimate' ? 'the estimate fields (no job history yet)' : 'job history where it exists, estimates elsewhere');
      const shuttleWord = !shuttle.available ? ''
        : (shuttle.historyIncludes ? ' (shuttle inside the historical Laser hours)'
          : (shuttle.cost ? `, of which Laser shuttle share ${fmtMoney(shuttle.cost)}` : ''));
      setCostNotice(
        `Labor recalculated to ${fmtMoney(data.calculated_labor_cost)} from ${srcWord}${shuttleWord} ` +
        `(gas ${fmtMoney(data.calculated_gas_cost, { precise: true })}, consumables ${fmtMoney(data.calculated_consumable_cost ?? 0, { precise: true })}). ` +
        `Press 'Save Changes' to store it — the step-by-step log is under "Calculation log" in the Pricing & Costing card.`
      );
      setCalcLogOpen(true);
    } catch (err) {
      const friendlyError = parseApiError(err.message, 'Recalculation failed');
      setError(friendlyError);
      alert(friendlyError);
    }
  };

  function safeParseFloat(strValue) {
    if (!strValue || (typeof strValue !== 'string' && typeof strValue !== 'number')) return 0;
    const parsed = parseFloat(strValue);
    return isNaN(parsed) ? 0 : parsed;
  }

  function parseTimeToMinutes(timeStr) {
    const timeValue = String(timeStr || "");
    if (!timeValue || timeValue.trim() === "") return 0;
    const parts = timeValue.split(':').map(part => parseFloat(part.trim())).filter(num => !isNaN(num));
    let totalMinutes = 0;
    if (parts.length === 1) totalMinutes = parts[0];
    else if (parts.length === 2) totalMinutes = parts[0] + (parts[1] / 60.0);
    else if (parts.length >= 3) totalMinutes = (parts[0] * 60) + parts[1] + (parts[2] / 60.0);
    else return 0;
    return totalMinutes >= 0 ? parseFloat(totalMinutes.toFixed(2)) : 0;
  }

  const handleDxfUpload = async (e) => {
    const file = e.target.files[0];
    // Clear the input up front: without this, re-picking the SAME file after a
    // failure fires no change event and the retry looks like a dead button.
    e.target.value = "";
    if (!file) return;
    const tooBig = checkUploadSize(file, "DXF");
    if (tooBig) { setError(tooBig); return; }
    const formData = new FormData();
    formData.append("file", file);
    try {
        let data;
        if (dbPartId) {
            // Existing part: use endpoint that saves to DB and uploads image to S3
            const res = await fetch(`${API_BASE}/parts/${dbPartId}/analyze_dxf`, { method: "POST", body: formData });
            if (!res.ok) throw new Error(await apiErrorMessage(res));
            data = await res.json();
            // Reset weight so the Sheet Stock auto-calc useEffect recomputes from the new area.
            setPartData(p => ({
                ...p,
                solid_profile_area: data.solid_profile_area ? data.solid_profile_area.toFixed(4) : "",
                size_length: data.size_length ? data.size_length.toFixed(4) : "",
                size_width: data.size_width ? data.size_width.toFixed(4) : "",
                pierce_count: (typeof data.pierce_count === 'number') ? data.pierce_count : p.pierce_count,
                description: (p.description && p.description.trim())
                    ? p.description
                    : (data.suggested_description || p.description),
                image_path: data.image_path || p.image_path,
                weight_lbs: 0,
                weight_grams: "",
            }));
            // Refresh the Part Files list — analyze_dxf writes an auto
            // "DXF Drawing" entry into part_documents so the operator
            // sees the uploaded .dxf alongside any manual uploads.
            loadPartDocuments(dbPartId);
        } else {
            // New part: use standalone endpoint that returns base64 image
            const res = await fetch(`${API_BASE}/dxf/analyze`, { method: "POST", body: formData });
            if (!res.ok) throw new Error(await apiErrorMessage(res));
            data = await res.json();
            // Stash the original DXF so it can be persisted via /parts/{id}/analyze_dxf
            // after the part is created (gives us S3 storage + part_documents row).
            setPendingDxfFile(file);
            setPartData(p => ({
                ...p,
                solid_profile_area: data.solid_profile_area ? data.solid_profile_area.toFixed(4) : "",
                size_length: data.size_length ? data.size_length.toFixed(4) : "",
                size_width: data.size_width ? data.size_width.toFixed(4) : "",
                pierce_count: (typeof data.pierce_count === 'number') ? data.pierce_count : p.pierce_count,
                description: (p.description && p.description.trim())
                    ? p.description
                    : (data.suggested_description || p.description),
                // Store base64 image temporarily - will be replaced with S3 URL after save
                image_base64: data.image_base64 || null,
                weight_lbs: 0,
                weight_grams: "",
            }));
        }
        setIsDirty(true);
        // Warnings: an outline that doesn't close, linework outside it, gaps closed.
        // An existing part's numbers were already saved by analyze_dxf.
        alert(dxfAnalyzedMessage(data, { saved: Boolean(dbPartId) }));
    } catch (error) {
        setError(parseApiError(error.message, 'Failed to analyze DXF', file));
    }
  };

  const handleCalculateLaserTimeFromDxf = async () => {
    if (!partData?.material_type) {
        alert("Set Material Type before calculating laser time.");
        return;
    }
    if (!partData?.thickness_id) {
        alert("Set Thickness before calculating laser time.");
        return;
    }
    const thicknessObj = thicknessOptions.find(t => t.thickness_id === parseInt(partData.thickness_id));
    const thicknessDisplay = thicknessObj?.display_thickness;
    if (!thicknessDisplay) {
        alert("Could not resolve thickness display name. Try saving the part and reloading.");
        return;
    }
    try {
        setIsCalculatingLaserTime(true);
        let resp;
        if (dbPartId && partData?.dxf_path) {
            const fd = new FormData();
            fd.append('material_type', partData.material_type);
            fd.append('thickness_display', thicknessDisplay);
            resp = await fetch(`${API_BASE}/parts/${dbPartId}/calculate-laser-time`, {
                method: 'POST',
                body: fd,
            });
        } else if (pendingDxfFile) {
            const fd = new FormData();
            fd.append('file', pendingDxfFile);
            fd.append('material_type', partData.material_type);
            fd.append('thickness_display', thicknessDisplay);
            resp = await fetch(`${API_BASE}/parts/calculate-laser-time-from-dxf`, {
                method: 'POST',
                body: fd,
            });
        } else {
            alert("Upload a DXF for this part first, then click Calculate from DXF.");
            return;
        }
        if (resp.status === 400) {
            // Surfaces actionable messages like "No laser tech spec exists for X
            // / Y. An operator must author a laser_tech_specs row before...".
            let detail = '';
            try {
                const body = await resp.json();
                detail = body.detail || '';
            } catch {
                detail = await resp.text();
            }
            alert(detail || "Calculate from DXF failed — see laser tech spec configuration.");
            return;
        }
        if (!resp.ok) {
            throw new Error(await apiErrorMessage(resp));
        }
        const data = await resp.json();
        // Laser Task Time gets the SCHEDULING estimate: per-part process time
        // (cut path + pierce dwell + pierce machine overhead + within-part
        // move) × the calibrated wall factor. Calibrated against 12 months of
        // Boyd/Faith task wall clocks (2026-08): wall ≈ 1.05 × process, with
        // the per-sheet 160 s handling share living at the cut-sheet level.
        // Fallback chain keeps the button working against an older nester.
        const schedulingSec = Math.round(
            data.scheduling_seconds
            ?? (typeof data.scheduling_minutes === 'number' ? data.scheduling_minutes * 60 : null)
            ?? (data.process_time_seconds ?? data.beam_on_seconds ?? 0) * 1.05
        );
        const procMm = Math.floor(schedulingSec / 60);
        const procSs = schedulingSec % 60;
        const processFormatted = `${procMm}:${String(procSs).padStart(2, '0')}`;
        handlePartDataChange('initial_est_laser_minutes', processFormatted);
        // beam_on_minutes feeds gas + consumable cost (laser actually on).
        // Save in the same gesture so both numbers stay in sync.
        if (typeof data.beam_on_minutes === 'number') {
            handlePartDataChange('beam_on_minutes', data.beam_on_minutes);
        }
        setIsDirty(true);

        const sourceLabels = {
            'laser_tech_specs': "shop laser tech specs for this material+thickness",
            'override': 'request overrides',
        };
        const srcLabel = sourceLabels[data.params_source] || data.params_source;

        const fmtSec = (s) => (typeof s === 'number' ? s.toFixed(2) : '?');
        const processMin = (data.process_time_minutes ?? data.beam_on_minutes ?? 0).toFixed(3);
        const beamOnMin = (data.beam_on_minutes || 0).toFixed(3);
        const cutPath = fmtSec(data.cut_path_seconds);
        const pierceDwell = fmtSec(data.pierce_dwell_seconds);
        const pierceOverhead = fmtSec(data.pierce_overhead_seconds);
        const moveSec = fmtSec(data.move_within_part_seconds);
        const moveIn = data.move_within_part_inches != null
            ? `${data.move_within_part_inches.toFixed(1)} in`
            : '?';

        alert(
            `Scheduling estimate: ${processFormatted} (process ${processMin} min × 1.05 wall factor) — written to Laser Task Time\n` +
            `Beam-on only: ${beamOnMin} min — written to Beam-On Minutes (gas/consumable cost driver)\n\n` +
            `Breakdown:\n` +
            `  Cut path:        ${cutPath}s\n` +
            `  Pierce dwell:    ${pierceDwell}s  (${data.pierce_count} × ${data.pierce_time_sec_used}s)\n` +
            `  Pierce overhead: ${pierceOverhead}s  (${data.pierce_count} × 0.5s machine cycle)\n` +
            `  Within-part move: ${moveSec}s  (${moveIn} at rapid traverse)\n\n` +
            `Source: ${srcLabel}\n` +
            `Params used: feedrate ${data.feedrate_ipm_used} ipm, pierce ${data.pierce_time_sec_used}s\n\n` +
            `Note: the wall factor covers within-task overhead; the per-sheet ` +
            `handling share (~160 s: swap, load, align) is added at the cut-sheet ` +
            `level, and full sheet overhead detail requires a nest run. Once ` +
            `historical cut data exists for this part, scheduling prefers the ` +
            `historical average over this estimate.`
        );
    } catch (err) {
        setError(parseApiError(err.message || String(err), 'Failed to calculate laser time'));
    } finally {
        setIsCalculatingLaserTime(false);
    }
  };

  const calculateMaterialPrice = async () => {
      if (!dbPartId) return;
      try {
          const res = await fetch(`${API_BASE}/parts/${dbPartId}/material_price`);
          if (!res.ok) throw new Error('Failed to fetch material prices');
          const data = await res.json();
          setMaterialPrices(data); 
          // Auto-update the partData cost if a method is already selected
          if (partData.material_price_method) {
              let newCost = 0;
              if (partData.material_price_method === 'weight') newCost = data.price_by_weight;
              else if (partData.material_price_method === 'bounding_area') newCost = data.price_by_bounding_area;
              else if (partData.material_price_method === 'solid_profile') newCost = data.price_by_solid_profile;
              else if (partData.material_price_method === 'cut_length') newCost = data.price_by_cut_length; // Specific fix for Shape Stock
              
              if (newCost > 0) {
                  setPartData(prev => ({ ...prev, material_cost_component: newCost }));
              }
          }

          alert("Material prices have been calculated. Please select a pricing method below.");
      } catch (error) {
          console.error("Failed to calculate material price:", error);
          setError(parseApiError(error.message, 'Failed to calculate material price'));
      }
  };
  
  async function handleToggleFlag(e) {
    // features/parts/partData (the phone's flag too): the questions, PATCH, the alerts. A cancelled or
    // refused flag leaves the switch where it was (controlled component).
    const r = await setPartFlag({ partId: dbPartId, on: e.target.checked, readOnly: isReadOnly, ui: desktopUi, actor: currentActor() });
    if (!r.done) return;
    setPartData(p => ({ ...p, ...r.payload }));
    loadParts();
  }

  // --- DOCUMENT HANDLING ---
  async function handleUploadDocument() {
    if (!dbPartId) return alert("Save the part first.");
    if (!docUploadFile) return alert("Please select a file.");
    if (!docDisplayName.trim()) return alert("Please enter a file name (e.g., 'CAD Drawing').");

    const tooBig = checkUploadSize(docUploadFile, "file");
    if (tooBig) return alert(tooBig);

    const pin = await pinPrompt("Enter PIN to upload document:");
    if (!pin) return;

    setIsUploadingDoc(true);
    const formData = new FormData();
    formData.append("file", docUploadFile);
    formData.append("display_name", docDisplayName);
    formData.append("description", docDescription);
    formData.append("pin", pin);

    try {
      const res = await fetch(`${API_BASE}/parts/${dbPartId}/documents`, {
        method: "POST",
        body: formData
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));

      alert("File uploaded successfully!");
      setDocUploadFile(null);
      setDocDisplayName("");
      setDocDescription("");
      loadPartDocuments(dbPartId); // Refresh list
    } catch (err) {
      alert(parseApiError(err.message, 'Upload failed', docUploadFile));
    } finally {
      setIsUploadingDoc(false);
    }
  }

  async function handleDeleteDocument(docId) {
    if (!window.confirm("Are you sure you want to delete this file? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API_BASE}/parts/${dbPartId}/documents/${docId}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      loadPartDocuments(dbPartId);
    } catch (err) {
      alert(parseApiError(err.message, 'Delete failed'));
    }
  }

  async function checkAndSetFile(file) {
    const problem = await partImageProblem(file); // partData: the 3000 x 3000 limit
    if (problem) { alert(problem); return; }
    setUploadFile(file);
  }

  function checkAndSetPpapFile(file) {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl); // Clean up to prevent memory leak
      if (img.width > 3000 || img.height > 3000) {
        alert("PPAP image is larger than 3000x3000. Please resize or pick a smaller screenshot.");
        return;
      }
      setPpapUploadFile(file);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl); // Clean up on error too
      alert("Failed to load PPAP image. Please select a valid image file.");
    };
    img.src = objectUrl;
  }

  function handlePaste(e) { 
    if (e.clipboardData?.files?.length) { 
        checkAndSetFile(e.clipboardData.files[0]); 
        e.preventDefault(); 
    } 
  }
  
  function handleFileChange(e) { 
    if (e.target.files?.length) checkAndSetFile(e.target.files[0]); 
    else setUploadFile(null); 
  }
  
  function handlePpapFileChange(e) {
    if (e.target.files?.length) checkAndSetPpapFile(e.target.files[0]);
    else setPpapUploadFile(null);
  }

  async function handleUploadImage() {
    const r = await uploadPartImage({ partId: dbPartId, file: uploadFile, ui: desktopUi }); // partData
    if (r.error) { setError(parseApiError(r.error, 'Failed to upload image')); return; }
    if (!r.done) return;
    handlePartDataChange('image_path', r.imageUrl);
    setUploadFile(null);
  }

  async function handleUploadPpapImage() {
    if (!dbPartId) { alert("Cannot upload image until part is saved/created."); return; }
    if (!ppapUploadFile) { alert("No file selected for PPAP image."); return; }
    try {
        const formData = new FormData();
        formData.append("file", ppapUploadFile);
        const res = await fetch(`${API_BASE}/parts/${dbPartId}/upload-image`, { method: "POST", body: formData });
        if (!res.ok) throw new Error(await apiErrorMessage(res));
        const data = await res.json();
        handlePartDataChange('ppap_image_path', data.image_url);
        alert("PPAP Image (Balloon Print) uploaded!");
        setPpapUploadFile(null);
    } catch (err) {
        setError(parseApiError(err.message, 'Failed to upload PPAP image'));
    }
  }

  function handleEnlarge(url) { setEnlargedImage(url); }
  function closeEnlarge() { setEnlargedImage(null); }

  const handlePrintLabel = async () => {
    if (!dbPartId) { alert("Please select a part to print a label for."); return; }
    try {
        const response = await fetch(`${API_BASE}/parts/${dbPartId}/label`);
        if (!response.ok) { const errorText = await response.text(); throw new Error(`Failed to generate label: ${errorText}`); }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
        console.error("Error printing label:", err);
        setError(parseApiError(err.message, 'Could not print label'));
    }
  };

  function PricingDebugInfo({ info, selectedMethod }) {
  if (!info) return null;

  // Handle Error State
  if (info.error) {
    return (
      <div className="alert alert-warning mt-2 mb-2 py-2 small">
        <i className="bi bi-exclamation-triangle me-2"></i>
        <strong>Material Cost Logic:</strong> {info.error}
      </div>
    );
  }

  // Handle Sheet Stock (CWT)
  if (info.method === "CWT (Sheet)") {
    let breakdown = null;
    let calcWeight = info.base_weight;

    // Show different logic based on what the user SELECTED
    if (selectedMethod === 'bounding_area') {
        calcWeight = info.calc_bounding_weight;
        breakdown = (
            <div className="mt-2 pt-2 border-top border-info-subtle">
                <strong><i className="bi bi-bounding-box me-1"></i> Bounding Area Math:</strong>
                <div className="ps-3 text-muted">
                    Dims: {info.bounding_dims}" × {info.thickness_in}"<br/>
                    Density: {info.density} lbs/in³<br/>
                    Calc Weight: <strong>{info.calc_bounding_weight?.toFixed(3)} lbs</strong>
                </div>
            </div>
        );
    } else if (selectedMethod === 'solid_profile') {
        calcWeight = info.calc_solid_weight;
        breakdown = (
            <div className="mt-2 pt-2 border-top border-info-subtle">
                <strong><i className="bi bi-pentagon me-1"></i> Solid Profile Math:</strong>
                <div className="ps-3 text-muted">
                    Area: {info.solid_area?.toFixed(4)} sq in<br/>
                    Thickness: {info.thickness_in}"<br/>
                    Density: {info.density} lbs/in³<br/>
                    Calc Weight: <strong>{info.calc_solid_weight?.toFixed(3)} lbs</strong>
                </div>
            </div>
        );
    }

    return (
      <div className="alert alert-info mt-2 mb-2 py-2 small bg-light border-info">
        <div className="d-flex justify-content-between">
            <div>
                <strong><i className="bi bi-calculator me-1"></i> {info.vendor} Rule:</strong>
            </div>
            <span className="badge bg-info text-dark">${info.cwt_price?.toFixed(2)}/cwt</span>
        </div>
        
        <div className="mt-1 mb-1">
           Rate: <strong>${info.price_per_lb?.toFixed(4)} / lb</strong>
        </div>
        
        {/* If default weight method, just show the simple calc */}
        {selectedMethod === 'weight' && (
            <div className="text-muted">
               Manual Weight: {info.base_weight?.toFixed(3)} lbs * ${info.price_per_lb?.toFixed(4)}
            </div>
        )}

        {/* If advanced method, show the breakdown */}
        {breakdown}
      </div>
    );
  }

  // Handle Shape Stock (Linear)
  if (info.method === "Linear (Shape)") {
    return (
      <div className="alert alert-info mt-2 mb-2 py-2 small bg-light border-info">
        <div className="d-flex justify-content-between">
            <strong><i className="bi bi-ruler me-1"></i> {info.vendor} Pricing:</strong>
        </div>
        <div className="mt-1 text-muted">
           Stick Cost: <strong>${info.stick_cost?.toFixed(2)}</strong> for {info.stock_length}"
           <br/>
           Cost/Inch: <strong>${info.cost_per_inch?.toFixed(4)}</strong>
           <br/>
           Math: {info.formula}
        </div>
      </div>
    );
  }

  return null;
}
  
  // PPAP - Export Control Plan
  const handleExportControlPlan = () => {
    if (!dbPartId) return;
    // Assumes a backend endpoint exists to generate the PDF
    openPdf(`${API_BASE}/parts/${dbPartId}/control_plan`);
  };

  // PAP - Export PSW (Part Submission Warrant)
  const handleExportPSW = () => {
    if (!dbPartId) return;
    openPdf(`${API_BASE}/reports/ppap/psw/${dbPartId}`);
  };

  // PAP - Export PFMEA 
  const handleGeneratePFMEA = async () => {
      if (!dbPartId) return;
      // Open the PDF in a new tab
      openPdf(`${API_BASE}/parts/${dbPartId}/pfmea_document`);
  };

  const chartProcesses = [...new Set(["Laser", ...selectedProcesses])];

  // --- Cost provenance derivations (F06 / F07 / F08 / F16 / F32) ---
  // Everything the Pricing & Costing card prints beside a number is derived
  // here from storedSnap (GET /parts/{id}), partData (live figures) and the
  // parsed engine log. Plain derivations, no hooks, so they never reorder.
  const hasStored = !!(storedSnap && storedSnap.part_id);
  const storedAsOfText = asOf(storedSnap?.cost_updated_at) || 'as-of date n/a';
  const storedAsOfLabel = storedLabel(storedSnap?.cost_updated_at);
  const calcLog = parseCalcLog(partData?.last_calculation_log);
  const materialLive = describeMaterialLive(storedSnap?.material_live);
  const methodUnchanged = hasStored && partData?.material_price_method === storedSnap?.material_price_method;
  const liveMaterialFromEngine = materialLive.available && methodUnchanged && materialLive.value !== null;
  const materialLiveValue = liveMaterialFromEngine ? materialLive.value : safeParseFloat(partData?.material_cost_component);
  const materialFresh = freshness(storedSnap?.material_cost_component, materialLiveValue);
  const totalFresh = freshness(storedSnap?.cost_labor_material, liveRollup.cost_labor_material);
  const opsOwnedIndex = opsOwnedStepsIndex(assembliesForPart);
  const coatingOwnedBy = coatingOwners(assembliesForPart);
  const opsOwnedOnRouting = selectedProcesses.filter(s => opsOwnersForStep(opsOwnedIndex, s).length > 0);
  const quoteOwners = (() => {
    const seen = new Map();
    coatingOwnedBy.forEach(a => seen.set(a.assembly_id, a));
    opsOwnedOnRouting.forEach(s => opsOwnersForStep(opsOwnedIndex, s).forEach(a => seen.set(a.assembly_id, a)));
    return [...seen.values()];
  })();
  const asQuoted = asQuotedInsideAssembly({
    liveTotal: liveRollup.cost_labor_material, coating: partData?.coating_cost_per_unit,
    coatingOwned: coatingOwnedBy.length > 0, logRows: calcLog.rows, opsOwnedIndex, processSteps: selectedProcesses,
  });
  const engineLabel = costEngine.engine === 'dynamic' ? 'live · estimate engine (unsaved edit)'
    : costEngine.engine === 'calculate_cost' ? 'live · history-aware engine'
      : costEngine.engine === 'stored_fallback' ? 'stored (engine unavailable)' : 'not computed';
  const engineTagKind = costEngine.engine === 'dynamic' ? 'estimate' : (calcLog.hoursSource === 'historical' ? 'historical' : (calcLog.hoursSource === 'estimate' ? 'estimate' : 'default'));
  const gasRow = calcLog.rows.find(r => r.kind === 'gas');
  const consRow = calcLog.rows.find(r => r.kind === 'consumables');
  // 'manual' semantics (server contract 2026-09-02): the typed manual_material_price;
  // $0 / blank = customer supplied. Follow material_live.source.kind when the API
  // resolved this very column value; otherwise the typed price on the page; on an
  // older API (no material_live) keep the legacy mapping / label.
  const manualTyped = safeParseFloat(partData?.manual_material_price) || 0;
  const manualDescribedByApi = materialLive.available
    && ['manual', 'manual_price'].includes(storedSnap?.material_price_method)
    && manualTyped === (safeParseFloat(storedSnap?.manual_material_price) || 0);
  const methodKind = (materialLive.available && methodUnchanged && materialLive.kind && materialLive.kind !== 'none')
    ? materialLive.kind
    : materialMethodKind(partData?.material_price_method, materialLive.available ? { manualPrice: manualTyped } : {});
  const manualLabel = manualMethodLabel({
    materialLive, describesThisMethod: manualDescribedByApi, manualPrice: manualTyped,
    legacyLabel: stockType === 'Shape Stock' ? 'Manual / Customer Supplied' : 'Customer Supplied / No Cost',
  });
  const materialSourceText = liveMaterialFromEngine
    ? `${materialLive.description || `engine: ${materialLive.kind || methodKind}`} (live via engine)`
    : (materialLive.available && !methodUnchanged)
      ? `method changed on this page (${methodKind}) — the engine value ${fmtMoney(materialLive.value, { dash: true })} was for the stored method; Save to re-resolve`
      : `${methodKind} — value selected on this page; live engine value n/a (API predates provenance)`;
  const ownersText = (owners) => owners.map(o => `#${o.assembly_id} ${o.assembly_name || ''}`.trim()).join(', ');
  const costFormulaText = formulaString(costFormulaTerms(liveRollup), liveRollup.cost_labor_material);
  const priceFormulaText = formulaString(priceFormulaTerms(liveRollup), liveRollup.price_labor_material);
  // Wave 2: the coating mirror sentence (decision 3) and the shuttle note on the Labor row (decision 4).
  const coatingMirror = describeCoatingMirror({
    steps: outsourcingRows.steps, prices: outsourcingRows.prices, loaded: outsourcingRows.loaded, partSaved: !!dbPartId,
  });
  const shuttleNoteForMath = !costShuttle.available ? ''
    : (costShuttle.historyIncludes ? ' · Laser shuttle: history includes it'
      : (costShuttle.cost ? ` · includes Laser shuttle share ${fmtMoney(costShuttle.cost)}` : ''));
  const mathRows = [
    { key: 'labor', item: 'Labor', basis: 'COST / unit', stored: storedSnap?.cost_labor, live: partData?.cost_labor,
      source: <><SourceTag kind={engineTagKind}>{engineLabel}</SourceTag>{calcLog.hoursSource && costEngine.engine !== 'dynamic' ? <> hours: <SourceTag kind={calcLog.hoursSource} /></> : null}</>,
      note: `Σ hours × true-cost $/hr per step (wages, 90-day punch-weighted; see Calculation log)${shuttleNoteForMath}${opsOwnedOnRouting.length ? ` — ${opsOwnedOnRouting.join(', ')} priced on ${ownersText(quoteOwners)} operations line` : ''}` },
    { key: 'gas', item: 'Gas', basis: 'COST / unit', precise: true, stored: storedSnap?.estimated_gas_cost, live: partData?.estimated_gas_cost,
      source: gasRow ? gasRow.raw : 'assist gas $/hr × beam-on hrs (engine line not in log)' },
    { key: 'cons', item: 'Consumables', basis: 'COST / unit', precise: true, stored: storedSnap?.estimated_consumable_cost, live: partData?.estimated_consumable_cost,
      source: consRow ? consRow.raw : 'nozzle + lens $/hr × beam-on hrs, at cost (engine line not in log)' },
    { key: 'material', item: 'Material', basis: 'COST / unit', precise: true, stored: storedSnap?.material_cost_component, live: materialLiveValue,
      source: materialSourceText,
      note: liveMaterialFromEngine && materialFresh.stale ? 'stored material is stale (its cut-sheet / cut-list / vendor row moved since the last save) — Save to refresh' : null },
    { key: 'coating', item: 'Coating', basis: 'COST / unit (outsourced)', stored: storedSnap?.coating_cost_per_unit, live: partData?.coating_cost_per_unit,
      struck: coatingOwnedBy.length > 0,
      source: coatingOwnedBy.length > 0
        ? <SourceTag kind="assembly_row">priced on {ownersText(coatingOwnedBy)} operations line — quotes exclude it from this part</SourceTag>
        : 'typed here / coater PO $/unit; marked up with material on the price side' },
    { key: 'total', item: 'Total cost', basis: 'COST / unit', emphasis: true, stored: storedSnap?.cost_labor_material, live: liveRollup.cost_labor_material,
      source: costFormulaText, note: COST_FORMULA },
    { key: 'price', item: 'Cost + material markup', basis: 'internal estimate — not a quote price', emphasis: true,
      stored: storedSnap?.price_labor_material, live: liveRollup.price_labor_material,
      source: priceFormulaText, note: `${PRICE_FORMULA} · markup ${liveRollup.terms.markup_percent}% (parts.markup_percent; unrelated to the quote's material markup)` },
  ];

  // Provenance line under the SELECTED material-method radio (F16 / F32).
  const renderMethodProvenance = (methodValue, row, rowKind) => {
    if (partData?.material_price_method !== methodValue) {
      // Not selected: show whatever allocation maths the row itself carries (stock cost ÷ pcs,
      // utilisation) so a low-utilisation allocation is visible BEFORE it is chosen.
      const info = row ? describeAllocationRow(row, rowKind) : null;
      if (!info || !info.text) return null;
      return (
        <div className="small text-muted ms-4">
          {info.text}
          {info.low ? <> <SourceTag kind="stale">low utilisation</SourceTag></> : null}
        </div>
      );
    }
    const rowInfo = row ? describeAllocationRow(row, rowKind) : { text: '', utilisationPct: null, low: false };
    if (liveMaterialFromEngine) {
      return (
        <div className="small text-muted ms-4">
          <i className="bi bi-info-circle me-1"></i>
          {materialLive.description || `engine: ${materialLive.kind || methodKind}`} — live {fmtMoney(materialLive.value, { precise: true })}
          {materialLive.utilisationPct !== null && !(materialLive.description || '').includes('%') ? <> · {describeUtilisation(materialLive.utilisationPct)}</> : null}
          {rowInfo.text ? <> · {rowInfo.text}</> : null}
          {(materialLive.low || rowInfo.low) ? <> <SourceTag kind="stale">low utilisation — a whole sheet/stick spread over few parts</SourceTag></> : null}
          {materialFresh.stale ? <> <SourceTag kind="stale">stored {fmtMoney(storedSnap?.material_cost_component, { precise: true })} is stale — Save to refresh</SourceTag></> : null}
          {materialLive.rowStale ? <> <SourceTag kind="stale" title="The cut sheet / cut list row's own stored value no longer matches the live sheet / stick maths — recalculate that sheet / list, then re-select and Save">row itself stale vs current stock price</SourceTag></> : null}
        </div>
      );
    }
    const isManualKind = methodValue === 'manual' || methodValue === 'manual_price';
    return (
      <div className="small text-muted ms-4">
        <i className="bi bi-info-circle me-1"></i>
        {rowInfo.text ? <>{rowInfo.text} · </> : null}
        {isManualKind && materialLive.available
          ? (manualTyped > 0
            ? `typed manual price ${fmtMoney(manualTyped)} (manual_material_price) — sent as Cost - Material on Save`
            : 'no typed price — customer supplied / $0 material')
          : (materialLive.available && !methodUnchanged
            ? 'method changed on this page — the engine re-resolves the source after Save'
            : 'selected — source description / utilisation n/a (API predates provenance)')}
        {rowInfo.low ? <> <SourceTag kind="stale">low utilisation</SourceTag></> : null}
      </div>
    );
  };

  // The typed manual_material_price box — shown under whichever manual radio is selected.
  const renderManualPriceInput = () => (
    <div className="mt-2" style={{ maxWidth: '150px' }}>
      <label htmlFor="manual_material_price_input" className="form-label small">Enter Price ($)</label>
      <input type="number" step="0.01" id="manual_material_price_input" className="form-control form-control-sm" value={partData?.manual_material_price || ""} disabled={isReadOnly} onChange={(e) => handlePartDataChange('manual_material_price', e.target.value)} />
    </div>
  );

  // --- PPAP Table Handlers ---
  const handleAddCharacteristic = () => {
    const newChar = {
        id: Date.now(), 
        balloon: ppapCriticalCharacteristics.length + 1,
        name: '',
        nominal: 0,
        tolerance: '±0.005',
        units: 'in',
        required_gauge: 'Calipers'
    };
    const newChars = [...ppapCriticalCharacteristics, newChar];
    handlePartDataChange('ppap_critical_characteristics', newChars);
    setEditingCharacteristicId(newChar.id);
  };

  const handleDeleteCharacteristic = (idToDelete) => {
      const newChars = ppapCriticalCharacteristics.filter(char => char.id !== idToDelete);
      const renumberedChars = newChars.map((char, index) => ({ ...char, balloon: index + 1 }));
      handlePartDataChange('ppap_critical_characteristics', renumberedChars);
  };

  const handleCharacteristicChange = (id, field, value) => {
      const newChars = ppapCriticalCharacteristics.map(char => {
          if (char.id === id) {
              let finalValue = value;
              if (field === 'nominal') {
                  const parsed = safeParseFloat(value);
                  finalValue = parsed;
              }
              return { ...char, [field]: finalValue };
          }
          return char;
      });
      handlePartDataChange('ppap_critical_characteristics', newChars);
  };

  const getFileTypeBadge = (filename) => {
    if (!filename) return { label: '???', color: 'bg-secondary' };
    
    const ext = filename.split('.').pop().toLowerCase();
    
    switch (ext) {
      case 'pdf': return { label: 'PDF', color: 'bg-danger' };
      case 'dxf': 
      case 'dwg': return { label: ext.toUpperCase(), color: 'bg-dark' };
      case 'step':
      case 'stp': return { label: 'STEP', color: 'bg-info text-dark' };
      case 'png':
      case 'jpg':
      case 'jpeg': return { label: 'IMG', color: 'bg-success' };
      case 'xls':
      case 'xlsx':
      case 'csv': return { label: 'EXCEL', color: 'bg-success' };
      case 'doc':
      case 'docx': return { label: 'DOC', color: 'bg-primary' };
      default: return { label: ext.toUpperCase().substring(0, 4), color: 'bg-secondary' };
    }
  };


  return (
    <div className="d-flex" style={{ height: "100vh" }}>
      {/* Left Panel: Part List */}
      <div className="border-end p-3" style={{ width: "400px", overflowY: "auto", flexShrink: 0 }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Parts</h5>
            <div className="btn-group">
                <button className="btn btn-sm btn-outline-primary" onClick={handleShowSingleNew}>New</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={handleShowBulkCreate}>Bulk Create</button>
            </div>
        </div>
        <div className="d-flex gap-3 mb-2">
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="showFlaggedOnly" checked={showFlaggedOnly} onChange={(e) => setShowFlaggedOnly(e.target.checked)} />
            <label className="form-check-label" htmlFor="showFlaggedOnly">Flagged</label>
          </div>
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="showInactive" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            <label className="form-check-label" htmlFor="showInactive">Inactive</label>
          </div>
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="showTemporary" checked={showTemporary} onChange={(e) => setShowTemporary(e.target.checked)} />
            <label className="form-check-label" htmlFor="showTemporary">Temporary</label>
          </div>
        </div>

        {/* NEW: Sort Dropdown */}
        <div className="mb-2">
            <label className="form-label">Sort By</label>
            <select className="form-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="name_asc">SKU (A-Z)</option>
                <option value="name_desc">SKU (Z-A)</option>
                <option value="date_newest">Newest Created</option>
                <option value="date_oldest">Oldest Created</option>
            </select>
        </div>

        <div className="mb-2">
            <label className="form-label">Filter by Stock Type</label>
            <select className="form-select" value={filterStockType} onChange={(e) => setFilterStockType(e.target.value)}>
                <option value="">All</option>
                <option value="Sheet Stock">Sheet Stock</option>
                <option value="Shape Stock">Shape Stock</option>
            </select>
        </div>
        <div className="mb-2">
            <label className="form-label small fw-bold">Material Specs</label>
            <select className="form-select form-select-sm mb-1" value={filterMaterialType} onChange={(e) => setFilterMaterialType(e.target.value)}>
                <option value="">All Types</option>
                {cwtOptions.material_types.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            
            <select className="form-select form-select-sm mb-1" value={filterSurfaceFinish} onChange={(e) => setFilterSurfaceFinish(e.target.value)}>
                <option value="">All Finishes</option>
                {cwtOptions.surface_finishes.map(f => <option key={f} value={f}>{f}</option>)}
            </select>

            <select className="form-select form-select-sm" value={filterThickness} onChange={(e) => setFilterThickness(e.target.value)}>
                <option value="">All Thicknesses</option>
                {/* UPDATED: Map over availableThicknessFilters instead of thicknessOptions */}
                {availableThicknessFilters.map(t => (
                    <option key={t.thickness_id} value={t.thickness_id}>
                        {t.display_thickness} 
                        {/* Only show material type label if we haven't already filtered by it */}
                        {!filterMaterialType && ` (${t.material_type})`}
                    </option>
                ))}
            </select>
        </div>
        <div className="mb-2">
          <label className="form-label">Filter by Customer</label>
          <select className="form-select" value={filterCustomerId} onChange={(e) => setFilterCustomerId(e.target.value)}>
            <option value="">All</option>
            {customers.map((cust) => <option key={cust.customer_id} value={cust.customer_id}>{cust.name}</option>)}
          </select>
        </div>
        <input type="text" className="form-control mb-2" placeholder="Search by SKU or description..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        {loading && <div>Loading...</div>}
        {!loading && filteredAndSortedParts.map((p) => (
          <div key={p.part_id} className={`p-2 border-bottom ${selectedPart?.part_id === p.part_id ? "bg-primary-subtle" : "bg-white"}`} style={{ cursor: "pointer" }} onClick={() => handleSelectPart(p)}>
            {p.active_status === false && <div className="text-secondary small fw-bold">[Inactive]</div>}
            <div className="fw-bold">
              {p.flagged && <span style={{ color: "red", marginRight: "4px" }}>🚩</span>}
              SKU: {p.product_id}
            </div>
            <div className="text-muted" style={{ fontSize: "0.9rem" }}>{p.description}</div>
          </div>
        ))}
      </div>
      
      {/* Right Panel: Main Content */}
      <div className="flex-grow-1 p-4 overflow-auto" onPaste={handlePaste}>
        {error && <div className="alert alert-danger">{error}</div>}

        {/* NEW: Conditional Rendering Logic */}
        {view === 'single' && partData && (
          <div>
            <h4>{dbPartId ? `Editing Part #${dbPartId}` : "Create New Part"}</h4>
            <div className="row">
              <div className="col-lg-8">
                <div className="card mb-4" id="part-details-card">
                  {/* NEW HEADER WITH VERSION CONTROLS */}
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <h5 className="mb-0 me-2">Part Details</h5>
                        {partData?.revision_code && (
                            <span className="badge bg-secondary me-2">Rev {partData.revision_code}</span>
                        )}
                        {isReadOnly && (
                            <span className="badge bg-warning text-dark">ARCHIVED / READ ONLY</span>
                        )}
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        {/* History Dropdown with SAFETY CHECK */}
                        {Array.isArray(revisionHistory) && revisionHistory.length > 1 && (
                            <div className="input-group input-group-sm" style={{width: "auto"}}>
                                <label className="input-group-text">History</label>
                                <select 
                                    className="form-select"
                                    value={partData?.part_id || ''}
                                    onChange={(e) => {
                                        const pid = parseInt(e.target.value);
                                        handleSelectPart({ 
                                            part_id: pid, 
                                            product_id: partData.product_id, 
                                            description: "Loading..." 
                                        });
                                    }}
                                >
                                    {revisionHistory.map(rev => (
                                        <option key={rev.part_id} value={rev.part_id}>
                                            {rev.revision_code} {rev.is_current_revision ? '(Current)' : `(${formatDateSafe(rev.created_at)})`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        
                        {dbPartId && !isReadOnly && (
                            <button className="btn btn-sm btn-outline-primary" onClick={handleRevisionClick}>
                                <i className="bi bi-git"></i> New Version
                            </button>
                        )}
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="row mb-3">
                      <div className="col-md-2"><label className="form-label">DB Part ID</label><input type="text" className="form-control" value={dbPartId || ""} readOnly /></div>
                      <div className="col-md-3">
                        <label className={`form-label${!productId?.trim() ? ' required-field-label' : ''}`}>Product ID (SKU)</label>
                        <input
                            type="text"
                            className={`form-control${!productId?.trim() ? ' required-field' : ''}`}
                            value={productId}
                            // Lock if ReadOnly (Archived) OR if it's an existing part that has moved beyond Rev A
                            disabled={isReadOnly || (!!dbPartId && partData?.revision_code !== 'A')}
                            onChange={(e) => handlePartDataChange('product_id', e.target.value)}
                            required
                        />
                      </div>
                      <div className="col-md-4"><label className={`form-label${stockType === 'Sheet Stock' && !description?.trim() ? ' required-field-label' : ''}`}>Description</label><input type="text" className={`form-control${stockType === 'Sheet Stock' && !description?.trim() ? ' required-field' : ''}`} value={description} disabled={isReadOnly} onChange={(e) => handlePartDataChange('description', e.target.value)} /></div>
                      <div className="col-md-3">
                        <label className="form-label">Stock Type</label>
                        <select className="form-select" value={stockType} disabled={isReadOnly} onChange={(e) => handlePartDataChange('stock_type', e.target.value)}>
                            <option value="Sheet Stock">Sheet Stock</option>
                            <option value="Shape Stock">Shape Stock</option>
                        </select>
                      </div>
                    </div>
                    <div className="row mb-3">
                      <div className="col-md-4">
                          <label className={`form-label${stockType === 'Sheet Stock' && !customerId ? ' required-field-label' : ''}`}>Customer</label>
                          <select className={`form-select${stockType === 'Sheet Stock' && !customerId ? ' required-field' : ''}`} value={customerId} disabled={isReadOnly} onChange={(e) => handlePartDataChange('customer_id', e.target.value)}>
                            <option value="">-- None --</option>
                            {customers.map((cust) => <option key={cust.customer_id} value={cust.customer_id}>{cust.name}</option>)}
                          </select>
                      </div>
                      
                      <div className="col-md-3">
                          <label className="form-label">Customer Drawing ID</label>
                          <input 
                            type="text" 
                            className="form-control" 
                            value={partData.customer_drawing_id || ""} 
                            disabled={isReadOnly} 
                            onChange={(e) => handlePartDataChange('customer_drawing_id', e.target.value)} 
                          />
                      </div>

                      <div className="col-md-5">
                          <label className="form-label">Flag for Review</label>
                          <div className="d-flex align-items-center">
                              <div className="form-check form-switch mt-1">
                                <input className="form-check-input" type="checkbox" checked={flagged} disabled={isReadOnly} onChange={handleToggleFlag} />
                                <label className="form-check-label">{flagged ? "Yes" : "No"}</label>
                              </div>
                          </div>
                          {flagged && flagReason && (
                            <div className="alert alert-warning mt-2 p-2" style={{ whiteSpace: "pre-line" }}>
                              <strong>Flag Reason:</strong> {flagReason}
                              {flaggedByName && <div className="small"><em>Flagged By: {flaggedByName}</em></div>}
                              {flaggedAt && <div className="small"><em>Flagged At: {new Date(flaggedAt).toLocaleString()}</em></div>}
                            </div>
                          )}
                      </div>
                    </div>

                    {/* Active Status Toggle */}
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label">Active Status</label>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={partData.active_status !== false}
                            disabled={isReadOnly}
                            onChange={(e) => handlePartDataChange('active_status', e.target.checked)}
                          />
                          <label className="form-check-label">
                            {partData.active_status !== false ? "Active" : "Inactive"}
                          </label>
                        </div>
                        <small className="text-muted">Inactive parts are hidden from the list by default</small>
                      </div>
                    </div>

                    <hr />
                    <div className="row mb-3">
                        <div className="col-md-12">
                            <label className="form-label">Notes</label>
                            <textarea className="form-control" value={notes} disabled={isReadOnly} onChange={(e) => handlePartDataChange('notes', e.target.value)} style={{height: '100px'}}/>
                        </div>
                    </div>

                    {componentConsumables.length > 0 && (
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label">Produces Component (auto-stocks on ship)</label>
                          <select className="form-select" value={consumableYieldId} disabled={isReadOnly} onChange={(e) => { setConsumableYieldId(e.target.value); setIsDirty(true); }}>
                            <option value="">-- None --</option>
                            {componentConsumables.map(c => (
                              <option key={c.consumable_id} value={c.consumable_id}>
                                {c.product_id} - {c.description}
                              </option>
                            ))}
                          </select>
                          <small className="text-muted">When this part ships, the selected component's stock will increase by the quantity shipped.</small>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="card mb-4">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Quality & PPAP</h5>
                        <div className="d-flex align-items-center">
                            <label className="form-label mb-0 me-2">PPAP Required?</label>
                            <div className="form-check form-switch mb-0">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={ppapRequired}
                                    disabled={isReadOnly}
                                    onChange={(e) => handlePartDataChange('ppap_required', e.target.checked)}
                                    style={{ width: '2.5em', height: '1.25em' }}
                                />
                                <label className="form-check-label ms-1">{ppapRequired ? 'Yes' : 'No'}</label>
                            </div>
                        </div>
                    </div>
                    {ppapRequired && (
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">PPAP Configuration</h6>
                                <div>
                                    <button
                                        className="btn btn-sm btn-outline-secondary me-2"
                                        onClick={handleGeneratePFMEA}
                                        disabled={isReadOnly}
                                        title="Generate Element 6: Process FMEA"
                                    >
                                        <i className="bi bi-exclamation-triangle me-1"></i> PFMEA
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-secondary me-2"
                                        onClick={handleExportControlPlan}
                                        disabled={isReadOnly}
                                    >
                                        <i className="bi bi-file-earmark-pdf me-1"></i> Control Plan
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={handleExportPSW}
                                        disabled={isReadOnly}
                                    >
                                        <i className="bi bi-file-earmark-check me-1"></i> Export PSW
                                    </button>
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <label className={`form-label${!ppapLevel || ppapLevel < 1 || ppapLevel > 5 ? ' required-field-label' : ''}`}>PPAP Level</label>
                                    <input type="number" min="1" max="5" className={`form-control${!ppapLevel || ppapLevel < 1 || ppapLevel > 5 ? ' required-field' : ''}`} value={ppapLevel} onChange={(e) => handlePartDataChange('ppap_level', e.target.value)} disabled={isReadOnly} />
                                    <small className="text-muted">Standard is Level 3</small>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">PPAP Status</label>
                                    <input type="text" className="form-control" value={ppapStatus} readOnly />
                                    {ppapLastApproved && <div className="small text-success mt-1">Approved: {new Date(ppapLastApproved).toLocaleDateString()}</div>}
                                </div>
                            </div>

                            <div className="row mb-3 p-3 border rounded bg-light">
                                <h6 className="mb-3">Sampling Requirements</h6>
                                <div className="col-md-4">
                                    <label className="form-label">Initial Sample Size</label>
                                    <input
                                        type="number"
                                        min="1"
                                        className="form-control"
                                        value={ppapSampleSizeInitial}
                                        disabled={isReadOnly}
                                        onChange={(e) => handlePartDataChange('ppap_sample_size_initial', e.target.value)}
                                    />
                                    <small className="text-muted">First Article Inspection — measured before laser Start. Set 0 to skip FAI.</small>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Ongoing Sample Size</label>
                                    <input
                                        type="number"
                                        min="1"
                                        className="form-control"
                                        value={ppapSampleSizeOngoing}
                                        disabled={isReadOnly}
                                        onChange={(e) => handlePartDataChange('ppap_sample_size_ongoing', e.target.value)}
                                    />
                                    <small className="text-muted">In-process samples — measured before task Complete.</small>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Ongoing Frequency</label>
                                    <select
                                        className="form-select"
                                        value={partData.ppap_frequency || "100%"}
                                        disabled={isReadOnly}
                                        onChange={(e) => handlePartDataChange('ppap_frequency', e.target.value)}
                                    >
                                        {PPAP_FREQUENCIES.map((opt) => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                    <small className="text-muted">Display-only label for the Control Plan PDF — not enforced by the system.</small>
                                </div>
                            </div>

                            <hr />

                            <h6>Critical Characteristics ({ppapCriticalCharacteristics.length})</h6>

                            <table className="table table-sm table-bordered">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Name</th>
                                        <th>Nominal (in)</th>
                                        <th>Tolerance</th>
                                        <th>USL</th>
                                        <th>LSL</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ppapCriticalCharacteristics.map((char, index) => {
                                        const charId = char.id || `temp-${index}`;
                                        const isEditing = editingCharacteristicId === charId;
                                        const limits = calculateLimits(char.nominal, char.tolerance);

                                        return (
                                            <tr key={charId}>
                                                <td>{index + 1}</td>
                                                <td>{isEditing ? <input type="text" className="form-control form-control-sm" value={char.name || ''} onChange={(e) => handleCharacteristicChange(charId, 'name', e.target.value)} /> : char.name}</td>
                                                <td>{isEditing ? <input type="number" step="0.0001" className="form-control form-control-sm" value={char.nominal || ''} onChange={(e) => handleCharacteristicChange(charId, 'nominal', e.target.value)} /> : char.nominal}</td>
                                                <td>{isEditing ? <input type="text" className="form-control form-control-sm" placeholder="e.g. ±0.005" value={char.tolerance || ''} onChange={(e) => handleCharacteristicChange(charId, 'tolerance', e.target.value)} /> : char.tolerance}</td>

                                                <td className="text-success fw-bold">{formatLimit(limits.usl, char.nominal)}</td>
                                                <td className="text-danger fw-bold">{formatLimit(limits.lsl, char.nominal)}</td>

                                                <td>
                                                    {isEditing ? (
                                                        <button className="btn btn-sm btn-success" onClick={() => setEditingCharacteristicId(null)}>Done</button>
                                                    ) : (
                                                        <div className="btn-group">
                                                            <button className="btn btn-sm btn-outline-primary" onClick={() => setEditingCharacteristicId(charId)} disabled={isReadOnly}>Edit</button>
                                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteCharacteristic(charId)} disabled={isReadOnly}>Del</button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {ppapCriticalCharacteristics.length === 0 && (
                                        <tr><td colSpan="7" className="text-center text-muted">No critical characteristics defined.</td></tr>
                                    )}
                                </tbody>
                            </table>

                            <button
                                type="button"
                                className="btn btn-sm btn-outline-success mt-2"
                                onClick={handleAddCharacteristic}
                                disabled={editingCharacteristicId !== null || isReadOnly}
                            >
                                + Add Callout Row
                            </button>
                        </div>
                    )}
                </div>

                <div className="card mb-4" id="material-card">
                  <h5 className="card-header">Pricing & Costing</h5>
                  <div className="card-body">
                    <h6>Material</h6>
                    <div className="p-3 border rounded mb-3">
                      
                      {stockType === 'Sheet Stock' && (
                        <>
                          <div className="row mb-3">
                            <div className="col-md-3"><label className={`form-label${!sizeLength || parseFloat(sizeLength) <= 0 ? ' required-field-label' : ''}`}>Length (in)</label><input type="number" step="0.01" className={`form-control${!sizeLength || parseFloat(sizeLength) <= 0 ? ' required-field' : ''}`} value={sizeLength} disabled={isReadOnly} onChange={(e) => handlePartDataChange('size_length', e.target.value)} /></div>
                            <div className="col-md-3"><label className={`form-label${!sizeWidth || parseFloat(sizeWidth) <= 0 ? ' required-field-label' : ''}`}>Width (in)</label><input type="number" step="0.01" className={`form-control${!sizeWidth || parseFloat(sizeWidth) <= 0 ? ' required-field' : ''}`} value={sizeWidth} disabled={isReadOnly} onChange={(e) => handlePartDataChange('size_width', e.target.value)} /></div>
                            <div className="col-md-3">
                              <label className={`form-label${!weightInGrams || parseFloat(weightInGrams) <= 0 ? ' required-field-label' : ''}`}>Weight (grams)</label>
                              <input type="number" step="0.01" className={`form-control${!weightInGrams || parseFloat(weightInGrams) <= 0 ? ' required-field' : ''}`} value={weightInGrams} disabled={isReadOnly} onChange={(e) => handleWeightGramsChange(e.target.value)} />
                              {weightPoundsDisplay && <small className="text-muted">Calculated: {weightPoundsDisplay} lbs</small>}
                            </div>
                          </div>
                          <div className="row mb-3">
                            <div className="col-md-4"><label className={`form-label${!materialType ? ' required-field-label' : ''}`}>Material Type</label><select className={`form-select${!materialType ? ' required-field' : ''}`} value={materialType} disabled={isReadOnly} onChange={(e) => handlePartDataChange('material_type', e.target.value)}><option value="">Select...</option>{cwtOptions.material_types.map(type => (<option key={type} value={type}>{type}</option>))}</select></div>
                            <div className="col-md-4"><label className={`form-label${!surfaceFinish ? ' required-field-label' : ''}`}>Surface Finish</label><select className={`form-select${!surfaceFinish ? ' required-field' : ''}`} value={surfaceFinish} disabled={isReadOnly} onChange={(e) => handlePartDataChange('surface_finish', e.target.value)}><option value="">Select...</option>{cwtOptions.surface_finishes.map(finish => (<option key={finish} value={finish}>{finish}</option>))}</select></div>
                            <div className="col-md-4">
                              <label className={`form-label${!thicknessId ? ' required-field-label' : ''}`}>Thickness</label>
                              <select className={`form-select${!thicknessId ? ' required-field' : ''}`} value={thicknessId} disabled={isReadOnly} onChange={(e) => handlePartDataChange('thickness_id', e.target.value)}>
                                <option value="">Select...</option>
                                {filteredThicknessOptions.map(opt => ( <option key={opt.thickness_id} value={opt.thickness_id}>{opt.display_thickness}</option>))}
                              </select>
                            </div>
                          </div>
                          <div className="row mb-3">
                            <div className="col-md-6"><label className="form-label">Upload DXF to populate dimensions and part image</label><input type="file" className="form-control" accept=".dxf" disabled={isReadOnly} onChange={handleDxfUpload} /></div>
                            <div className="col-md-6">
                              <label className="form-label">Solid Profile Area (sq. in.)</label>
                              <input type="text" className={`form-control ${(!solidProfileArea || parseFloat(solidProfileArea) === 0) ? 'is-invalid' : ''}`} value={solidProfileArea} readOnly />
                            </div>
                          </div>
                          {profileAreaProblem(solidProfileArea) === 'none' && (
                              <div className="alert alert-warning fw-bold mt-2" data-testid="profile-area-warning">
                                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                  No DXF analyzed yet, so this part has no profile area. Material cost on cut sheets uses the bounding box ({sizeLength || '?'} × {sizeWidth || '?'} in) until it does. Upload the part's DXF above (save a DWG as DXF first) for accurate costing.
                              </div>
                          )}
                          {profileAreaProblem(solidProfileArea) === 'zero' && (
                              <div className="alert alert-warning fw-bold mt-2" data-testid="profile-area-warning">
                                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                  Profile area is 0: the DXF was analyzed but no closed outline was found (often a lead-in line breaking the closed profile). Material cost on cut sheets uses the bounding box ({sizeLength || '?'} × {sizeWidth || '?'} in). Fix the DXF and upload it again for accurate costing.
                              </div>
                          )}
                          {missingSpecWarning && (
                              <div className="alert alert-warning fw-bold mt-2">
                                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                  {missingSpecWarning}
                              </div>
                          )}
                        </>
                      )}

                      {stockType === 'Shape Stock' && (
                        <>
                          <div className="row mb-3">
                            <div className="col-md-4">
                                <label className="form-label">Part Cut Length (in)</label>
                                <input type="number" step="0.01" className="form-control" value={sizeLength} disabled={isReadOnly} onChange={(e) => handlePartDataChange('size_length', e.target.value)} />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label">Material Type</label>
                                <select className="form-select" value={materialType} disabled={isReadOnly} onChange={(e) => handlePartDataChange('material_type', e.target.value)}>
                                <option value="">Select...</option>
                                {cwtOptions.material_types.map(type => (<option key={type} value={type}>{type}</option>))}
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className={`form-label${!partData?.profile_shape ? ' required-field-label' : ''}`}>Profile Shape</label>
                                <select className={`form-select${!partData?.profile_shape ? ' required-field' : ''}`} value={partData?.profile_shape || ""} disabled={isReadOnly} onChange={(e) => handlePartDataChange('profile_shape', e.target.value)}>
                                    <option value="">Select...</option>
                                    {PROFILE_SHAPES.map(shape => <option key={shape} value={shape}>{shape}</option>)}
                                </select>
                            </div>
                          </div>
                          <div className="row mb-3">
                              <div className="col-md-12">
                                  <label className="form-label">Select Material</label>
                                  <select className="form-select" value={partData?.material_id || ""} disabled={isReadOnly} onChange={(e) => handlePartDataChange('material_id', e.target.value)}>
                                      <option value="">-- Select a Profile Shape First --</option>
                                      {shapeStockOptions.map(mat => (
                                          <option key={mat.material_id} value={mat.material_id}>
                                              {mat.material_code} - {mat.description}
                                          </option>
                                      ))}
                                  </select>
                              </div>
                          </div>
                          <div className="row mb-3">
                              <div className="col-md-4"><label className="form-label">Profile Width</label><input type="text" className="form-control" readOnly value={selectedShapeMaterial?.profile_width || ""} /></div>
                              <div className="col-md-4"><label className="form-label">Profile Height</label><input type="text" className="form-control" readOnly value={selectedShapeMaterial?.profile_height || ""} /></div>
                              <div className="col-md-4"><label className="form-label">Wall Thickness</label><input type="text" className="form-control" readOnly value={selectedShapeMaterial?.wall_thickness || ""} /></div>
                          </div>
                          {selectedShapeMaterial?.weight && selectedShapeMaterial?.stock_length && sizeLength && parseFloat(sizeLength) > 0 && (
                          <div className="row mb-3">
                              <div className="col-md-4">
                                  <label className="form-label">Part Weight (lbs)</label>
                                  <input type="text" className="form-control" readOnly value={((selectedShapeMaterial.weight / selectedShapeMaterial.stock_length) * parseFloat(sizeLength)).toFixed(2)} />
                              </div>
                          </div>
                          )}
                        </>
                      )}

                      <hr/>
                      <h6>Select Material Price Method</h6>
                      {materialLive.unresolved.length > 0 && (
                          <div className="alert alert-danger py-1 px-2 small" data-testid="part-material-unresolved">
                              <strong>Material cost unresolved (not $0):</strong> {materialLive.unresolved.map(u => u.message || u.code).join(' · ')}.
                              {' '}Quotes show this part as “cost unresolved” until its material price is chosen again below and saved.
                          </div>
                      )}
                      <button className="btn btn-secondary mb-2" onClick={calculateMaterialPrice} disabled={isReadOnly}>Calculate Material Prices</button>
                      {/* F32: the CWT / linear debug box describes ONLY the weight, area and
                          cut-length methods — hidden beside a nested cut-sheet / cut-list
                          selection so it cannot be read as that method's math. */}
                      {materialPrices && materialPrices.debug_info && isMaterialDebugMethod(partData.material_price_method) && (
                          <PricingDebugInfo
                              info={materialPrices.debug_info}
                              selectedMethod={partData.material_price_method}
                          />
                      )}
                      {materialPrices && materialPrices.debug_info && !materialPrices.debug_info.error && !isMaterialDebugMethod(partData.material_price_method) && (
                          <div className="small text-muted mb-2">
                              CWT / linear reference prices are shown on the radios below; the rule box appears only when a weight, area or cut-length method is selected.
                          </div>
                      )}

                      {stockType === 'Sheet Stock' && (
                        <>
                          {['weight', 'bounding_area', 'solid_profile'].map(method => {
                            const priceKey = `price_by_${method}`;
                            const price = materialPrices[priceKey];
                            const priceLabel = price != null ? ` ($${price.toFixed(2)})` : '';
                            const labelText = `By ${method.replace('_', ' ')}${priceLabel}`;
                            return (
                              <div className="form-check" key={method}>
                                <input className="form-check-input" type="radio" name="materialPriceMethod" id={`price_${method}`} value={method} checked={partData.material_price_method === method} disabled={isReadOnly} onChange={handleMaterialPriceMethodChange} />
                                <label className="form-check-label text-capitalize" htmlFor={`price_${method}`}>{labelText}</label>
                                {renderMethodProvenance(method, null, null)}
                              </div>
                            );
                          })}
                          <div className="form-check">
                              <input className="form-check-input" type="radio" name="materialPriceMethod" id="price_manual_price" value="manual_price" checked={partData.material_price_method === 'manual_price'} disabled={isReadOnly} onChange={handleMaterialPriceMethodChange} />
                              <label className="form-check-label" htmlFor="price_manual_price">Manual Price</label>
                          </div>
                          {partData.material_price_method === 'manual_price' && renderManualPriceInput()}
                          <div className="form-check">
                            <input className="form-check-input" type="radio" name="materialPriceMethod" id="price_manual" value="manual" checked={partData.material_price_method === 'manual'} disabled={isReadOnly} onChange={handleMaterialPriceMethodChange} />
                            <label className="form-check-label" htmlFor="price_manual">{manualLabel.text}</label>
                            {renderMethodProvenance('manual', null, null)}
                            {partData.material_price_method === 'manual' && renderManualPriceInput()}
                          </div>
                          {cutSheetsForPart.length > 0 && (
                            <div className="mt-2 pt-2 border-top">
                              {cutSheetsForPart.map(cs => {
                                const value = `nested_${cs.cut_sheet_part_id}`;
                                const priceLabel = cs.material_cost !== null ? `(${fmtMoney(cs.material_cost)})` : '(No cost calculated)';
                                const labelText = `From Cut Sheet: ${cs.cut_sheet_name} ${priceLabel}`;
                                return (
                                  <div className="form-check" key={value}>
                                    <input className="form-check-input" type="radio" name="materialPriceMethod" id={`price_${value}`} value={value} checked={partData.material_price_method === value} onChange={handleMaterialPriceMethodChange} disabled={isReadOnly || cs.material_cost === null} />
                                    <label className="form-check-label" htmlFor={`price_${value}`}>{labelText}</label>
                                    {renderMethodProvenance(value, cs, 'cut_sheet')}
                                  </div>
                                );
                              })}
                              <div className="small text-muted mt-1">
                                <i className="bi bi-info-circle me-1"></i>
                                Cut-sheet allocation = the whole sheet (less defined remnants) shared across the parts nested on it by area —
                                a lightly nested sheet makes a small part look expensive. Utilisation below 25% is flagged. The figure is a
                                snapshot copied when the radio is clicked; the sheet's own row is refreshed by sheet edits / recalcs and the nightly job.
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      {stockType === 'Shape Stock' && (
                        <>
                            <div className="form-check">
                                <input className="form-check-input" type="radio" name="materialPriceMethod" id="price_cut_length" value="cut_length" checked={partData.material_price_method === 'cut_length'} disabled={isReadOnly} onChange={handleMaterialPriceMethodChange} />
                                <label className="form-check-label" htmlFor="price_cut_length">Cut Length Costing {materialPrices.price_by_cut_length ? `(${fmtMoney(materialPrices.price_by_cut_length)})` : ''}</label>
                                {renderMethodProvenance('cut_length', null, null)}
                            </div>
                            {cutListsForPart.length > 0 && (
                              <div className="mt-2 pt-2 border-top">
                                {cutListsForPart.map(cl => {
                                  const value = `nested_cut_list_${cl.cut_list_part_id}`;
                                  const priceLabel = cl.material_cost !== null ? `(${fmtMoney(cl.material_cost)})` : '(No cost calculated)';
                                  const labelText = `Stock Length Allocation: ${cl.cut_list_name} ${priceLabel}`;
                                  return (
                                    <div className="form-check" key={value}>
                                      <input className="form-check-input" type="radio" name="materialPriceMethod" id={`price_${value}`} value={value} checked={partData.material_price_method === value} onChange={handleMaterialPriceMethodChange} disabled={isReadOnly || cl.material_cost === null} />
                                      <label className="form-check-label" htmlFor={`price_${value}`}>{labelText}</label>
                                      {renderMethodProvenance(value, cl, 'cut_list')}
                                    </div>
                                  );
                                })}
                                <div className="small text-muted mt-1">
                                  <i className="bi bi-info-circle me-1"></i>
                                  Stock-length allocation = the whole stick ÷ Σ(length × qty) on the list, with NO drop credit — the parts pay for the
                                  full stick even when most of it re-enters inventory as a remnant. Utilisation below 25% is flagged. The figure is a
                                  snapshot: cut-list rows are refreshed only by the list's "calculate material costs" action (not the nightly job), so
                                  re-select the radio and Save after a stick reprice.
                                </div>
                              </div>
                            )}
                            <div className="form-check">
                                <input className="form-check-input" type="radio" name="materialPriceMethod" id="price_manual" value="manual" checked={partData.material_price_method === 'manual'} disabled={isReadOnly} onChange={handleMaterialPriceMethodChange} />
                                <label className="form-check-label" htmlFor="price_manual">{manualLabel.text}</label>
                                {renderMethodProvenance('manual', null, null)}
                                {partData.material_price_method === 'manual' && renderManualPriceInput()}
                            </div>
                        </>
                      )}
                    </div>
                    <h6 id="labor-card">Labor</h6>
                    <div className="p-3 border rounded mb-3" style={isReadOnly ? {pointerEvents: 'none', opacity: 0.6} : {}}>
                      <label className={`form-label${stockType === 'Sheet Stock' && !partData?.process_steps?.trim() ? ' required-field-label' : ''}`}>Process Sequence</label>
                      <ProcessSequencer allProcesses={allProcessOptions} selectedSteps={selectedSteps} setSelectedSteps={setSelectedSteps}/>
                      
                      <button className="btn btn-sm btn-outline-info mt-2" onClick={() => setShowFlowModal(true)}>
                        <i className="bi bi-diagram-3 me-1"></i> Visualize Process Flow
                      </button>
                    </div>
                    {selectedSteps.some(step => fabSubProcesses.includes(step.name)) && (
                        <div className="p-3 border rounded bg-light mb-3">
                            <h6 className="mb-3">Fabrication Time Estimates</h6>
                            <div className="row">
                                {selectedSteps.some(step => step.name === "Cutting") && (<div className="col-md-6 mb-3"><label className={`form-label${!hasTimeValue(cuttingMinutes) ? ' required-field-label' : ''}`}>Cutting Est. Time (min)</label><input type="text" className={`form-control${!hasTimeValue(cuttingMinutes) ? ' required-field' : ''}`} placeholder="MM:SS or Mins" value={cuttingMinutes} disabled={isReadOnly} onChange={(e) => handlePartDataChange('initial_est_cutting_minutes', e.target.value)} /><small className="text-muted">{avgCuttingMinutes ? `Hist. Avg: ${avgCuttingMinutes} min` : 'No history'}</small></div>)}
                                {selectedSteps.some(step => step.name === "Drilling") && (<div className="col-md-6 mb-3"><label className={`form-label${!hasTimeValue(drillingMinutes) ? ' required-field-label' : ''}`}>Drilling Est. Time (min)</label><input type="text" className={`form-control${!hasTimeValue(drillingMinutes) ? ' required-field' : ''}`} placeholder="MM:SS or Mins" value={drillingMinutes} disabled={isReadOnly} onChange={(e) => handlePartDataChange('initial_est_drilling_minutes', e.target.value)} /><small className="text-muted">{avgDrillingMinutes ? `Hist. Avg: ${avgDrillingMinutes} min` : 'No history'}</small></div>)}
                                {selectedSteps.some(step => step.name === "Milling") && (<div className="col-md-6 mb-3"><label className={`form-label${!hasTimeValue(millingMinutes) ? ' required-field-label' : ''}`}>Milling Est. Time (min)</label><input type="text" className={`form-control${!hasTimeValue(millingMinutes) ? ' required-field' : ''}`} placeholder="MM:SS or Mins" value={millingMinutes} disabled={isReadOnly} onChange={(e) => handlePartDataChange('initial_est_milling_minutes', e.target.value)} /><small className="text-muted">{avgMillingMinutes ? `Hist. Avg: ${avgMillingMinutes} min` : 'No history'}</small></div>)}
                                {selectedSteps.some(step => step.name === "Milling") && (<div className="col-md-6 mb-3"><label className="form-label">Milling Operations (machine setups)</label><input type="number" min={1} max={9} className="form-control" placeholder="1" value={partData?.milling_op_count ?? ""} disabled={isReadOnly} onChange={(e) => handlePartDataChange('milling_op_count', e.target.value)} /><small className="text-muted">Leave blank or 1 for a normal single-setup part. Set 2+ when the run needs the machine re-set partway — e.g. machine one half of every part, change the setup, then the other half. Each operation is timed separately, can be run by a different operator, and the task stays open until the last one is finished.</small></div>)}
                                {selectedSteps.some(step => step.name === "Lathe") && (<div className="col-md-6 mb-3"><label className={`form-label${!hasTimeValue(latheMinutes) ? ' required-field-label' : ''}`}>Lathe Est. Time (min)</label><input type="text" className={`form-control${!hasTimeValue(latheMinutes) ? ' required-field' : ''}`} placeholder="MM:SS or Mins" value={latheMinutes} disabled={isReadOnly} onChange={(e) => handlePartDataChange('initial_est_lathe_minutes', e.target.value)} /><small className="text-muted">{avgLatheMinutes ? `Hist. Avg: ${avgLatheMinutes} min` : 'No history'}</small></div>)}
                                {selectedSteps.some(step => step.name === "Lathe") && (<div className="col-md-6 mb-3"><label className="form-label">Lathe Operations (machine setups)</label><input type="number" min={1} max={9} className="form-control" placeholder="1" value={partData?.lathe_op_count ?? ""} disabled={isReadOnly} onChange={(e) => handlePartDataChange('lathe_op_count', e.target.value)} /><small className="text-muted">Leave blank or 1 for a normal single-setup part. Set 2+ when the run needs the machine re-set partway — e.g. machine one half of every part, change the setup, then the other half. Each operation is timed separately, can be run by a different operator, and the task stays open until the last one is finished.</small></div>)}
                                {selectedSteps.some(step => step.name === "Welding") && (<div className="col-md-6 mb-3"><label className={`form-label${!hasTimeValue(weldingMinutes) ? ' required-field-label' : ''}`}>Welding Est. Time (min)</label><input type="text" className={`form-control${!hasTimeValue(weldingMinutes) ? ' required-field' : ''}`} placeholder="MM:SS or Mins" value={weldingMinutes} disabled={isReadOnly} onChange={(e) => handlePartDataChange('initial_est_weld_minutes', e.target.value)} /><small className="text-muted">{avgWeldingMinutes ? `Hist. Avg: ${avgWeldingMinutes} min` : 'No history'}</small></div>)}
                                {selectedSteps.some(step => step.name === "Assembly") && (<div className="col-md-6 mb-3"><label className={`form-label${!hasTimeValue(assemblyMinutes) ? ' required-field-label' : ''}`}>Assembly Est. Time (min)</label><input type="text" className={`form-control${!hasTimeValue(assemblyMinutes) ? ' required-field' : ''}`} placeholder="MM:SS or Mins" value={assemblyMinutes} disabled={isReadOnly} onChange={(e) => handlePartDataChange('initial_est_assembly_minutes', e.target.value)} /><small className="text-muted">{avgAssemblyMinutes ? `Hist. Avg: ${avgAssemblyMinutes} min` : 'No history'}</small></div>)}
                                {selectedSteps.some(step => step.name === "Tapping") && (<div className="col-md-6 mb-3"><label className={`form-label${!hasTimeValue(fabTapMinutes) ? ' required-field-label' : ''}`}>Fab Tapping Est. Time (min)</label><input type="text" className={`form-control${!hasTimeValue(fabTapMinutes) ? ' required-field' : ''}`} placeholder="MM:SS or Mins" value={fabTapMinutes} disabled={isReadOnly} onChange={(e) => handlePartDataChange('initial_est_fab_tap_minutes', e.target.value)} /><small className="text-muted">{avgFabTapMinutes ? `Hist. Avg: ${avgFabTapMinutes} min` : 'No history'}</small></div>)}
                            </div>
                        </div>
                    )}
                    <div className="row">
                        {selectedProcesses.includes("Laser") && (
                            <>
                                <div className="col-md-3 mb-3">
                                    <label className={`form-label${!hasTimeValue(laserMinutes) ? ' required-field-label' : ''}`}>Laser Task Time</label>
                                    {(() => {
                                        const hasDxf = !!(partData?.dxf_path || pendingDxfFile);
                                        const reasons = [];
                                        if (!hasDxf) reasons.push("upload a DXF");
                                        if (!partData?.material_type) reasons.push("set Material Type");
                                        if (!partData?.thickness_id) reasons.push("set Thickness");
                                        const calcDisabled = reasons.length > 0 || isCalculatingLaserTime || isReadOnly;
                                        const calcTitle = reasons.length
                                          ? `To enable: ${reasons.join(", ")}.`
                                          : "Recalculate from this part's DXF using the nester's beam-on formula.";
                                        return (
                                          <div className="input-group input-group-sm">
                                            <input
                                                type="text"
                                                className={`form-control${!hasTimeValue(laserMinutes) ? ' required-field' : ''}`}
                                                placeholder="MM:SS or Mins"
                                                value={laserMinutes}
                                                disabled={isReadOnly}
                                                onChange={(e) => handlePartDataChange('initial_est_laser_minutes', e.target.value)}
                                            />
                                            <button
                                              type="button"
                                              className="btn btn-outline-primary"
                                              onClick={handleCalculateLaserTimeFromDxf}
                                              disabled={calcDisabled}
                                              title={calcTitle}
                                            >
                                              {isCalculatingLaserTime ? (
                                                <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span></>
                                              ) : (
                                                <><i className="bi bi-cpu me-1"></i> Calc from DXF</>
                                              )}
                                            </button>
                                          </div>
                                        );
                                    })()}
                                    <small className="text-muted">{avgLaserHours ? `Hist. Avg: ${avgLaserHours} min` : 'No history'}</small>
                                    {(() => {
                                        // Surface disabled reasons inline so the operator doesn't need
                                        // to discover them via the button's hover-title.
                                        if (!selectedProcesses.includes("Laser") || isReadOnly) return null;
                                        const hasDxf = !!(partData?.dxf_path || pendingDxfFile);
                                        const reasons = [];
                                        if (!hasDxf) reasons.push("upload a DXF");
                                        if (!partData?.material_type) reasons.push("set Material Type");
                                        if (!partData?.thickness_id) reasons.push("set Thickness");
                                        if (reasons.length === 0) return null;
                                        return (
                                          <div className="small text-warning mt-1">
                                            <i className="bi bi-exclamation-triangle me-1"></i>
                                            Calc from DXF needs: {reasons.join(", ")}
                                          </div>
                                        );
                                    })()}
                                </div>
                                <div className="col-md-3 mb-3">
                                    <label className="form-label text-success fw-bold">Beam On Time (min)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={partData?.beam_on_minutes ? parseFloat(partData.beam_on_minutes).toFixed(4) : "0.0000"}
                                        readOnly
                                        disabled
                                        style={{backgroundColor: "#e8f5e9"}}
                                    />
                                    <small className="text-muted">Auto-calc from Cut Sheet</small>
                                </div>
                                <div className="col-md-2 mb-3">
                                    <label className="form-label">Pierce Count</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={pierceCount}
                                        disabled={isReadOnly}
                                        onChange={(e) => handlePartDataChange('pierce_count', e.target.value)}
                                    />
                                </div>
                            </>
                        )}
                        {selectedProcesses.includes("Tap") && <div className="col-md-6 mb-3"><label className={`form-label${!hasTimeValue(tapMinutes) ? ' required-field-label' : ''}`}>Tap Est. Time (min)</label><input type="text" className={`form-control${!hasTimeValue(tapMinutes) ? ' required-field' : ''}`} placeholder="MM:SS or Mins" value={tapMinutes} disabled={isReadOnly} onChange={(e) => handlePartDataChange('initial_est_tap_minutes', e.target.value)} /><small className="text-muted">{avgTapHours ? `Hist. Avg: ${avgTapHours} min` : 'No history'}</small></div>}
                        {selectedProcesses.includes("Brake") && <div className="col-md-6 mb-3"><label className={`form-label${!hasTimeValue(brakeMinutes) ? ' required-field-label' : ''}`}>Brake Est. Time (min)</label><input type="text" className={`form-control${!hasTimeValue(brakeMinutes) ? ' required-field' : ''}`} placeholder="MM:SS or Mins" value={brakeMinutes} disabled={isReadOnly} onChange={(e) => handlePartDataChange('initial_est_brake_minutes', e.target.value)} /><small className="text-muted">{avgBrakeHours ? `Hist. Avg: ${avgBrakeHours} min` : 'No history'}</small></div>}
                        {brakeAfterLaser && (
                          <div className="col-md-6 mb-3">
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="brake-sheet-dividers"
                                checked={!!partData?.brake_sheet_dividers}
                                disabled={isReadOnly}
                                onChange={(e) => handlePartDataChange('brake_sheet_dividers', e.target.checked)}
                              />
                              <label className="form-check-label fw-semibold" htmlFor="brake-sheet-dividers">Keep sheets apart for the Brake</label>
                            </div>
                            <small className="text-muted">The laser operator stacks the sheets in order with cardboard between them and that sheet's printed label on each card, so the brake operator re-checks the setup at every sheet. Leave off unless this part's bends need it.</small>
                          </div>
                        )}
                        {selectedProcesses.includes("Blast") && (
                            <>
                                <div className="col-md-4 mb-3">
                                    <label className={`form-label${!hasTimeValue(blastMinutes) ? ' required-field-label' : ''}`}>Blast Est. Time (min)</label>
                                    <input
                                        type="text"
                                        className={`form-control${!hasTimeValue(blastMinutes) ? ' required-field' : ''}`}
                                        placeholder="MM:SS or Mins"
                                        value={blastMinutes}
                                        disabled={isReadOnly}
                                        onChange={(e) => handlePartDataChange('initial_est_blast_minutes', e.target.value)}
                                    />
                                    <small className="text-muted">{avgBlastHours ? `Hist. Avg: ${avgBlastHours} min` : 'No history'}</small>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <label className="form-label text-primary">Blast Time Top</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="MM:SS"
                                        value={partData.blast_time_top || ""}
                                        disabled={isReadOnly}
                                        onChange={(e) => handlePartDataChange('blast_time_top', e.target.value)}
                                    />
                                </div>
                                <div className="col-md-4 mb-3">
                                    <label className="form-label text-primary">Blast Time Bottom</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="MM:SS"
                                        value={partData.blast_time_bottom || ""}
                                        disabled={isReadOnly}
                                        onChange={(e) => handlePartDataChange('blast_time_bottom', e.target.value)}
                                    />
                                </div>
                            </>
                        )}
                        {selectedProcesses.includes("Finish") && <div className="col-md-6 mb-3"><label className={`form-label${!hasTimeValue(finishMinutes) ? ' required-field-label' : ''}`}>Finish Est. Time (min)</label><input type="text" className={`form-control${!hasTimeValue(finishMinutes) ? ' required-field' : ''}`} placeholder="MM:SS or Mins" value={finishMinutes} disabled={isReadOnly} onChange={(e) => handlePartDataChange('initial_est_finish_minutes', e.target.value)} /><small className="text-muted">{avgFinishHours ? `Hist. Avg: ${avgFinishHours} min` : 'No history'}</small></div>}
                    </div>
                    
                    <h6 className="d-flex align-items-center mt-4">
                      Final Pricing
                      <span 
                        className="ms-2" 
                        data-bs-toggle="tooltip" 
                        data-bs-html="true"
                        title={pricingHelpText.replace(/\n/g, '<br />')}
                        style={{ cursor: 'pointer' }}
                      >
                        <i className="bi bi-question-circle-fill text-muted"></i>
                      </span>
                    </h6>
                    <div className="p-3 border rounded bg-light" data-testid="pricing-costing-card">
                        {costNotice && (
                          <div className="alert alert-success py-2 px-3 small d-flex justify-content-between align-items-start mb-3">
                            <div>{costNotice}</div>
                            <button type="button" className="btn-close btn-sm ms-2" aria-label="Dismiss" onClick={() => setCostNotice(null)}></button>
                          </div>
                        )}
                        {/* ROW 1: The Four Cost Components + Total Sum. Every box carries a
                            source / as-of tag (F06 / F16): labor, gas and consumables are LIVE
                            from the cost engine (stored value printed beneath); material is the
                            selected / stored snapshot beside the engine's live resolution; the
                            total is STORED vs LIVE with a stale badge. All figures are per-unit COST. */}
                        <div className="row mb-3">
                            <div className="col-md-2">
                                <label className="form-label text-muted small fw-bold">Cost - Labor</label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text">$</span>
                                    <input
                                        type="number"
                                        className="form-control"
                                        readOnly
                                        value={partData?.cost_labor ? Number(partData.cost_labor).toFixed(2) : '0.00'}
                                        title={`Σ hours × true-cost $/hr per step — ${engineLabel}. Includes the Laser shuttle share shown beneath. Open "Calculation log" below for the per-step math.`}
                                    />
                                </div>
                                <div className="small text-muted mt-1" style={{ lineHeight: 1.3 }}>
                                    <SourceTag kind={engineTagKind} title="Which engine produced this figure">{engineLabel}</SourceTag>
                                    {calcLog.hoursSource && costEngine.engine !== 'dynamic' ? <> hours: <SourceTag kind={calcLog.hoursSource} /></> : null}
                                    <ShuttleLine shuttle={costShuttle} />
                                    <StoredLine asOfLabel={storedAsOfLabel} stored={storedSnap?.cost_labor} live={partData?.cost_labor} hasStored={hasStored} />
                                    {opsOwnedOnRouting.length > 0 && (
                                        <div>
                                            <SourceTag kind="assembly_row" title="These steps belong to the assembly's operations line; quotes exclude them from this part">
                                                {opsOwnedOnRouting.join(', ')}: priced on {ownersText(quoteOwners)} ops line
                                            </SourceTag>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="col-md-2">
                                <label className="form-label text-muted small fw-bold">Cost - Gas</label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text">$</span>
                                    <input
                                        type="number"
                                        className="form-control"
                                        readOnly
                                        value={partData?.estimated_gas_cost ? Number(partData.estimated_gas_cost).toFixed(4) : '0.0000'}
                                        title={gasRow ? gasRow.raw : 'assist gas $/hr × beam-on hrs'}
                                    />
                                </div>
                                <div className="small text-muted mt-1" style={{ lineHeight: 1.3 }}>
                                    {gasRow && gasRow.source ? <SourceTag kind={gasRow.source === 'historical' ? 'historical' : (gasRow.source === 'none' ? 'default' : 'estimate')}>{gasRow.source === 'historical' ? 'historical avg' : gasRow.source === 'theoretical_beam_on' ? 'theoretical · beam-on' : gasRow.source === 'theoretical_total_time' ? 'theoretical · total time' : 'no data'}</SourceTag> : <SourceTag kind="default">live · at cost</SourceTag>}
                                    <StoredLine asOfLabel={storedAsOfLabel} stored={storedSnap?.estimated_gas_cost} live={partData?.estimated_gas_cost} precise hasStored={hasStored} />
                                </div>
                            </div>

                            {/* --- NEW CONSUMABLE FIELD --- */}
                            <div className="col-md-3">
                                <label className="form-label text-muted small fw-bold">Cost - Consumables</label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text">$</span>
                                    <input
                                        type="number"
                                        className="form-control"
                                        readOnly
                                        value={partData?.estimated_consumable_cost ? Number(partData.estimated_consumable_cost).toFixed(4) : '0.0000'}
                                        title={consRow ? consRow.raw : 'nozzle + lens $/hr × beam-on hrs, at cost'}
                                    />
                                </div>
                                <div className="small text-muted mt-1" style={{ lineHeight: 1.3 }}>
                                    <SourceTag kind="default">{consRow && consRow.hours !== null ? `live · at cost · ${consRow.rateNote || 'nozzle+lens'}` : (costEngine.engine === 'dynamic' ? 'kept from load (estimate engine returns none)' : 'live · at cost')}</SourceTag>
                                    <StoredLine asOfLabel={storedAsOfLabel} stored={storedSnap?.estimated_consumable_cost} live={partData?.estimated_consumable_cost} precise hasStored={hasStored} />
                                </div>
                            </div>

                            <div className="col-md-2">
                                <label className="form-label text-muted small fw-bold">Cost - Material</label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text">$</span>
                                    <input
                                        type="number"
                                        className="form-control"
                                        readOnly
                                        value={partData?.material_cost_component ? Number(partData.material_cost_component).toFixed(2) : '0.00'}
                                        title={materialSourceText}
                                    />
                                </div>
                                <div className="small text-muted mt-1" style={{ lineHeight: 1.3 }}>
                                    <SourceTag kind={methodUnchanged ? 'default' : 'estimate'} title={materialSourceText}>{methodUnchanged ? `stored · ${methodKind}` : `selected · ${methodKind} (unsaved)`}</SourceTag>
                                    {liveMaterialFromEngine ? (
                                        <div>
                                            live (engine): {fmtMoney(materialLive.value, { precise: true })}
                                            {materialFresh.stale ? <> ({fmtDelta(materialFresh.delta)} <SourceTag kind="stale">stale — Save to refresh</SourceTag>)</> : <> <span className="badge bg-success">in sync</span></>}
                                        </div>
                                    ) : (
                                        <div>{materialLive.available
                                            ? (methodUnchanged ? 'live: engine could not resolve this method (see radio)' : 'live: re-resolves after Save')
                                            : 'live: n/a — API predates provenance'}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-md-3">
                                <label className="form-label text-primary small fw-bold">Total Cost (L+G+C+M+Coat) — live</label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text bg-primary text-white border-primary">$</span>
                                    <input
                                        type="text"
                                        className="form-control border-primary fw-bold"
                                        readOnly
                                        value={liveRollup.cost_labor_material.toFixed(2)}
                                        title={costFormulaText}
                                    />
                                </div>
                                <div className="small mt-1" style={{ lineHeight: 1.3 }}>
                                    <div className="text-muted">{costFormulaText}</div>
                                    {hasStored ? (
                                        <div>
                                            <strong>Stored total {fmtMoney(storedSnap.cost_labor_material)}</strong> ({storedAsOfText}) vs <strong>live {fmtMoney(liveRollup.cost_labor_material)}</strong>
                                            {totalFresh.stale ? (
                                                <>
                                                    {' '}<SourceTag kind="stale" title="The Assemblies card and SO conversion read the STORED total until you save">stale {fmtDelta(totalFresh.delta)}</SourceTag>
                                                    {!isReadOnly && dbPartId && (
                                                        <button type="button" className="btn btn-link btn-sm p-0 ms-1 align-baseline" onClick={handleSavePart}>Save to update</button>
                                                    )}
                                                </>
                                            ) : <> <span className="badge bg-success">in sync</span></>}
                                        </div>
                                    ) : <div className="text-muted">not saved yet — Create Part stores this total</div>}
                                    {asQuoted.applies && (
                                        <div className="text-muted">
                                            As quoted inside {ownersText(quoteOwners)}: <strong>{fmtMoney(asQuoted.value)}</strong>{asQuoted.approximate ? ' (approx.)' : ''} — excludes{' '}
                                            {asQuoted.excluded.map(e => `${e.label} ${e.unknown ? '(not in log)' : fmtMoney(e.amount)}`).join(', ')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ROW 1b: Outsourced Coating (per-unit; priced with markup like material) */}
                        <div className="row mb-3 align-items-end">
                            <div className="col-md-3">
                                <label className="form-label text-muted small fw-bold" htmlFor="coating-cost-mirror">Coating $/unit (outsourced)</label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text">$</span>
                                    {/* Decision 3 (wave 2): READ-ONLY mirror of the Outsourcing card's Coating
                                        row. The card is the single editor of an outsourced $/piece; this box
                                        never writes — see coatingPayloadFromCard in handleSavePart. */}
                                    <input
                                        id="coating-cost-mirror"
                                        type="number"
                                        step="0.0001"
                                        className="form-control bg-light"
                                        readOnly
                                        value={partData?.coating_cost_per_unit ?? ''}
                                        placeholder="0.00"
                                        title="Read-only mirror of the Outsourcing card → Coating row. Edit the $/piece there; it writes the row and this column together."
                                    />
                                </div>
                                <div className="small text-muted mt-1" style={{ lineHeight: 1.3 }}>
                                    <div data-testid="coating-mirror-note">
                                        <SourceTag kind={coatingMirror.kind} title="parts.coating_cost_per_unit is written by the Outsourcing card, the price-book write-through (set-preferred / RFQ award) and the nightly — never by this box">mirror</SourceTag>{' '}{coatingMirror.text}
                                    </div>
                                    {coatingOwnedBy.length > 0 ? (
                                        <SourceTag kind="assembly_row" title="The assembly's operations line carries a priced Coating row, so the quote engine strips this part's coating when the part is quoted inside that assembly">
                                            <span style={{ textDecoration: 'line-through' }}>{fmtMoney(partData?.coating_cost_per_unit)}</span>{' '}priced on {ownersText(coatingOwnedBy)} operations line
                                        </SourceTag>
                                    ) : (
                                        <SourceTag kind="default">COST / unit · in total and marked up with material</SourceTag>
                                    )}
                                    <StoredLine asOfLabel={storedAsOfLabel} stored={storedSnap?.coating_cost_per_unit} live={partData?.coating_cost_per_unit} hasStored={hasStored} />
                                </div>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label text-muted small fw-bold">Coating Vendor (coater)</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={partData?.coating_vendor_id || ''}
                                    onChange={(e) => handlePartDataChange('coating_vendor_id', e.target.value || null)}
                                >
                                    <option value="">-- None --</option>
                                    {serviceVendors.map((v) => (
                                        <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>
                                    ))}
                                </select>
                            </div>
                            {/* Requested finish COLOR — prints on the PO that travels to the coater. */}
                            <div className="col-md-6">
                                <CoatingColorField
                                    code={partData?.coating_color_code || ''}
                                    name={partData?.coating_color_name || ''}
                                    hex={partData?.coating_color_hex || ''}
                                    onChange={handlePartDataPatch}
                                    help="Prints on the coater's PO."
                                />
                            </div>
                        </div>

                        {/* Per-step outsourcing. The fields above are the LEGACY coating-only pair,
                            kept because parts.coating_* is still the system of record until the
                            Phase 5 strip -- the card below writes THROUGH to them for Coating, so
                            the two never disagree. Every other step gets a checkbox here that is
                            disabled with the server's own reason rather than hidden. */}
                        {partData?.part_id ? (
                            <div className="row mb-3">
                                <div className="col-12">
                                    {/* onChanged is NOT optional. The card's PATCH (and the
                                        price-book Apply) writes part_processes AND parts.coating_*
                                        in one transaction and recomputes the stored rollup; without
                                        this callback the read-only mirror above and the stored
                                        snapshot keep the pre-edit values. Wave 2 (decision 3): Save
                                        no longer sends coating_cost_per_unit unless the card changed
                                        it — coatingFromCard is set HERE, from the value the server
                                        echoes, and only when it differs from the page copy;
                                        coating_vendor_id still travels (the vendor select above
                                        remains an editor and _sync_part_processes mirrors it).
                                        Refreshes only the coating fields + the stored snapshot — a
                                        full reload would discard unsaved edits elsewhere on the
                                        form. onData hands the card's rows to the mirror (vendor /
                                        book price / date). */}
                                    <PartOutsourcingCard
                                        partId={partData.part_id}
                                        onData={handleOutsourcingData}
                                        onChanged={async () => {
                                            try {
                                                const r = await fetch(`${API_BASE}/parts/${partData.part_id}`);
                                                if (!r.ok) return;
                                                const fresh = await r.json();
                                                const before = safeParseFloat(partData?.coating_cost_per_unit);
                                                const after = safeParseFloat(fresh.coating_cost_per_unit);
                                                if (Math.abs(before - after) >= 0.00005) setCoatingFromCard(fresh.coating_cost_per_unit ?? null);
                                                setPartData(prev => prev ? {
                                                    ...prev,
                                                    coating_cost_per_unit: fresh.coating_cost_per_unit,
                                                    coating_vendor_id: fresh.coating_vendor_id,
                                                    coating_color_code: fresh.coating_color_code,
                                                    coating_color_name: fresh.coating_color_name,
                                                    coating_color_hex: fresh.coating_color_hex,
                                                } : prev);
                                                // The write-through recomputed the stored rollup server-side.
                                                setStoredSnap(snapshotFromPart(fresh));
                                            } catch (e) {
                                                // Leave the legacy fields as they are rather than
                                                // blanking them — a failed refresh must not turn
                                                // into a cleared price on the next Save.
                                                console.error("Failed to refresh coating fields", e);
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        ) : null}

                        {/* ROW 1c: coating explainer + the one failure mode worth catching here —
                            a part routed through Coating with nobody having said what color. */}
                        <div className="row mb-3">
                            <div className="col-12">
                                {selectedProcesses.includes("Coating") && !(partData?.coating_color_code || '').trim() ? (
                                    <div className="alert alert-warning py-2 px-3 mb-0 small">
                                        <strong>No coating color set.</strong> This part routes through{' '}
                                        <strong>Coating</strong>, so the PO generated at the hand-off will tell the
                                        coater to stop and call for the color. Set it above.
                                    </div>
                                ) : (
                                    <small className="text-muted">
                                        Add <strong>Coating</strong> to the process steps above to route this part through an
                                        outside finisher. The $/unit (edited on the Outsourcing card's Coating row) is part of Total Cost and is marked up with material in
                                        "Cost + material markup"; when an assembly's operations line prices coating, quotes
                                        charge it there and exclude it from this part (see the tag above). The PO to the coater
                                        is generated automatically when the step before Coating completes, and carries the color code above.
                                    </small>
                                )}
                            </div>
                        </div>

                        {/* ROW 2: Markup and Final Prices */}
                        <div className="row align-items-end">
                            <div className="col-md-2">
                                <label className="form-label small" title="parts.markup_percent — a PERCENT applied to material + coating in the internal estimate only. Quotes use their own material markup (a fraction) and never read this.">Material Markup %</label>
                                <div className="input-group input-group-sm">
                                    <input
                                        type="number"
                                        step="0.1"
                                        className="form-control"
                                        value={markup_percent}
                                        disabled={isReadOnly}
                                        onChange={(e) => handlePartDataChange('markup_percent', e.target.value)}
                                    />
                                    <span className="input-group-text">%</span>
                                </div>
                                <div className="small text-muted mt-1">blank = 10%; 0 stays 0</div>
                            </div>

                            {/* F28: this box used to be labelled "Calculated Price". It is labour at
                                WAGE rates + gas + consumables at cost + (material + coating) × (1 +
                                markup%) — an internal estimate, not the quote engine's price. */}
                            <div className="col-md-5">
                                <label className="form-label small" title="Internal estimate at true-cost wage rates plus the material markup. NOT a quote price: quotes price labour at the quote's selling $/hr and material at the quote's markup, and never read this value.">
                                    Cost + material markup <span className="text-muted">(internal estimate; not a quote price)</span>
                                </label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text">$</span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        readOnly
                                        value={liveRollup.price_labor_material.toFixed(2)}
                                        title={priceFormulaText}
                                    />
                                </div>
                                <div className="small text-muted mt-1" style={{ lineHeight: 1.3 }}>
                                    <div>{priceFormulaText}</div>
                                    <StoredLine asOfLabel={storedAsOfLabel} stored={storedSnap?.price_labor_material} live={liveRollup.price_labor_material} hasStored={hasStored} />
                                </div>
                            </div>

                            <div className="col-md-5">
                                <label className="form-label fw-bold" title="Book price. Set here or written by quote approval (parts.price_source / price_quote_id when the API reports them).">
                                    Final Sale Price
                                    {partData?.price_source ? <span className="badge bg-light text-dark border ms-2 fw-normal">{partData.price_source === 'quote_approval' ? `from quote #${partData.price_quote_id ?? '?'}` : partData.price_source}{partData.price_set_at ? ` · ${asOf(partData.price_set_at)}` : ''}</span> : null}
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text bg-success text-white border-success">$</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="form-control border-success fw-bold fs-5"
                                        value={price}
                                        disabled={isReadOnly}
                                        onChange={(e) => handlePartDataChange('price', e.target.value)}
                                    />
                                </div>
                                <div className="small text-muted mt-1">PRICE / unit (book). Margin vs live cost: {(() => { const p = safeParseFloat(price); const c = liveRollup.cost_labor_material; return p > 0 ? `${(((p - c) / p) * 100).toFixed(1)}%` : '—'; })()}</div>
                            </div>
                        </div>

                        {/* Stored vs live reconciliation (F06) — one row per figure, the
                            formula beside each total, and the engine's own per-step log (F08)
                            rendered as a table instead of window.alert. */}
                        <details className="mt-3" open>
                            <summary className="small fw-bold" style={{ cursor: 'pointer' }}>
                                Cost &amp; price math — stored vs live
                                {hasStored && totalFresh.stale ? <> <SourceTag kind="stale">stored total stale {fmtDelta(totalFresh.delta)}</SourceTag></> : null}
                                {hasStored && !totalFresh.stale ? <> <span className="badge bg-success">in sync</span></> : null}
                            </summary>
                            <div className="mt-2">
                                <PartEntryCostMathTable rows={mathRows} storedAsOf={storedAsOfLabel} hasStored={hasStored} />
                                <div className="small text-muted">
                                    <strong>Stored</strong> = parts columns as last written (Save here, or the daily Update All Part Costs) — what the Assemblies card and Sales Order
                                    conversion read. <strong>Live</strong> = this page right now: labor / gas / consumables from the cost engine, material as selected, coating as typed,
                                    totals by the formulas shown. All per unit; COST unless the row says otherwise.
                                    {storedSnap?.rollup && typeof storedSnap.rollup.is_stale === 'boolean' ? (
                                        <> API check: {storedSnap.rollup.is_stale
                                            ? <span className="text-warning">server says stored total {fmtMoney(storedSnap.rollup.stored_total)} ≠ its own live rollup {fmtMoney(storedSnap.rollup.live_total)}</span>
                                            : <span className="text-success">server rollup agrees with stored ({fmtMoney(storedSnap.rollup.live_total)})</span>}.</>
                                    ) : null}
                                    {storedSnap?.price_calc && storedSnap.price_calc.live !== null && storedSnap.price_calc.live !== undefined ? (
                                        <> Server price check (cost + material markup at {storedSnap.price_calc.markup_percent ?? '?'}%): stored {fmtMoney(storedSnap.price_calc.stored)} vs its live {fmtMoney(storedSnap.price_calc.live)}
                                            {storedSnap.price_calc.is_stale ? <span className="text-warning"> (stale)</span> : null}.</>
                                    ) : null}
                                </div>
                            </div>
                        </details>
                        <details className="mt-2" open={calcLogOpen} onToggle={(e) => { if (e.target === e.currentTarget) setCalcLogOpen(e.target.open); }}>
                            <summary className="small fw-bold" style={{ cursor: 'pointer' }}>
                                Calculation log — labor / gas / consumables per step
                                {calcLog.hoursSource ? <> <SourceTag kind={calcLog.hoursSource} /></> : null}
                                {costEngine.engine === 'dynamic' ? <> <SourceTag kind="estimate">unsaved edit — estimate engine</SourceTag></> : null}
                            </summary>
                            <div className="mt-2">
                                <PartEntryCalcLogTable
                                    log={partData?.last_calculation_log}
                                    engine={costEngine.engine}
                                    engineAt={costEngine.at}
                                    opsOwnedIndex={opsOwnedIndex}
                                    shuttle={costShuttle}
                                />
                            </div>
                        </details>
                    </div>
                  </div>
                </div>

                {/* Only render TaskTrendDashboard if dbPartId exists.
                   NOTE: Added key={dbPartId} to force re-mount on part change, preventing crash on empty history.
                */}
                {dbPartId && (<div className="card mb-4"><h5 className="card-header">Reporting & Analytics</h5><div className="card-body"><TaskTrendDashboard key={dbPartId} partId={dbPartId} processOptions={chartProcesses} apiBaseUrl={API_BASE} /></div></div>)}
              </div>
              <div className="col-lg-4">
                {/* New Part Creation Checklist */}
                {isNewPart && newPartChecklist && (
                  <div className="card mb-4 border-primary" style={{ position: 'sticky', top: '1rem', zIndex: 100 }}>
                    <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                      <span><i className="bi bi-list-check me-2"></i>New Part Checklist</span>
                      <span className="badge bg-white text-primary">{newPartChecklist.completedRequired}/{newPartChecklist.totalRequired}</span>
                    </div>
                    <div className="card-body p-0">
                      {/* Progress Bar */}
                      <div className="progress rounded-0" style={{ height: '8px' }}>
                        <div
                          className={`progress-bar ${newPartChecklist.allRequiredComplete ? 'bg-success' : 'bg-primary'}`}
                          role="progressbar"
                          style={{ width: `${newPartChecklist.progressPercent}%` }}
                          aria-valuenow={newPartChecklist.progressPercent}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        />
                      </div>

                      {/* Checklist Items */}
                      <ul className="list-group list-group-flush">
                        {newPartChecklist.items.map((item) => (
                          <li
                            key={item.id}
                            className={`list-group-item d-flex align-items-center py-2 ${item.complete ? 'bg-success-subtle' : item.required ? 'bg-light' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => scrollToSection(item.section)}
                          >
                            <span className="me-2" style={{ width: '20px' }}>
                              {item.complete ? (
                                <i className="bi bi-check-circle-fill text-success"></i>
                              ) : item.required ? (
                                <i className="bi bi-circle text-danger"></i>
                              ) : (
                                <i className="bi bi-circle text-muted"></i>
                              )}
                            </span>
                            <div className="flex-grow-1">
                              <span className={item.complete ? 'text-success' : item.required ? 'fw-medium' : 'text-muted'}>
                                {item.label}
                                {item.required && !item.complete && <span className="text-danger ms-1">*</span>}
                              </span>
                              {item.hint && !item.complete && (
                                <div className="text-muted small">{item.hint}</div>
                              )}
                            </div>
                            <i className="bi bi-chevron-right text-muted small"></i>
                          </li>
                        ))}
                      </ul>

                      {/* Ready to Save Indicator */}
                      {newPartChecklist.allRequiredComplete && (
                        <div className="p-3 bg-success-subtle text-success text-center">
                          <i className="bi bi-check-circle-fill me-2"></i>
                          <strong>Ready to Save!</strong>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="card mb-4">
                  <h5 className="card-header">Part Image</h5>
                  <div className="card-body">
                    {imageUrl ? <img src={imageUrl} alt="Part" style={{ width: "100%", maxHeight: "400px", objectFit: "contain", cursor: "pointer" }} className="img-thumbnail mb-2 dark-invertible" onClick={() => handleEnlarge(imageUrl)} /> : <div className="text-center p-4" style={{border: '2px dashed #ccc'}}>No image</div>}
                    <div className="input-group mt-2"><input type="file" className="form-control" accept="image/*" onChange={handleFileChange} /><button className="btn btn-sm btn-secondary" type="button" onClick={handleUploadImage} disabled={isReadOnly}>Upload</button></div>
                    {uploadFile && <div style={{ marginTop: "1rem" }}><p>Preview:</p><img src={URL.createObjectURL(uploadFile)} alt="Pending upload preview" style={{ maxWidth: "100%", border: "1px solid #ccc" }}/></div>}
                    <small className="text-muted d-block mt-2">You can also paste an image (Ctrl+V).</small>
                  </div>
                </div>
                
                {ppapRequired && (
                  <div className="card mb-4">
                    <h5 className="card-header">PPAP Balloon Print</h5>
                    <div className="card-body">
                      {ppapImageUrl ? <img src={ppapImageUrl} alt="PPAP Balloon Print" style={{ width: "100%", maxHeight: "200px", objectFit: "contain", cursor: "pointer" }} className="img-thumbnail mb-2 dark-invertible" onClick={() => handleEnlarge(ppapImageUrl)} /> : <div className="text-center p-4" style={{border: '2px dashed #ccc'}}>No Balloon Print</div>}
                      <div className="input-group mt-2">
                        <input type="file" className="form-control" accept="image/*,.pdf" onChange={handlePpapFileChange} />
                        <button className="btn btn-sm btn-info" type="button" onClick={handleUploadPpapImage} disabled={!dbPartId || !ppapUploadFile || isReadOnly}>Upload Print</button>
                      </div>
                      {ppapUploadFile && <div style={{ marginTop: "1rem" }}><p className="small">Pending PPAP upload preview:</p><img src={URL.createObjectURL(ppapUploadFile)} alt="Pending PPAP upload preview" style={{ maxWidth: "100%", border: "1px solid #ccc" }}/></div>}
                      <small className="text-muted d-block mt-2">Upload the drawing with measurement balloons/callouts.</small>
                    </div>
                  </div>
                )}

                {/* --- FILES / DOCUMENTS CARD --- */}
                <div className="card mb-4">
                  <h5 className="card-header d-flex justify-content-between align-items-center">
                    <span>Part Files</span>
                    <span className="badge bg-secondary">{partDocuments.length}</span>
                  </h5>
                  <div className="card-body">
                    
                    {/* List Existing Files */}
                    {partDocuments.length > 0 ? (
                      <div className="list-group list-group-flush mb-3">
                        {partDocuments.map(doc => {
                          // Calculate badge settings for this file
                          const typeBadge = getFileTypeBadge(doc.original_filename);
                          
                          return (
                            <div key={doc.document_id} className="list-group-item px-0">
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="text-truncate" style={{maxWidth: '70%'}}>
                                  <div className="fw-bold d-flex align-items-center">
                                    {/* File Type Badge */}
                                    <span className={`badge ${typeBadge.color} me-2`} style={{width: '45px', fontSize: '0.65rem'}}>
                                      {typeBadge.label}
                                    </span>
                                    {doc.display_name}
                                  </div>
                                  <div className="small text-muted mt-1">{doc.description}</div>
                                  <div className="small text-muted" style={{fontSize:'0.75rem'}}>
                                    {new Date(doc.created_at).toLocaleDateString()} by {doc.uploaded_by_name || 'Unknown'}
                                  </div>
                                </div>
                                <div className="btn-group">
                                  <a href={doc.file_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary" title="Download/View">
                                    <i className="bi bi-download"></i>
                                  </a>
                                  {!isReadOnly && (
                                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteDocument(doc.document_id)} title="Delete">
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center text-muted small mb-3 p-3 bg-light border border-dashed rounded">
                        No files uploaded yet.
                      </div>
                    )}

                    {/* Upload Form */}
                    {!isReadOnly && dbPartId && (
                      <div className="bg-light p-2 rounded border">
                        <h6 className="small fw-bold">Add New File</h6>
                        <div className="mb-2">
                          <input 
                            type="text" 
                            className="form-control form-control-sm mb-1" 
                            placeholder="File Name (e.g. CAD Drawing Rev B)" 
                            value={docDisplayName}
                            onChange={e => setDocDisplayName(e.target.value)}
                          />
                          <input 
                            type="text" 
                            className="form-control form-control-sm mb-1" 
                            placeholder="Description (Optional)" 
                            value={docDescription}
                            onChange={e => setDocDescription(e.target.value)}
                          />
                          <input 
                            type="file" 
                            className="form-control form-control-sm" 
                            onChange={e => setDocUploadFile(e.target.files[0] || null)} 
                          />
                        </div>
                        <button
                          className="btn btn-sm btn-success w-100"
                          onClick={handleUploadDocument}
                          disabled={isUploadingDoc || !docUploadFile}
                        >
                          {isUploadingDoc ? "Uploading..." : "Upload File"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* --- REVISION HISTORY & APPROVALS CARD --- */}
                {dbPartId && revisionHistory.length > 1 && (
                  <div className="card mb-4">
                    <h5 className="card-header d-flex justify-content-between align-items-center">
                      <span>Revision History &amp; Approvals</span>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                          const cur = revisionHistory.find(r => r.is_current_revision);
                          if (cur) setRepointPartId(cur.part_id);
                          else alert("No current revision found for this part family.");
                        }}
                        title="Update open quotes / sales orders / assemblies that still reference an older revision — in place, same order numbers, price unchanged"
                      >
                        <i className="bi bi-arrow-up-circle me-1"></i>Update open orders
                      </button>
                    </h5>
                    <div className="card-body p-2">
                      {openRefsWarning && (
                        <div className="alert alert-warning py-2 px-3 small mb-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
                          <div>
                            <strong>⚠ Older revisions of this part are still referenced:</strong>{" "}
                            {[
                              openRefsWarning.assemblies > 0 ? `${openRefsWarning.assemblies} assembly reference(s)` : null,
                              openRefsWarning.soLines > 0 ? `${openRefsWarning.soLines} sales order line(s)` : null,
                              openRefsWarning.quoteLines > 0 ? `${openRefsWarning.quoteLines} quote line(s)` : null,
                            ].filter(Boolean).join(", ")}
                          </div>
                          <button
                            className="btn btn-sm btn-warning fw-bold"
                            onClick={() => setRepointPartId(openRefsWarning.currentPartId)}
                          >
                            Review &amp; update
                          </button>
                        </div>
                      )}
                      <div className="list-group list-group-flush">
                        {buildRevisionTrailRows().map(row => {
                          const ev = row.event;
                          const status = ev ? ev.status : "no_form";
                          return (
                            <div key={row.key} className="list-group-item px-2">
                              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                <div style={{minWidth: '45%'}}>
                                  <div className="fw-bold">
                                    Rev {row.old_code || '—'} <i className="bi bi-arrow-right small"></i> Rev {row.new_code}
                                    {row.is_current && <span className="badge bg-primary ms-2" style={{fontSize:'0.65rem'}}>Current</span>}
                                  </div>
                                  <div className="small text-muted">
                                    {row.created_at ? new Date(row.created_at).toLocaleDateString() : ""}
                                    {ev?.created_by_name ? ` by ${ev.created_by_name}` : ""}
                                    {ev?.change_requested_by ? ` · ${ev.change_requested_by === 'customer' ? 'customer request' : 'internal change'}` : ""}
                                  </div>
                                  {ev?.change_description && (
                                    <div className="small mt-1" style={{whiteSpace:'pre-wrap'}}>{ev.change_description}</div>
                                  )}
                                  {ev?.pricing_impact && (
                                    <div className="small text-muted mt-1">
                                      <i className="bi bi-cash-coin me-1"></i>
                                      Pricing: {ev.pricing_impact === 'revised'
                                        ? `revised — see quote #${ev.pricing_quote_ref || '—'}`
                                        : 'no change to quoted pricing'}
                                    </div>
                                  )}
                                  {ev?.approved_via === 'portal' && (
                                    <div className="small text-success mt-1">
                                      <i className="bi bi-globe2 me-1"></i>
                                      Approved online by {ev.approver_name}{ev.approver_title ? `, ${ev.approver_title}` : ''}
                                    </div>
                                  )}
                                  {!ev?.signed_document_id && ev?.changes_requested_at && (
                                    <div className="small text-danger mt-1">
                                      <i className="bi bi-chat-left-text me-1"></i>
                                      Customer requested changes {new Date(ev.changes_requested_at).toLocaleDateString()}:{" "}
                                      {ev.portal_comments || ''}
                                    </div>
                                  )}
                                  {!ev?.signed_document_id && ev?.portal_link_active && (
                                    <div className="small text-muted mt-1">
                                      <i className="bi bi-link-45deg me-1"></i>
                                      Online approval link active
                                      {ev.portal_link_sent_to ? ` — sent to ${ev.portal_link_sent_to}` : ''}
                                      {ev.portal_link_expires_at ? ` (expires ${new Date(ev.portal_link_expires_at).toLocaleDateString()})` : ''}
                                    </div>
                                  )}
                                </div>
                                <div className="text-end">
                                  <div>
                                    {status === 'signed' && (
                                      <span className="badge bg-success">
                                        ✓ Signed{ev?.signed_at ? ` ${new Date(ev.signed_at).toLocaleDateString()}` : ''}
                                      </span>
                                    )}
                                    {status === 'awaiting_signature' && <span className="badge bg-warning text-dark">Awaiting signature</span>}
                                    {status === 'no_form' && <span className="badge bg-secondary">No approval form</span>}
                                  </div>
                                  {status !== 'signed' && (
                                    <div className="d-flex gap-1 justify-content-end align-items-center mt-1">
                                      <select
                                        className="form-select form-select-sm w-auto"
                                        style={{ fontSize: '0.72rem' }}
                                        value={(pricingSelByPart[row.new_part_id]?.impact) || ''}
                                        onChange={e => setPricingSelByPart(p => ({ ...p, [row.new_part_id]: { ...(p[row.new_part_id] || {}), impact: e.target.value } }))}
                                        title="Sets the PRICING IMPACT checkbox printed on the approval form — takes effect on Generate/Regenerate"
                                      >
                                        <option value="">Pricing impact: not set</option>
                                        <option value="unchanged">No change to quoted pricing</option>
                                        <option value="revised">Pricing revised — see quote #…</option>
                                      </select>
                                      {(pricingSelByPart[row.new_part_id]?.impact) === 'revised' && (
                                        <input
                                          className="form-control form-control-sm"
                                          style={{ width: '90px', fontSize: '0.72rem' }}
                                          placeholder="Quote #"
                                          value={(pricingSelByPart[row.new_part_id]?.ref) || ''}
                                          onChange={e => setPricingSelByPart(p => ({ ...p, [row.new_part_id]: { ...(p[row.new_part_id] || {}), ref: e.target.value } }))}
                                        />
                                      )}
                                    </div>
                                  )}
                                  <div className="btn-group btn-group-sm mt-1" role="group">
                                    {ev?.form_url && (
                                      <a className="btn btn-sm btn-outline-primary" href={ev.form_url} target="_blank" rel="noreferrer" title="View the generated approval form">
                                        Form
                                      </a>
                                    )}
                                    {status !== 'signed' && (
                                      <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => handleGenerateApprovalForm(row.new_part_id, ev?.change_description)}
                                        title="Generate the customer approval form for this revision change"
                                      >
                                        {status === 'awaiting_signature' ? 'Regenerate' : 'Generate form'}
                                      </button>
                                    )}
                                    {status !== 'signed' && (
                                      <button
                                        className="btn btn-sm btn-outline-info"
                                        onClick={() => handleCreatePortalLink(row.new_part_id)}
                                        title="Create an online approval link — the customer reviews and approves on a web page (no printing); approving stores the signed record automatically"
                                      >
                                        <i className="bi bi-globe2 me-1"></i>Portal link
                                      </button>
                                    )}
                                    {status !== 'signed' && ev && (
                                      <label className="btn btn-sm btn-outline-success mb-0" title="Upload the customer-signed copy (PDF or photo)">
                                        Upload signed
                                        <input
                                          type="file"
                                          hidden
                                          accept=".pdf,image/*"
                                          onChange={e => { handleUploadSignedApproval(ev.revision_event_id, e.target.files[0] || null); e.target.value = ""; }}
                                        />
                                      </label>
                                    )}
                                    {status === 'signed' && ev?.signed_url && (
                                      <a className="btn btn-sm btn-outline-success" href={ev.signed_url} target="_blank" rel="noreferrer">
                                        Signed copy
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="small text-muted mt-2 px-1">
                        Each revision's approval form is stored on the part; upload the customer-signed copy to complete the paper trail.
                      </div>
                    </div>
                  </div>
                )}

                {dbPartId && (
                  <div className="card mb-4">
                      <h5 className="card-header">Related Items</h5>
                      <div className="card-body">
                          {stockType === 'Sheet Stock' && (
                              <div className="mb-4">
                                  <h6>Cut Sheets Referencing Part</h6>
                                  {loadingCutSheetsForPart ? <div>Searching...</div> : cutSheetsForPart.length === 0 ? <div className="small">No cut sheets found.</div> : <table className="table table-sm table-bordered"><thead><tr><th>ID</th><th>Desc</th><th>Img</th><th>Action</th></tr></thead><tbody>{cutSheetsForPart.map(cs => <tr key={cs.cut_sheet_id}><td>{cs.cut_sheet_id}</td><td>{cs.description}</td><td>{cs.image_path ? <img src={cs.image_path} alt="Cut Sheet" style={{ width: "60px", cursor: "pointer" }} onClick={() => handleEnlarge(cs.image_path)} /> : "N/A"}</td><td><button className="btn btn-sm btn-primary" onClick={() => window.open(`/cut-sheets/${cs.cut_sheet_id}`, "_blank")}>View</button></td></tr>)}</tbody></table>}
                              </div>
                          )}
                          {stockType === 'Shape Stock' && (
                              <div className="mb-4">
                                  <h6>Cut Lists Referencing Part</h6>
                                  {loadingCutListsForPart ? <div>Searching...</div> : cutListsForPart.length === 0 ? <div className="small">No cut lists found.</div> : <table className="table table-sm table-bordered"><thead><tr><th>ID</th><th>Name</th><th>Action</th></tr></thead><tbody>{cutListsForPart.map(cl => <tr key={cl.cut_list_id}><td>{cl.cut_list_id}</td><td>{cl.cut_list_name}</td><td><button className="btn btn-sm btn-primary" onClick={() => window.open(`/cut-lists/${cl.cut_list_id}`, "_blank")}>View</button></td></tr>)}</tbody></table>}
                              </div>
                          )}
                          <div data-testid="assemblies-containing-part">
                              <h6>Assemblies Containing Part</h6>
                              {/* F07: membership comes from GET /parts/{id}.assemblies (primary AND
                                  component) with the role, join station and which cost lines the
                                  assembly's operations line owns. */}
                              {loadingAssembliesForPart ? <div>Searching...</div> : assembliesForPart.length === 0 ? <div className="small">No assemblies found (neither primary nor component).</div> : (
                                  <table className="table table-sm table-bordered" style={{ fontSize: '0.85rem' }}>
                                      <thead><tr><th>ID</th><th>Assembly</th><th>Role</th><th>Qty/set</th><th>Join</th><th>Priced on the assembly's ops line</th></tr></thead>
                                      <tbody>
                                          {assembliesForPart.map(asm => (
                                              <tr key={asm.assembly_id}>
                                                  <td>{asm.assembly_id}</td>
                                                  <td>
                                                      {asm.assembly_name}
                                                      {asm.active_status === false ? <span className="badge bg-secondary ms-1">retired</span> : null}
                                                      <div className="small text-muted">{assemblyRoleSentence(asm)}</div>
                                                  </td>
                                                  <td>
                                                      {asm.role === 'primary' ? <span className="badge bg-primary">Primary</span>
                                                        : asm.role === 'component' ? <span className="badge bg-secondary">Component</span>
                                                          : <span className="text-muted">unknown</span>}
                                                      {asm.roleSource === 'inferred' ? <div className="small text-muted" title="Role inferred from GET /assemblies (the part API did not report it)">inferred</div> : null}
                                                  </td>
                                                  <td>{asm.quantity_per_assembly ?? '—'}</td>
                                                  <td>{asm.join_workstation || '—'}</td>
                                                  <td className="small">
                                                      {asm.assembly_prices_coating ? <SourceTag kind="assembly_row" className="me-1">coating</SourceTag> : null}
                                                      {(asm.ops_owned_steps || []).map(s => <SourceTag key={s} kind="assembly_row" className="me-1">{s}</SourceTag>)}
                                                      {!asm.assembly_prices_coating && (asm.ops_owned_steps || []).length === 0
                                                        ? <span className="text-muted">{asm.has_provenance ? 'nothing — every step is priced on this part' : 'n/a (API predates provenance)'}</span>
                                                        : null}
                                                  </td>
                                              </tr>
                                          ))}
                                      </tbody>
                                  </table>
                              )}
                          </div>
                      </div>
                  </div>
                )}
              </div>
            </div>
            {!isReadOnly && (
                <button className="btn btn-primary btn-lg mt-3" onClick={handleSavePart}>{dbPartId ? "Save Changes" : "Create Part"}</button>
            )}
            {dbPartId && (<button className="btn btn-secondary btn-lg mt-3 ms-2" onClick={handlePrintLabel}>🖨️ Print Label</button>)}
            {dbPartId && (
              <button className="btn btn-info btn-lg mt-3 ms-2" onClick={handleRecalculateFromHistory} title="Recalculate labor cost based on historical job data.">
                <i className="bi bi-calculator me-2"></i>
                Recalculate from History
              </button>
            )}

          </div>
        )}

        {/* NEW: Render the BulkPartsEntryPage when view is 'bulk' */}
        {view === 'bulk' && (
          <BulkPartsEntryPage 
            customers={customers}
            cwtOptions={cwtOptions}
            thicknessOptions={thicknessOptions}
          />
        )}

      </div>

      {/* Enlarged Image Modal */}
      {enlargedImage && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} onClick={closeEnlarge}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header"><button type="button" className="btn-close" onClick={closeEnlarge}></button></div>
              <div className="modal-body text-center"><img src={enlargedImage} alt="Enlarged" style={{ maxWidth: "100%", maxHeight: "160vh" }} /></div>
            </div>
          </div>
        </div>
      )}

      {/* --- NEW: Revision Confirmation Modal --- */}
      {showRevModal && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} onClick={() => setShowRevModal(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">Create New Revision</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowRevModal(false)}></button>
              </div>
              <div className="modal-body">
                <p className="fw-bold mb-1">You are about to create a new revision of this part.</p>
                <p className="mb-2">This will archive the current version (Read-Only) and create a new editable copy.</p>
                <hr className="my-2" />
                <label className="form-label small fw-bold mb-1">What changed? <span className="text-muted fw-normal">(printed on the customer approval form)</span></label>
                <textarea
                  className="form-control form-control-sm mb-2"
                  rows={3}
                  placeholder='e.g. Added 0.312" hole near lower flange for powder coat racking, per customer request.'
                  value={revChangeDescription}
                  onChange={e => setRevChangeDescription(e.target.value)}
                />
                <div className="d-flex align-items-center gap-3 mb-1 flex-wrap">
                  <select className="form-select form-select-sm w-auto" value={revRequestedBy} onChange={e => setRevRequestedBy(e.target.value)}>
                    <option value="customer">Requested by customer</option>
                    <option value="internal">Internal change</option>
                  </select>
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="revGenFormChk" checked={revGenerateForm} onChange={e => setRevGenerateForm(e.target.checked)} />
                    <label className="form-check-label small" htmlFor="revGenFormChk">
                      Generate customer approval form (for signature)
                    </label>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                  <label className="form-label small fw-bold mb-0">Pricing impact:</label>
                  <select
                    className="form-select form-select-sm w-auto"
                    value={revPricingImpact}
                    onChange={e => setRevPricingImpact(e.target.value)}
                    title="Sets the PRICING IMPACT checkbox printed on the approval form"
                  >
                    <option value="unchanged">No change to quoted pricing</option>
                    <option value="revised">Pricing revised — see quote #…</option>
                    <option value="">Leave unset (blank boxes)</option>
                  </select>
                  {revPricingImpact === 'revised' && (
                    <input
                      className="form-control form-control-sm"
                      style={{ width: '110px' }}
                      placeholder="Quote #"
                      value={revPricingQuoteRef}
                      onChange={e => setRevPricingQuoteRef(e.target.value)}
                    />
                  )}
                </div>
                <hr className="my-2" />
                <p className="mb-2">Do you want to copy the existing documents/files (CAD, Prints, etc.) to the new revision?</p>
              </div>
              <div className="modal-footer d-flex justify-content-between">
                <button className="btn btn-outline-secondary" onClick={() => setShowRevModal(false)}>Cancel</button>
                <div>
                    <button className="btn btn-warning me-2" onClick={() => handleConfirmRevision(false)}>No, Start Clean</button>
                    <button className="btn btn-success" onClick={() => handleConfirmRevision(true)}>Yes, Copy Files</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- Revision → open-order update modal --- */}
      {repointPartId && (
        <RevisionRepointModal
          partId={repointPartId}
          onClose={() => setRepointPartId(null)}
          onApplied={() => {
            if (partData?.product_id) loadRevisionApprovals(partData.product_id);
            refreshOpenRefsWarning();
          }}
        />
      )}

      {/* --- NEW: Process Flow Modal --- */}
      {showFlowModal && (
        <ProcessFlowModal
            isOpen={showFlowModal}
            onClose={() => setShowFlowModal(false)}
            processSteps={partData?.process_steps || ""}
            productId={partData?.product_id}
        />
      )}
    </div>
  );
}

// --- Process Flow Modal Component ---
function ProcessFlowModal({ isOpen, onClose, processSteps, productId }) {
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    useEffect(() => {
        if (isOpen) {
            const steps = processSteps.split(',').map(s => s.trim()).filter(Boolean);
            const newNodes = [];
            const newEdges = [];

            // Start Node
            newNodes.push({
                id: 'start',
                type: 'input',
                data: { label: 'Raw Material' },
                position: { x: 250, y: 0 },
                style: { background: '#e9ecef', border: '1px solid #ced4da', borderRadius: '5px', padding: '10px', width: 150, textAlign: 'center' }
            });

            let yPos = 100;
            steps.forEach((step, index) => {
                const id = `step-${index}`;
                newNodes.push({
                    id: id,
                    data: { label: step },
                    position: { x: 250, y: yPos },
                    style: { background: '#fff', border: '1px solid #0d6efd', borderRadius: '5px', padding: '10px', width: 150, textAlign: 'center' }
                });

                // Edge from prev
                const prevId = index === 0 ? 'start' : `step-${index - 1}`;
                newEdges.push({
                    id: `e-${prevId}-${id}`,
                    source: prevId,
                    target: id,
                    type: 'smoothstep',
                    markerEnd: { type: MarkerType.ArrowClosed },
                });

                yPos += 100;
            });

            // End Node
            const endId = 'shipping';
            newNodes.push({
                id: endId,
                type: 'output',
                data: { label: 'Shipping / Finish' },
                position: { x: 250, y: yPos },
                style: { background: '#d1e7dd', border: '1px solid #198754', borderRadius: '5px', padding: '10px', width: 150, textAlign: 'center' }
            });

            const lastStepId = steps.length > 0 ? `step-${steps.length - 1}` : 'start';
            newEdges.push({
                id: `e-${lastStepId}-${endId}`,
                source: lastStepId,
                target: endId,
                type: 'smoothstep',
                markerEnd: { type: MarkerType.ArrowClosed },
            });

            setNodes(newNodes);
            setEdges(newEdges);
        }
    }, [isOpen, processSteps, setNodes, setEdges]);

    if (!isOpen) return null;

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div className="bg-white rounded shadow" style={{ width: '80%', height: '80%', display: 'flex', flexDirection: 'column' }}>
                <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
                    <h5 className="m-0">Process Flow: {productId}</h5>
                    <button className="btn-close" onClick={onClose}></button>
                </div>
                <div className="flex-grow-1">
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        fitView
                    >
                        <Background />
                        <Controls />
                    </ReactFlow>
                </div>
                <div className="p-3 border-top text-end">
                    <button className="btn btn-primary" onClick={() => window.print()}>Print / Save PDF</button>
                </div>
            </div>
        </div>
    );
}

/**
 * RevisionRepointModal — update open quotes / sales orders that still
 * reference a SUPERSEDED revision of a part, in place (same order numbers,
 * price untouched). Propose-first: loads eligibility per line from the API,
 * pre-checks the eligible ones, and refuses nothing silently — ineligible
 * rows show the server's reason. PIN-gated apply.
 */
function RevisionRepointModal({ partId, onClose, onApplied }) {
  const API_BASE = config.API_BASE_URL;
  const [refs, setRefs] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [selSo, setSelSo] = useState(new Set());
  const [selQuote, setSelQuote] = useState(new Set());
  const [selAsmComp, setSelAsmComp] = useState(new Set());   // assembly_component_ids
  const [selAsmPrim, setSelAsmPrim] = useState(new Set());   // assembly_ids (primary slot)
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/parts/${partId}/open_order_references`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(detailToString(data.detail) || `HTTP ${res.status}`);
        if (cancelled) return;
        setRefs(data);
        setSelSo(new Set((data.so_lines || []).filter(r => r.eligible).map(r => r.so_line_id)));
        setSelQuote(new Set((data.quote_lines || []).filter(r => r.eligible).map(r => r.quote_part_id)));
        setSelAsmComp(new Set((data.assembly_refs || []).filter(r => r.eligible && r.kind === 'component').map(r => r.assembly_component_id)));
        setSelAsmPrim(new Set((data.assembly_refs || []).filter(r => r.eligible && r.kind === 'primary').map(r => r.assembly_id)));
      } catch (e) {
        if (!cancelled) setLoadErr(String(e.message || e));
      }
    })();
    return () => { cancelled = true; };
  }, [partId, API_BASE]);

  const toggleIn = (set, setter, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };

  async function handleApply() {
    if (selSo.size === 0 && selQuote.size === 0 && selAsmComp.size === 0 && selAsmPrim.size === 0) {
      alert("Nothing selected."); return;
    }
    const pin = await pinPrompt("Enter PIN to update the selected orders / assemblies to the new revision:");
    if (!pin) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/parts/${partId}/apply_revision_to_open_orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pin,
          so_line_ids: [...selSo],
          quote_part_ids: [...selQuote],
          assembly_component_ids: [...selAsmComp],
          assembly_primary_ids: [...selAsmPrim],
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(detailToString(data.detail) || `HTTP ${res.status}`);
      let msg = data.message || "Updated.";
      const cutSheets = data.cut_sheets_still_on_old_rev || [];
      if (cutSheets.length > 0) {
        msg += `\n\n⚠ Cut sheet(s) still nest the OLD revision: ${cutSheets.map(cs => cs.cut_sheet_name).join(', ')}.` +
               "\nImport the updated nest as a cut-sheet revision before cutting.";
      }
      alert(msg);
      if (onApplied) onApplied(data);
      onClose();
    } catch (e) {
      alert(`Update failed: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  }

  const eligibleSo = (refs?.so_lines || []).filter(r => r.eligible).length;
  const eligibleQuote = (refs?.quote_lines || []).filter(r => r.eligible).length;
  const eligibleAsm = (refs?.assembly_refs || []).filter(r => r.eligible).length;
  const totalSelected = selSo.size + selQuote.size + selAsmComp.size + selAsmPrim.size;

  // Open assembly-linked order lines that ride along with the SELECTED
  // assembly fixes (updated automatically by the API).
  const selectedPairs = new Set();
  (refs?.assembly_refs || []).forEach(r => {
    const on = r.kind === 'component' ? selAsmComp.has(r.assembly_component_id) : selAsmPrim.has(r.assembly_id);
    if (on) selectedPairs.add(`${r.assembly_id}:${r.old_part_id}`);
  });
  const rideAlongLines = [
    ...((refs?.assembly_order_lines?.so_lines) || [])
      .filter(l => l.eligible && selectedPairs.has(`${l.source_assembly_id}:${l.old_part_id}`))
      .map(l => l.order_number),
    ...((refs?.assembly_order_lines?.quote_lines) || [])
      .filter(l => l.eligible && selectedPairs.has(`${l.source_assembly_id}:${l.old_part_id}`))
      .map(l => `quote #${l.quote_id}`),
  ];

  return (
    <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1055 }} onClick={onClose}>
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              Update open orders {refs ? `to Rev ${refs.part.revision_code} — ${refs.part.product_id}` : ""}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {loadErr && <div className="alert alert-danger py-2">{loadErr}</div>}
            {!refs && !loadErr && <div className="text-center text-muted p-4">Loading references…</div>}
            {refs && (
              <>
                <p className="small text-muted mb-3">
                  These open orders still reference an older revision. Updating repoints them in place —
                  <strong> same SO / quote numbers, price untouched</strong> — so nothing needs re-issuing.
                  Lines whose production has begun cannot be updated here.
                </p>

                <h6 className="mb-1">Sales order lines</h6>
                {(refs.so_lines || []).length === 0 ? (
                  <div className="text-muted small mb-3">None reference an older revision.</div>
                ) : (
                  <table className="table table-sm align-middle mb-3">
                    <thead><tr className="small text-muted">
                      <th style={{width: '30px'}}></th><th>SO</th><th>Customer</th><th>Status</th>
                      <th className="text-end">Qty</th><th>On rev</th><th></th>
                    </tr></thead>
                    <tbody>
                      {(refs.so_lines || []).map(r => (
                        <tr key={r.so_line_id} className={r.eligible ? "" : "table-light text-muted"}>
                          <td>
                            <input type="checkbox" className="form-check-input"
                              disabled={!r.eligible}
                              checked={selSo.has(r.so_line_id)}
                              onChange={() => toggleIn(selSo, setSelSo, r.so_line_id)} />
                          </td>
                          <td className="fw-bold">{r.order_number}{r.customer_po ? <span className="fw-normal small text-muted"> · PO {r.customer_po}</span> : null}</td>
                          <td className="small">{r.customer_name}</td>
                          <td className="small">{r.so_status}</td>
                          <td className="text-end">{r.quantity_ordered}</td>
                          <td><span className="badge bg-warning text-dark">Rev {r.old_revision_code}</span></td>
                          <td className="small text-danger">{r.eligible ? "" : r.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <h6 className="mb-1">Quote lines</h6>
                {(refs.quote_lines || []).length === 0 ? (
                  <div className="text-muted small mb-3">None reference an older revision.</div>
                ) : (
                  <table className="table table-sm align-middle mb-3">
                    <thead><tr className="small text-muted">
                      <th style={{width: '30px'}}></th><th>Quote</th><th>Customer</th><th>Status</th>
                      <th className="text-end">Qty</th><th>On rev</th><th></th>
                    </tr></thead>
                    <tbody>
                      {(refs.quote_lines || []).map(r => (
                        <tr key={r.quote_part_id} className={r.eligible ? "" : "table-light text-muted"}>
                          <td>
                            <input type="checkbox" className="form-check-input"
                              disabled={!r.eligible}
                              checked={selQuote.has(r.quote_part_id)}
                              onChange={() => toggleIn(selQuote, setSelQuote, r.quote_part_id)} />
                          </td>
                          <td className="fw-bold">#{r.quote_id}{r.linked_so_number ? <span className="fw-normal small text-muted"> → {r.linked_so_number}</span> : null}</td>
                          <td className="small">{r.customer_name}</td>
                          <td className="small">{r.quote_status}</td>
                          <td className="text-end">{r.quantity}</td>
                          <td><span className="badge bg-warning text-dark">Rev {r.old_revision_code}</span></td>
                          <td className="small text-danger">{r.eligible ? "" : r.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <h6 className="mb-1">Assemblies containing an older revision</h6>
                {(refs.assembly_refs || []).length === 0 ? (
                  <div className="text-muted small mb-3">No assembly references an older revision.</div>
                ) : (
                  <>
                    <table className="table table-sm align-middle mb-1">
                      <thead><tr className="small text-muted">
                        <th style={{width: '30px'}}></th><th>Assembly</th><th>Role</th>
                        <th className="text-end">Qty/asm</th><th>On rev</th><th></th>
                      </tr></thead>
                      <tbody>
                        {(refs.assembly_refs || []).map(r => {
                          const key = r.kind === 'component' ? `c${r.assembly_component_id}` : `p${r.assembly_id}`;
                          const checked = r.kind === 'component' ? selAsmComp.has(r.assembly_component_id) : selAsmPrim.has(r.assembly_id);
                          const toggle = () => (r.kind === 'component'
                            ? toggleIn(selAsmComp, setSelAsmComp, r.assembly_component_id)
                            : toggleIn(selAsmPrim, setSelAsmPrim, r.assembly_id));
                          return (
                            <tr key={key} className={r.eligible ? "" : "table-light text-muted"}>
                              <td>
                                <input type="checkbox" className="form-check-input"
                                  disabled={!r.eligible} checked={checked} onChange={toggle} />
                              </td>
                              <td className="fw-bold">#{r.assembly_id} {r.assembly_name}</td>
                              <td className="small">{r.kind === 'primary' ? 'Primary part' : 'Component'}</td>
                              <td className="text-end">{r.quantity_per_assembly ?? '—'}</td>
                              <td><span className="badge bg-warning text-dark">Rev {r.old_revision_code}</span></td>
                              <td className="small text-danger">{r.eligible ? "" : r.reason}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {rideAlongLines.length > 0 && (
                      <div className="small text-muted mb-3">
                        <i className="bi bi-arrow-return-right me-1"></i>
                        Open assembly-linked lines on {[...new Set(rideAlongLines)].join(", ")} update automatically with the selected assembly fixes.
                      </div>
                    )}
                    {rideAlongLines.length === 0 && <div className="mb-3"></div>}
                  </>
                )}

                {(refs.cut_sheets_on_old_rev || []).length > 0 && (
                  <div className="alert alert-warning py-2 small mb-0">
                    <strong>⚠ Cut sheets still nest the OLD revision:</strong>{" "}
                    {(refs.cut_sheets_on_old_rev || []).map(cs => `${cs.cut_sheet_name} (Rev ${cs.revision_code})`).join(", ")}
                    <div>Import the updated nest as a cut-sheet revision before cutting new material.</div>
                  </div>
                )}

                {eligibleSo === 0 && eligibleQuote === 0 && eligibleAsm === 0 && (
                  <div className="alert alert-info py-2 small mt-2 mb-0">
                    Nothing is eligible for an in-place update — open orders and assemblies either don't reference an older revision, or their production has begun.
                  </div>
                )}
              </>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Close</button>
            <button
              className="btn btn-primary"
              onClick={handleApply}
              disabled={busy || !refs || totalSelected === 0}
            >
              {busy ? "Updating…" : `Update ${totalSelected} item(s)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
